import {
  clearAvailabilityFailureStatus,
  mountAvailabilityFailure,
} from "./availability-error";
import {
  renderSelectedPlaceSummaryView,
  updatePlaceSelectionView,
  bindPlaceCards,
  renderPlaceCard,
  mountPlaceSelectionPrompt,
} from "./place-time-presentation";
import { isTestRecipientRejection } from "./test-recipient-error";
import { submissionSelectionKey } from "./submission-selection";
import {
  dateKey as calendarDateKey,
  monthKey as calendarMonthKey,
  monthBounds,
  shiftMonth,
  displayCalendarDate,
  displayZonedDateTime,
} from "./calendar-dates";
import {
  bookingBasePath,
  inBookingPath,
  bookingRelativePath,
  routingProfile,
} from "./public-address";
import { mountCityDayTimeline, type Shortcut } from "./city-day-timeline";
import { standardCityMapCenters } from "./city-map-centers";
import {
  drawCityDayMap,
  renderCityMapSchedule,
  type PublicMapContext,
} from "./city-day-map";
import { restoreRenderFocus } from "./render-focus";
import {
  bookingArtworkUrl,
  cityDayArtworkUrl,
  installArtworkFailureFallback,
} from "./booking-artwork";
import { mountDaySchedule } from "./day-schedule";
import {
  AttachmentScanPendingError,
  waitForAttachmentScan,
} from "./attachment-scan";
import {
  availabilityFetch,
  cacheUpdates,
  clearAvailabilityCache,
  availabilityRetrySeconds,
  AVAILABILITY_HANDOFF_TTL_MS,
  AVAILABILITY_CACHE_LIMIT,
} from "./availability-cache";
import createClient from "openapi-fetch";
import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import logoUrl from "../images/Mi-logo.svg?url";
import type { paths } from "./api/schema";
import "./styles.css";
import { companyCountries, mountCountryPicker } from "./country-picker";
import { renderBookingProposal } from "./proposals";
import {
  captureContactFields,
  clearDraft,
  isPublicEmail,
  readDraft,
  writeDraft,
} from "./journey";
import { startFormProtection, stopFormProtection } from "./form-protection";

declare global {
  interface Window {
    turnstile?: {
      render(
        element: HTMLElement,
        options: {
          sitekey: string;
          callback(token: string): void;
          "error-callback"(): void;
        },
      ): string;
      reset(widgetId: string): void;
    };
  }
}

const api = createClient<paths>({
  fetch: availabilityFetch,
  baseUrl: import.meta.env.VITE_BOOKING_API_BASE_URL || "/api/v1",
});
import { mountTimezonePicker } from "./timezone-picker";

let profileSlug = routingProfile;
let hostWebsite = "";
let hostDisplayName = "Palavio Booking";
let hostPublished = true;
const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || "";
const mapyTileApiKey = import.meta.env.VITE_MAPY_TILE_API_KEY || "";
const app = document.querySelector<HTMLElement>("#booking-app")!;
installArtworkFailureFallback(app);
app.addEventListener(
  "error",
  (event) => {
    const target = event.target;
    if (
      target instanceof HTMLImageElement &&
      target.closest(".meeting-summary-media")
    )
      target.closest(".meeting-summary")?.classList.add("without-artwork");
  },
  true,
);
if (import.meta.env.VITE_DEPLOYMENT_ENVIRONMENT === "staging") {
  if (
    import.meta.env.VITE_BOOKING_API_BASE_URL !==
    "https://api-staging.palavio.com/v1"
  )
    throw new Error("Staging Booking must use its dedicated API.");
  const banner = document.createElement("div");
  banner.className = "staging-banner";
  banner.setAttribute("role", "status");
  banner.textContent =
    "Staging | Simulated calendar | Email captured | Teams disabled";
  app.before(banner);
  void fetch(`${import.meta.env.VITE_BOOKING_API_BASE_URL}/health`, {
    signal: AbortSignal.timeout(10000),
  })
    .then((response) => {
      if (!response.ok) throw new Error("Backend unavailable");
    })
    .catch(() => {
      banner.textContent +=
        " | Staging je pozastavený nebo dočasně nedostupný. Testovací backend musí aktivovat správce. / Staging is paused or temporarily unavailable.";
    });
}
const logo = document.querySelector<HTMLImageElement>("#brand-logo");
if (logo) logo.src = logoUrl;

type Locale = "cs" | "en";
const requestedLocale = new URLSearchParams(window.location.search).get("lang");
let locale: Locale =
  requestedLocale === "cs" || requestedLocale === "en"
    ? requestedLocale
    : localStorage.getItem("booking-locale") === "cs" ||
        localStorage.getItem("booking-locale") === "en"
      ? (localStorage.getItem("booking-locale") as Locale)
      : /^(cs|sk)(-|$)/i.test(navigator.languages[0] || navigator.language)
        ? "cs"
        : "en";
localStorage.setItem("booking-locale", locale);
document.documentElement.lang = locale;
api.use({
  onRequest({ request }) {
    request.headers.set("Accept-Language", locale);
    const url = new URL(request.url);
    if (
      /\/booking\/public\/(reservations|management-links|proposals)\//.test(
        url.pathname,
      )
    ) {
      request.headers.set("X-Booking-Profile", profileSlug);
    }
    return request;
  },
});

type PublicProfile =
  paths["/booking/public/{profileSlug}"]["get"]["responses"][200]["content"]["application/json"];
type PublicMeetingType = PublicProfile["meetingTypes"][number];
type ApiCityDayDetail =
  paths["/booking/public/{profileSlug}/city/{cityDaySlug}"]["get"]["responses"][200]["content"]["application/json"];
type CityMeetingType = ApiCityDayDetail["meetingTypes"][number];
type BookingMeetingType = PublicMeetingType | CityMeetingType;
// Catalog-origin selections need the public type, not an administrative type ID.
type PublicCityDayDetail = Omit<ApiCityDayDetail, "meetingTypes"> & {
  meetingTypes: BookingMeetingType[];
};
type PublicReservation =
  paths["/booking/public/reservations/{actionToken}"]["get"]["responses"][200]["content"]["application/json"];
type Slot = {
  cityKey?: string;
  startsAt: string;
  endsAt: string;
  recommended?: boolean;
  recommendation?: "adjacent" | "fills_gap" | "same_place" | null;
  travelStatus?: "estimate" | "pending" | "verified";
  preferredPlaceIds?: string[];
};
type VenueAvailability =
  paths["/booking/public/reservations/{actionToken}/availability"]["get"]["responses"][200]["content"]["application/json"];
type CompanySuggestion = {
  officialName: string;
  registrationNumber: string;
  registeredAddress: string;
  selectionToken: string;
};
type ProblemDetails = {
  type?: string;
  status?: number;
  detail?: string;
  title?: string;
  requestId?: string;
};
type AttachmentUploadState = {
  fileKey: string;
  id: string;
  uploadToken: string;
  uploadMode: "api" | "direct";
  uploadUrl?: string;
  uploaded: boolean;
  acceptedId?: string;
  expiresAt?: number;
  retryNotBefore?: number;
};

const supportedTimeZones = (() => {
  const intl = Intl as typeof Intl & {
    supportedValuesOf?: (key: "timeZone") => string[];
  };
  return (
    intl.supportedValuesOf?.("timeZone") ?? [
      "Europe/Prague",
      "Europe/London",
      "America/New_York",
      "Asia/Tokyo",
    ]
  );
})();

let profile: PublicProfile;
let selectedType: BookingMeetingType | undefined;
let selectedCityDay: PublicCityDayDetail | undefined;
let cityDayInformation: PublicCityDayDetail | undefined;
let cityDayEndTimer: ReturnType<typeof setTimeout> | undefined;
function watchCityDayEnd() {
  clearTimeout(cityDayEndTimer);
  if (!selectedCityDay) return;
  const remaining = Date.parse(selectedCityDay.endsAt) - Date.now();
  if (!Number.isFinite(remaining) || remaining < 0) return;
  const id = selectedCityDay.id;
  cityDayEndTimer = setTimeout(
    () => {
      if (selectedCityDay?.id !== id) return;
      if (cityDayEnded()) renderEndedCityDay();
      else watchCityDayEnd();
    },
    Math.min(remaining + 1, 2147483647),
  );
}
function cityDayEnded() {
  return Boolean(
    selectedCityDay && Date.parse(selectedCityDay.endsAt) <= Date.now(),
  );
}
function informationalPlaceOptions() {
  return cityDayInformation?.id === selectedCityDay?.id && selectedCityDay
    ? cityDayInformation?.placeOptions
    : selectedCityDay?.placeOptions;
}
function informationalFixedPlace() {
  const options = informationalPlaceOptions();
  return options?.places.find((place) => place.id === options.fixedPlaceId);
}
function cityDayMapMarkup() {
  return mapyTileApiKey
    ? `<div id="city-place-map" class="city-place-map" aria-label="${escapeHtml(ui("places.map", "Mapa míst setkání", "Meeting-place map"))}"></div>`
    : "";
}
function otherBookingOptions() {
  return `<a class="back-link" href="${bookingBasePath}/">${escapeHtml(ui("cityDay.moreOptions", "Zobrazit další možnosti rezervace", "View other booking options"))}</a>`;
}
function renderEndedCityDay(): void {
  if (!selectedCityDay) return;
  stopSlotMonitoring();
  stopFormProtection();
  calendarAbort.abort();
  selectedSlot = "";
  slots = [];
  const place = informationalFixedPlace();
  app.innerHTML = `<section class="hero"><div class="hero-copy"><h1>${escapeHtml(selectedCityDay.title || selectedCityDay.city)}</h1><p>${escapeHtml(displayDate(selectedCityDay.localDate))} · ${escapeHtml(selectedCityDay.timeZone)}</p><h2>${escapeHtml(ui("cityDay.ended", "Tento den již proběhl", "This day has already ended"))}</h2>${otherBookingOptions()}</div></section><section class="fixed-place"><h2>${escapeHtml(place?.fullName || place?.name || selectedCityDay.location || selectedCityDay.city)}</h2>${place ? `<p>${escapeHtml(place.address)}</p>${cityDayMapMarkup()}` : ""}</section>`;
  mountCityPlaceMap(true);
}
let standardPlaceOptions: PublicCityDayDetail["placeOptions"] = null;
let standardPlaceTypeId = "";
function basePlaceOptions() {
  if (
    selectedType?.locations.find((x) => x.id === selectedLocationId)?.kind !==
    "in_person"
  )
    return null;
  return selectedCityDay
    ? selectedCityDay.placeOptions
    : selectedType?.slug === standardPlaceTypeId &&
        selectedType.locations.find((x) => x.id === selectedLocationId)
          ?.kind === "in_person"
      ? standardPlaceOptions
      : null;
}
let placeView = false;
let dayOffer: PublicCityDayDetail["placeOptions"] = null;
let dayPlaceSlots = new Map<string, Slot[]>();
let monthOrigins: Array<{ day: PublicCityDayDetail; slots: Slot[] }> = [];
let calendarAbort = new AbortController();
function rawPlaceOptions() {
  return selectedType?.locations.find((x) => x.id === selectedLocationId)
    ?.kind === "in_person" &&
    placeView &&
    dayOffer
    ? dayOffer
    : basePlaceOptions();
}
function offeredPlace(
  place: NonNullable<PublicCityDayDetail["placeOptions"]>["places"][number],
): boolean {
  const evidence = place.availability;
  return (
    !evidence ||
    (evidence.state === "current"
      ? (evidence.availableStartCount ?? 0) > 0
      : evidence.lastKnownAvailable === true)
  );
}
function currentPlaceOptions() {
  const options = rawPlaceOptions();
  return options
    ? {
        ...options,
        cityName:
          options.cityName ||
          selectedCityDay?.publicCity?.name ||
          selectedCityDay?.city ||
          cityFilter ||
          homeCityName,
        places: options.places.filter(offeredPlace),
      }
    : options;
}
let placeChangeNotice = "";
function needsMeetingPlaceSelection(): boolean {
  return (
    selectedType?.locations.find(
      (location) => location.id === selectedLocationId,
    )?.kind === "in_person" &&
    Boolean(currentPlaceOptions()) &&
    !selectedCityPlaceId
  );
}
function savePlaceUrl() {
  if ((!selectedCityDay && !placeView) || rescheduleActionToken) return;
  const url = new URL(location.href);
  if (selectedCityPlaceId) url.searchParams.set("place", selectedCityPlaceId);
  else url.searchParams.delete("place");
  url.searchParams.delete("time");
  url.searchParams.delete("shortcut");
  history.replaceState(history.state, "", url);
}
function scheduleSignal() {
  return AbortSignal.any([calendarAbort.signal, AbortSignal.timeout(10000)]);
}
async function limited<T, R>(
  items: T[],
  run: (item: T) => Promise<R>,
): Promise<R[]> {
  const output: R[] = new Array(items.length);
  let next = 0;
  await Promise.all(
    Array.from({ length: Math.min(3, items.length) }, async () => {
      while (next < items.length) {
        const i = next++;
        output[i] = await run(items[i]!);
      }
    }),
  );
  return output;
}
let selectedCityPlaceId = "";
let visitorAddress = "";
let visitorLocationDetails = "";
let visitorLocationDetailsAddress = "";
let visitorAddressVerification:
  { source: "osm"; token: string } | { source: "visitor" } | undefined;
let visitorAddressQuery = "";
let visitorAddressNotice = "";
let visitorAddressType = "";
let visitorAddressCity = "";
let visitorAddressExpiresAt = 0;
let visitorAddressRetryAt = 0;
let visitorAddressTokens = new Map<
  string,
  { token: string; expiresAt: string }
>();
function validAddressConfirmation(): boolean {
  if (!visitorAddressVerification) return false;
  const city = selectedCityDay?.publicCity?.name || cityFilter || homeCityName;
  if (
    visitorAddressType !== selectedType?.slug ||
    (city && visitorAddressCity !== city) ||
    (selectedCityDay && !visitorAddressTokens.has(selectedCityDay.slug))
  ) {
    clearAddressConfirmation();
    return false;
  }
  if (
    !Number.isFinite(visitorAddressExpiresAt) ||
    visitorAddressExpiresAt <= Date.now()
  ) {
    clearAddressConfirmation();
    visitorAddressNotice = ui(
      "places.expired",
      "Platnost ověření vypršela. Ověřte prosím adresu znovu.",
      "Address verification expired. Please check the address again.",
    );
    return false;
  }
  return true;
}
let visitorAddressRevision = 0;
let visitorAddressKind: "place" | "address" = "address";
let visitorCoordinates: { latitude: number; longitude: number } | null = null;
function otherPinFallback(): string {
  return '<span class="city-place-marker-image selected other-pin-fallback"><svg viewBox="0 0 42 50" aria-hidden="true"><path d="M21 48C18 42 8 29 8 18a13 13 0 0 1 26 0c0 11-10 24-13 30Z" fill="#f7f6f2" stroke="#080b24" stroke-width="2"/><circle cx="21" cy="18" r="4" fill="#080b24"/></svg></span>';
}
function ownAddressMarkup(): string {
  if (selectedCityPlaceId !== "other") return "";
  if (validAddressConfirmation())
    return `<div class="visitor-address visitor-address-confirmed" role="status" tabindex="-1"><div class="visitor-address-heading"><strong>${escapeHtml(ui("places.chosen", "Adresa vybrána", "Address selected"))}</strong><button type="button" class="address-edit-button" id="change-visitor-address">${escapeHtml(ui("places.editAddress", "Upravit", "Edit"))}</button></div><p>${escapeHtml(visitorAddress)}</p><small>${escapeHtml(visitorAddressVerification?.source === "osm" ? ui("places.osmProvenance", visitorAddressKind === "place" ? "Místo nalezeno v OSM." : "Adresní bod / budova z OSM — nejde o ověřený podnik.", visitorAddressKind === "place" ? "Place found in OSM." : "OSM address confirmed: address point / building, not a verified business.") : ui("places.manualProvenance", "Ručně zadaná, neověřená adresa.", "Manually entered, unverified address."))}</small><label for="visitor-location-details">${escapeHtml(ui("places.details", "Upřesnění místa (volitelné)", "Location details (optional)"))}</label><input id="visitor-location-details" type="text" value="${escapeHtml(visitorLocationDetails)}" aria-describedby="visitor-location-details-hint"/><small id="visitor-location-details-hint">${escapeHtml(ui("places.detailsHint", "Firma, místnost, patro nebo vstup. Nejvýše 160 znaků.", "Company, room, floor or entrance. Up to 160 characters."))}</small><p class="address-next"><span class="address-next-desktop">${escapeHtml(ui("places.nextTime", "Nyní vyberte čas vpravo.", "Now choose a time on the right."))}</span><span class="address-next-mobile">${escapeHtml(ui("places.nextTimeMobile", "Nyní vyberte čas níže.", "Now choose a time below."))}</span></p></div>`;
  return `<div class="visitor-address"><label for="visitor-address">${escapeHtml(ui("places.address", "Adresa ve vybraném městě", "Address in the selected city"))}</label><input id="visitor-address" maxlength="500" value="${escapeHtml(visitorAddressQuery || visitorAddress)}"/><button type="button" class="text-button" id="verify-visitor-address">${escapeHtml(ui("places.verify", "Ověřit adresu", "Check address"))}</button><div id="visitor-address-results" aria-live="polite">${escapeHtml(visitorAddressNotice)}</div></div>`;
}
function refreshOwnAddress(focus = false): void {
  document.querySelector(".visitor-address")?.remove();
  document
    .querySelector(".other-place-card")
    ?.insertAdjacentHTML("beforeend", ownAddressMarkup());
  wireVisitorAddress();
  if (focus)
    document
      .querySelector<HTMLElement>(
        ".visitor-address-confirmed, #visitor-address",
      )
      ?.focus();
}
function clearAddressConfirmation(): void {
  visitorAddressVerification = undefined;
  visitorAddressExpiresAt = 0;
  visitorAddressNotice = "";
  visitorAddressTokens.clear();
  visitorCoordinates = null;
  visitorAddressRevision++;
  cityPlaceMarkers.get("other")?.remove();
  cityPlaceMarkers.delete("other");
  cityMapContentKey = "";
}
function wireVisitorAddress(): void {
  const details = document.querySelector<HTMLInputElement>(
    "#visitor-location-details",
  );
  if (details) {
    const update = () => {
      visitorLocationDetails = details.value
        .replace(/[\r\n\u2028\u2029]+/g, " ")
        .trim();
      visitorLocationDetailsAddress = visitorAddress;
      details.setCustomValidity(
        visitorLocationDetails.length > 160
          ? ui(
              "places.detailsTooLong",
              "Upřesnění místa může mít nejvýše 160 znaků.",
              "Location details must be 160 characters or fewer.",
            )
          : "",
      );
      details.setAttribute(
        "aria-invalid",
        String(visitorLocationDetails.length > 160),
      );
      saveJourney();
    };
    details.oninput = update;
    details.onpaste = (event) => {
      event.preventDefault();
      details.setRangeText(
        (event.clipboardData?.getData("text") ?? "").replace(
          /[\r\n\u2028\u2029]+/g,
          " ",
        ),
        details.selectionStart ?? 0,
        details.selectionEnd ?? 0,
        "end",
      );
      update();
    };
    update();
  }
  const change = document.querySelector<HTMLButtonElement>(
    "#change-visitor-address",
  );
  if (change)
    change.onclick = () => {
      clearAddressConfirmation();
      selectedSlot = "";
      saveJourney();
      refreshOwnAddress(true);
    };
  const input = document.querySelector<HTMLInputElement>("#visitor-address");
  if (!input) return;
  const results = document.querySelector<HTMLElement>(
    "#visitor-address-results",
  )!;
  input.oninput = () => {
    visitorLocationDetails = "";
    visitorLocationDetailsAddress = "";
    visitorAddress = input.value;
    visitorAddressQuery = input.value;
    clearAddressConfirmation();

    button.disabled = false;
    results.replaceChildren();
    saveJourney();
  };
  const button = document.querySelector<HTMLButtonElement>(
    "#verify-visitor-address",
  )!;
  button.onclick = async () => {
    if ((visitorAddressQuery || visitorAddress).trim().length < 3) {
      input.focus();
      return;
    }
    if (Date.now() < visitorAddressRetryAt) {
      results.textContent =
        ui(
          "places.addressRateLimit",
          "Hledání bylo dočasně omezeno. Zkuste to znovu za",
          "Address search is temporarily limited. Try again in",
        ) + ` ${Math.ceil((visitorAddressRetryAt - Date.now()) / 1000)} s.`;
      return;
    }
    clearAddressConfirmation();
    const revision = visitorAddressRevision;
    button.disabled = true;

    results.textContent = ui(
      "places.checking",
      "Ověřuji adresu…",
      "Checking address…",
    );
    try {
      const daySlugs = selectedCityDay
        ? [selectedCityDay.slug]
        : [
            ...new Set([
              ...(!cityFilter && basePlaceOptions()?.allowOther
                ? [`type:${selectedType!.slug}`]
                : []),
              ...monthOrigins
                .filter(
                  (source) =>
                    source.day.placeOptions?.allowOther &&
                    source.day.localDate === selectedDay,
                )
                .map((source) => source.day.slug),
              ...(dayPlaceSlots.get("other") ?? [])
                .map((slot) =>
                  slot.cityKey
                    ? citySlotOrigins.get(slot.cityKey)?.slug
                    : `type:${selectedType!.slug}`,
                )
                .filter((slug): slug is string => Boolean(slug)),
            ]),
          ];
      if (!daySlugs.length)
        throw new Error("No available CityDay for this proposal.");
      const address = visitorAddressQuery || visitorAddress;
      visitorAddressQuery = address;
      visitorAddressTokens.clear();
      const responses = await limited(daySlugs, async (cityDaySlug) => {
        const result = cityDaySlug.startsWith("type:")
          ? await api.POST(
              "/booking/public/{profileSlug}/{meetingTypeSlug}/verify-address",
              {
                params: {
                  path: { profileSlug, meetingTypeSlug: cityDaySlug.slice(5) },
                },
                body: { address },
              },
            )
          : await api.POST(
              "/booking/public/{profileSlug}/city/{cityDaySlug}/verify-address",
              {
                params: { path: { profileSlug, cityDaySlug } },
                body: { address },
              },
            );
        if (result.response.status === 429) {
          const retry = result.response.headers.get("Retry-After") ?? "60";
          const seconds = Number(retry);
          visitorAddressRetryAt = Number.isFinite(seconds)
            ? Date.now() + Math.max(1, seconds) * 1000
            : Math.max(
                Date.now() + 1000,
                Date.parse(retry) || Date.now() + 60000,
              );
        }
        if (result.error) throw result.error;
        return { slug: cityDaySlug, data: result.data };
      });
      const response = { data: responses[0]!.data };
      if (revision !== visitorAddressRevision) return;
      results.replaceChildren();

      if (!response.data.items.length) {
        results.textContent =
          response.data.providerStatus === "unavailable"
            ? ui(
                "places.addressUnavailable",
                "Služba ověření adres je dočasně nedostupná. Zkuste hledání znovu.",
                "Address verification is temporarily unavailable. Retry the search.",
              )
            : response.data.emptyReason === "street_only"
              ? ui(
                  "places.streetOnly",
                  "Našli jsme ulici, ale ne konkrétní místo. Doplňte číslo domu nebo název podniku.",
                  "We found the street, but not a specific place. Add a house number or business name.",
                )
              : ui(
                  "places.addressNotFound",
                  "Konkrétní adresu jsme nenašli. Doplňte číslo domu nebo název podniku a zkuste to znovu.",
                  "No specific address found. Add a house number or business name and try again.",
                );
      }
      if (response.data.items.length) {
        const prompt = document.createElement("p");
        prompt.className = "address-results-prompt";
        prompt.textContent = ui(
          "places.chooseAddress",
          "Vyberte adresu, kterou chcete použít",
          "Choose the address you want to use",
        );
        results.prepend(prompt);
      }
      for (const item of response.data.items) {
        const result = document.createElement("section");
        result.className = "address-result";
        const address = document.createElement("strong");
        address.textContent = item.address;
        const source = document.createElement("small");
        source.textContent =
          item.resultKind === "place"
            ? "OpenStreetMap"
            : ui(
                "places.addressPoint",
                "OpenStreetMap — adresní bod / budova, nikoli ověřený podnik",
                "OpenStreetMap — address point / building, not a verified business",
              );
        result.append(address, source);
        const choice = document.createElement("button");
        choice.type = "button";
        choice.className = "text-button";
        choice.textContent = ui(
          "places.useAddress",
          "Použít tuto adresu",
          "Use this address",
        );
        choice.setAttribute(
          "aria-label",
          `${choice.textContent}: ${item.address}`,
        );
        choice.onclick = () => {
          if (revision !== visitorAddressRevision) return;
          visitorAddressKind =
            item.resultKind === "place" ? "place" : "address";
          if (
            visitorLocationDetailsAddress &&
            visitorLocationDetailsAddress !== item.address
          )
            visitorLocationDetails = "";
          visitorLocationDetailsAddress = item.address;
          visitorAddress = item.address;
          input.value = item.address;
          visitorAddressTokens = new Map(
            responses.flatMap(({ slug, data }) => {
              const match = data.items.find(
                (candidate) =>
                  candidate.address === item.address &&
                  candidate.latitude === item.latitude &&
                  candidate.longitude === item.longitude &&
                  candidate.resultKind === item.resultKind,
              );
              return match
                ? [
                    [
                      slug,
                      { token: match.token, expiresAt: match.expiresAt },
                    ] as const,
                  ]
                : [];
            }),
          );
          visitorAddressVerification = { source: "osm", token: item.token };
          visitorAddressType = selectedType?.slug ?? "";
          visitorAddressCity =
            response.data.city ||
            currentPlaceOptions()?.cityName ||
            homeCityName;
          visitorAddressExpiresAt = Date.parse(item.expiresAt);
          if (!validAddressConfirmation()) {
            refreshOwnAddress(true);
            return;
          }
          visitorCoordinates = {
            latitude: item.latitude,
            longitude: item.longitude,
          };
          saveJourney();
          mountCityPlaceMap();
          refreshOwnAddress(true);
          selectedSlot = "";
          // Keep the address editor alive while its times refresh: the visitor
          // may already be editing or verifying a different address.
          void renderCalendar(true).then(() => {
            document
              .querySelector<HTMLInputElement>("#visitor-location-details")
              ?.focus();
          });
        };
        result.append(choice);
        results.append(result);
      }
    } catch {
      if (revision === visitorAddressRevision) {
        results.textContent =
          Date.now() < visitorAddressRetryAt
            ? ui(
                "places.addressRateLimit",
                "Hledání bylo dočasně omezeno. Zkuste to znovu za",
                "Address search is temporarily limited. Try again in",
              ) +
              ` ${Math.ceil((visitorAddressRetryAt - Date.now()) / 1000)} s.`
            : ui(
                "places.addressUnavailable",
                "Služba ověření adres je dočasně nedostupná. Zkuste hledání znovu.",
                "Address verification is temporarily unavailable. Retry the search.",
              );
      }
    } finally {
      if (revision === visitorAddressRevision) button.disabled = false;
    }
  };
}

let cityFilter = "";
let filteredCityDays: PublicCityDayDetail[] = [];
let homeCityName = "";
let homeCityImage: PublicCityDayDetail["publicCity"] extends infer P
  ? NonNullable<P> extends { image: infer I }
    ? I
    : never
  : never = null;
const citySlotOrigins = new Map<string, PublicCityDayDetail>();
let selectedSlot = "";
let selectedLocationId = "";
const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
let attendeeTimeZone = browserTimeZone || "UTC";
let visibleMonth = monthKey(new Date(), attendeeTimeZone);
let slots: Slot[] = [];
let turnstileToken = turnstileSiteKey ? "" : "local-bypass";
const formStartedAt = Date.now();
let clientSubmissionId: string = crypto.randomUUID();
let selectedCompany: CompanySuggestion | null = null;
let attachment: File | null = null;
let attachmentUpload: AttachmentUploadState | null = null;
let rescheduleActionToken: string | null = null;
let rescheduleContextNotice = "";
const rescheduleDayErrors = new Set<string>();
let reschedulingReservation: PublicReservation | null = null;
let rescheduleAvailability: VenueAvailability | null = null;
let cityPlaceMap: L.Map | null = null;
let slotRevalidationTimer = 0;
let slotVisibilityHandler: (() => void) | null = null;
let draft = readDraft();
if (draft?.clientSubmissionId) clientSubmissionId = draft.clientSubmissionId;
let submittedSelection =
  draft?.submissionSelection ??
  (draft?.clientSubmissionId
    ? submissionSelectionKey(
        draft.typeSlug,
        draft.path.match(/\/city\/([^/]+)/)?.[1] ?? "",
        draft.slot,
        draft.locationId,
        draft.cityPlaceId ?? "",
        draft.timeZone,
      )
    : "");
let currentStep: 1 | 2 | 3 = 1;
type AvailabilityFreshness = {
  state: "current" | "refreshing" | "error";
  checkedAt: string | null;
  complete?: boolean;
  uncheckedDates?: string[];
  refreshAfterSeconds?: number;
  days?: Array<{
    date: string;
    state: string;
    hasStoredResult?: boolean;
    checkedAt: string | null;
  }>;
};
let storedDates = new Set<string>();
let uncheckedDates = new Set<string>();
let availabilityState: "current" | "refreshing" | "error" = "current";
let availabilityFailure: unknown;
let availabilityRefreshAfter = 0;
let bookingNotice:
  | {
      minimumNoticeHours: number;
      earliestStartsAt: string;
      recheckAt: string | null;
      unavailableReason: "minimum_notice" | "no_available_slots" | null;
    }
  | undefined;
let noticeTimer = 0;
let availabilityRetryTimer = 0;
let availabilityRetryAttempt = 0;
let availabilityRetryContext = "";
let alternativesUnverified = false;
function availabilityContext() {
  return [
    selectedType?.slug,
    selectedLocationId,
    selectedCityDay?.slug,
    selectedCityPlaceId,
    selectedDay,
    visibleMonth,
    attendeeTimeZone,
  ].join("|");
}
function collectFreshness(value: {
  availabilityFreshness?: AvailabilityFreshness;
  bookingNotice?: typeof bookingNotice;
}) {
  if (value.bookingNotice) bookingNotice = value.bookingNotice;
  const fresh = value.availabilityFreshness;
  if (!fresh) return;
  for (const day of fresh.days ?? [])
    if (day.hasStoredResult || day.checkedAt) storedDates.add(day.date);
  const incomplete =
    fresh.complete === false || Boolean(fresh.uncheckedDates?.length);
  if (
    (fresh.state !== "current" || incomplete) &&
    (fresh.uncheckedDates?.includes(selectedDay) ||
      (!fresh.uncheckedDates?.length && Boolean(selectedCityDay)))
  ) {
    const origin = mapPresenceOrigin();
    const cached =
      origin &&
      mapOverviews.get(
        `${loadRevision}|${profileSlug}|${origin.slug}|${locale}`,
      );
    if (cached) {
      cached.at = 0;
      cached.value = {
        ...cached.value,
        timelineState: "stale",
        availabilityState: "needs_check",
      };
    }
  }
  for (const day of fresh.uncheckedDates ?? []) uncheckedDates.add(day);
  if (
    (fresh.state !== "current" || incomplete) &&
    !fresh.uncheckedDates?.length
  ) {
    if (selectedCityDay) uncheckedDates.add(selectedCityDay.localDate);
    else {
      // Older saved responses may not carry per-day freshness. Do not turn
      // their empty dates into a claim that the calendar is fully booked.
      const bounds = monthBounds(visibleMonth);
      for (
        let d = new Date(bounds.from + "T00:00Z");
        d.toISOString().slice(0, 10) <= bounds.to;
        d.setUTCDate(d.getUTCDate() + 1)
      )
        uncheckedDates.add(d.toISOString().slice(0, 10));
    }
  }
  if (fresh.state !== "current" || incomplete)
    availabilityState = fresh.state === "error" ? "error" : "refreshing";
  if (fresh.state !== "current" || incomplete)
    availabilityRefreshAfter = Math.max(
      availabilityRefreshAfter,
      fresh.refreshAfterSeconds ?? 0,
    );
}
function availabilityStatusMarkup(): string {
  return '<div class="availability-status" role="status" aria-live="polite" aria-hidden="true"><span></span></div>';
}
function availabilityStatusHost(root: ParentNode = app): HTMLElement | null {
  return (
    root.querySelector<HTMLElement>(".calendar-shell") ??
    root.querySelector<HTMLElement>(".city-place-picker, .fixed-place") ??
    root.querySelector<HTMLElement>(".calendar-and-slots")
  );
}
function availabilityStatus(text = "", busy = false) {
  // A prepared month can be current while this selected day's fresh check failed.
  // Its generic completion must not clear the day-specific error and retry.
  if (!text && rescheduleActionToken && rescheduleDayErrors.has(selectedDay))
    return;
  const target = availabilityStatusHost();
  if (!target) return;
  let notice = target.querySelector<HTMLElement>(
    ":scope > .availability-status",
  );
  if (!notice) {
    notice = document.createElement("div");
    notice.className = "availability-status";
    notice.setAttribute("role", "status");
    target.append(notice);
  }
  notice.setAttribute("role", "status");
  notice.setAttribute("aria-hidden", String(!text && !busy));
  notice.classList.remove("reschedule-day-error");
  notice.classList.toggle("is-busy", busy);
  let label = notice.querySelector("span");
  if (!label) {
    label = document.createElement("span");
    notice.prepend(label);
  }
  if (label.textContent !== text) label.textContent = text;
  label.classList.remove("reschedule-day-check");
  if (!refreshTimedOut) {
    notice.querySelectorAll<HTMLButtonElement>("button").forEach((button) => {
      if (!(
        !busy &&
        button.dataset.rescheduleDayRetry === "true" &&
        rescheduleActionToken &&
        rescheduleDayErrors.has(selectedDay)
      ))
        button.remove();
    });
  }
}
let refreshSessionStarted = 0;
let refreshTimedOut = false;
let verificationDeadlineTimer = 0;
let visibleCheckStarted = 0;
let visibleCheckContext = "";
let visibleCheckTimer = 0;
function selectionVerified() {
  return selectedDay
    ? !uncheckedDates.has(selectedDay) &&
        (availabilityState === "current" || uncheckedDates.size > 0)
    : availabilityState === "current";
}
function updateAvailabilityIndicator() {
  clearTimeout(visibleCheckTimer);
  if (currentStep !== 2) return;
  if (
    rescheduleActionToken &&
    rescheduleDayErrors.has(selectedDay) &&
    availabilityState === "current"
  ) {
    showRescheduleDayError();
    return;
  }
  if (
    availabilityState === "error" &&
    availabilityFailure &&
    app.querySelector(".day-slots > .availability-failure")?.isConnected
  ) {
    clearAvailabilityFailureStatus(app);
    return;
  }
  if (refreshTimedOut) {
    availabilityStatus(
      ui(
        "calendar.longWait",
        "Ověření dostupnosti trvá déle.",
        "Availability verification is taking longer.",
      ),
    );
    const notice = app.querySelector<HTMLElement>(".availability-status");
    if (notice && (storedDates.size > 0 || slots.length > 0)) {
      const label = notice.querySelector("span");
      if (label)
        label.textContent +=
          " " +
          ui(
            "calendar.savedHint",
            "Zobrazen\u00e9 star\u0161\u00ed \u00fadaje jsou orienta\u010dn\u00ed.",
            "Previously displayed results are indicative only.",
          );
    }
    if (notice && !notice.querySelector("button")) {
      const retry = document.createElement("button");
      retry.className = "text-button";
      retry.textContent = ui(
        "calendar.retryVerification",
        "Zkusit ověřit dostupnost",
        "Retry availability verification",
      );
      retry.disabled = availabilityRetrySeconds() > 0;
      if (retry.disabled)
        window.setTimeout(() => {
          if (retry.isConnected) retry.disabled = false;
        }, availabilityRetrySeconds() * 1000);
      retry.onclick = () => {
        clearAvailabilityCache();
        void renderCalendar();
      };
      notice.append(retry);
    }
    return;
  }
  const waiting = selectedDay
    ? !selectionVerified()
    : availabilityState !== "current";
  if (!waiting) {
    visibleCheckStarted = 0;
    availabilityStatus();
    return;
  }
  if (availabilityState === "error") {
    availabilityStatus(
      availabilityRetrySeconds() > 0
        ? ui(
            "calendar.rateLimited",
            "Dostupnost ověřujeme příliš často. Po uplynutí čekání ji automaticky obnovíme.",
            "Availability is being checked too often. We will retry automatically after the waiting period.",
          )
        : ui(
            "calendar.failed",
            "Dostupnost se nepodařilo ověřit. Automaticky to zkusíme znovu.",
            "Availability could not be verified. We will retry automatically.",
          ),
    );
    const notice = app.querySelector<HTMLElement>(".availability-status");
    notice?.setAttribute("role", "alert");
    return;
  }
  const same = visibleCheckContext === availabilityContext();
  const elapsed =
    same && visibleCheckStarted
      ? performance.now() - visibleCheckStarted
      : 30000;
  if (document.hidden || !navigator.onLine) {
    availabilityStatus();
    return;
  }
  if (elapsed < 150) {
    visibleCheckTimer = window.setTimeout(
      updateAvailabilityIndicator,
      150 - elapsed,
    );
    return;
  }
  const hasSaved = selectedDay
    ? storedDates.has(selectedDay) ||
      slots.some((s) => dateKey(new Date(s.startsAt)) === selectedDay)
    : storedDates.size > 0 || slots.length > 0;
  const busy = !hasSaved && elapsed < 30000;
  availabilityStatus(
    busy
      ? ui(
          "calendar.checking",
          `Ověřujeme termíny pro ${selectedType?.durationMinutes}minutovou schůzku…`,
          `Checking times for a ${selectedType?.durationMinutes}-minute meeting…`,
        )
      : ui(
          "calendar.autoRefresh",
          hasSaved
            ? "Ověření dostupnosti trvá déle. Starší údaje jsou orientační."
            : "Ověření dostupnosti trvá déle.",
          hasSaved
            ? "Availability verification is taking longer. Older results are provisional."
            : "Availability verification is taking longer.",
        ),
    busy,
  );
  if (busy)
    visibleCheckTimer = window.setTimeout(
      updateAvailabilityIndicator,
      30000 - elapsed,
    );
}
function scheduleAvailabilityRefresh() {
  clearTimeout(availabilityRetryTimer);
  const key = availabilityContext();
  if (key !== availabilityRetryContext) {
    availabilityRetryAttempt = 0;
    availabilityRetryContext = key;
  }
  if (bookingNotice?.unavailableReason === "minimum_notice") {
    availabilityStatus();
    return;
  }
  const needs =
    availabilityState !== "current" ||
    rawPlaceOptions()?.places.some(
      (p) =>
        p.selectable && p.availability && p.availability.state !== "current",
    ) ||
    alternativesUnverified ||
    slots.some((s) => s.travelStatus === "pending");
  if (!needs) {
    clearTimeout(verificationDeadlineTimer);
    refreshTimedOut = false;
    availabilityRetryAttempt = 0;
    availabilityStatus();
    clearTimeout(noticeTimer);
    if (
      bookingNotice?.recheckAt &&
      currentStep === 2 &&
      !document.hidden &&
      navigator.onLine
    ) {
      const delay = Date.parse(bookingNotice.recheckAt) - Date.now();
      noticeTimer = window.setTimeout(
        () => {
          clearAvailabilityCache();
          void renderCalendar(true, true);
        },
        Math.max(1000, Math.min(delay, 2147483647)),
      );
    }
    return;
  }
  if (currentStep !== 2 || document.hidden || !navigator.onLine) return;
  if (
    refreshSessionStarted &&
    performance.now() - refreshSessionStarted >= 120000
  ) {
    refreshTimedOut = true;
    updateAvailabilityIndicator();
    return;
  }
  updateAvailabilityIndicator();
  const delay = Math.max(
    availabilityRefreshAfter > 0
      ? availabilityRefreshAfter
      : ([2, 5, 10, 30][availabilityRetryAttempt] ?? 60),
    availabilityRetrySeconds(),
  );
  availabilityRetryTimer = window.setTimeout(
    () => {
      if (
        currentStep !== 2 ||
        key !== availabilityContext() ||
        document.hidden ||
        !navigator.onLine
      )
        return;
      if (
        refreshSessionStarted &&
        performance.now() - refreshSessionStarted >= 120000
      ) {
        refreshTimedOut = true;
        updateAvailabilityIndicator();
        return;
      }
      availabilityRetryAttempt++;
      void renderCalendar(true, true);
    },
    Math.min(
      delay * 1000,
      Math.max(1, 120000 - (performance.now() - refreshSessionStarted)),
    ),
  );
}
for (const event of ["visibilitychange", "online"])
  (event === "visibilitychange" ? document : window).addEventListener(
    event,
    () => {
      if (document.hidden || !navigator.onLine) {
        clearTimeout(availabilityRetryTimer);
        clearTimeout(visibleCheckTimer);
        availabilityStatus();
        clearTimeout(noticeTimer);
        return;
      }
      if (
        currentStep === 2 &&
        bookingNotice?.recheckAt &&
        Date.parse(bookingNotice.recheckAt) <= Date.now()
      ) {
        clearAvailabilityCache();
        void renderCalendar(true, true);
        return;
      }
      if (currentStep === 2) {
        availabilityRetryAttempt = 0;
        scheduleAvailabilityRefresh();
      }
    },
  );
window.addEventListener("offline", () => clearTimeout(availabilityRetryTimer));
function currentAvailabilityRequest(url: URL) {
  if (!selectedType) return false;
  if (selectedCityDay)
    return url.pathname.endsWith(
      `/city/${encodeURIComponent(selectedCityDay.slug)}/availability`,
    );
  if (
    url.pathname.endsWith(
      `/${encodeURIComponent(selectedType.slug)}/availability`,
    )
  )
    return true;
  return (
    url.searchParams.get("meetingType") === selectedType.slug &&
    filteredCityDays.some(
      (day) =>
        day.publicCity?.name === (cityFilter || homeCityName) &&
        day.localDate.slice(0, 7) === visibleMonth &&
        url.pathname.endsWith(
          `/city/${encodeURIComponent(day.slug)}/availability`,
        ),
    )
  );
}
let cacheRefreshTimer: ReturnType<typeof setTimeout>;
cacheUpdates.addEventListener("refresh", (event) => {
  const { key, state } = (event as CustomEvent<{ key: string; state: string }>)
    .detail;
  if (state !== "updated" || currentStep !== 2 || !selectedType) return;
  const requested = new URL(key.split("|")[0]);
  if (!currentAvailabilityRequest(requested)) return;
  const from = requested.searchParams.get("from");
  if (from && from.slice(0, 7) !== visibleMonth) return;
  if (
    requested.searchParams.get("timeZone") &&
    requested.searchParams.get("timeZone") !== attendeeTimeZone
  )
    return;
  const place =
    requested.searchParams.get("place") ??
    requested.searchParams.get("cityPlaceId");
  if (place && place !== selectedCityPlaceId) return;
  if (
    from &&
    from === requested.searchParams.get("to") &&
    selectedDay &&
    from !== selectedDay
  )
    return;
  const context = availabilityContext();
  const mapOrigin = mapPresenceOrigin();
  if (
    mapOrigin &&
    requested.pathname.endsWith(
      `/city/${encodeURIComponent(mapOrigin.slug)}/availability`,
    )
  ) {
    const cached = mapOverviews.get(
      `${loadRevision}|${profileSlug}|${mapOrigin.slug}|${locale}`,
    );
    if (cached) {
      cached.at = 0;
    }
  }
  clearTimeout(cacheRefreshTimer);
  cacheRefreshTimer = setTimeout(() => {
    if (currentStep === 2 && context === availabilityContext())
      void renderCalendar(true, true);
  }, 150);
});

let selectedDay = "";
let submitting = false;
let rejectedSlot = "";
let loadRevision = 0;
let calendarRevision = 0;
try {
  if (draft?.timeZone) {
    new Intl.DateTimeFormat("en", { timeZone: draft.timeZone });
    attendeeTimeZone = draft.timeZone;
  }
} catch {
  /* Use the browser zone. */
}

function saveJourney(): void {
  if (
    rescheduleActionToken ||
    /\/(manage|proposal|access)\//.test(location.pathname)
  )
    return;
  const form = document.querySelector<HTMLFormElement>("#reservation-form");
  draft = {
    savedAt: Date.now(),
    path: location.pathname,
    step: currentStep,
    typeSlug: selectedType?.slug ?? "",
    slot: selectedSlot,
    locationId: selectedLocationId,
    cityPlaceId: selectedCityPlaceId,
    manualAddressConfirmed: visitorAddressVerification?.source === "visitor",
    timeZone: attendeeTimeZone,
    fields: {
      ...draft?.fields,
      guestLocationProposal: visitorAddress,
      guestLocationDetails: visitorLocationDetails,
      visitorLocationDetailsAddress,
      visitorAddressQuery,
      ...(form ? captureContactFields(form) : {}),
    },
    invitees: form
      ? Array.from(
          form.querySelectorAll<HTMLInputElement>(
            '[name="additionalInviteeEmail"]',
          ),
          (input) => input.value,
        )
      : (draft?.invitees ?? []),
    hadAttachment:
      Boolean(attachment) || (!form && Boolean(draft?.hadAttachment)),
    clientSubmissionId,
    submissionSelection: submittedSelection,
  };
  writeDraft(draft);
}

function setStep(step: 1 | 2 | 3, push = false): void {
  if (step !== 1) {
    disposeCityTimeline?.();
    disposeCityTimeline = undefined;
  }
  currentStep = step;
  const state = {
    bookingStep: step,
    typeSlug: selectedType?.slug ?? "",
    calendarMonth: visibleMonth,
    calendarDay: selectedDay,
  };
  if (push) history.pushState(state, "", location.href);
  else history.replaceState(state, "", location.href);
  saveJourney();
}

async function navigateBooking(url: URL): Promise<void> {
  if (submitting) return;
  const shared = sharedAvailabilityReturn();
  if (
    shared &&
    url.origin === location.origin &&
    url.pathname.startsWith(`${bookingBasePath}/`)
  )
    url.searchParams.set("shared", shared);
  saveJourney();
  stopSlotMonitoring();
  stopFormProtection();
  history.pushState({ bookingStep: 1 }, "", url);
  selectedSlot = "";
  selectedDay = "";
  selectedType = undefined;
  selectedCityDay = undefined;
  clientSubmissionId = crypto.randomUUID();
  await loadProfile();
}

app.addEventListener("input", () => {
  if (!submitting) clientSubmissionId = crypto.randomUUID();
  saveJourney();
});
app.addEventListener("change", () => saveJourney());
document.addEventListener("click", (event) => {
  const anchor = (event.target as Element | null)?.closest<HTMLAnchorElement>(
    "a[href]",
  );
  if (
    !anchor ||
    event.defaultPrevented ||
    event.button !== 0 ||
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey ||
    event.altKey ||
    anchor.target
  )
    return;
  const url = new URL(anchor.href);
  if (
    url.origin !== location.origin ||
    !inBookingPath(url.pathname) ||
    /\/(manage|proposal|access)\//.test(url.pathname)
  )
    return;
  event.preventDefault();
  void navigateBooking(url);
});
window.addEventListener("popstate", () => {
  if (submitting) return;
  // Form fields are captured on input, before history changes the current path.
  stopSlotMonitoring();
  stopFormProtection();
  void loadProfile();
});
app.addEventListener(
  "click",
  (event) => {
    const target = (event.target as Element)?.closest(".slot");
    if (target?.closest('.scheduling-card[aria-busy="true"]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  true,
);
app.addEventListener("click", (event) => {
  const button = (event.target as Element | null)?.closest<HTMLButtonElement>(
    "[data-booking-step]",
  );
  if (!button || submitting) return;
  saveJourney();
  if (button.dataset.bookingStep === "1") {
    void navigateBooking(new URL(`${bookingBasePath}/`, location.origin));
  } else if (button.dataset.bookingStep === "2") {
    if (
      (rejectedSlot && rejectedSlot === selectedSlot) ||
      contactSlotState === "unavailable"
    ) {
      selectedSlot = "";
      rejectedSlot = "";
      clearAvailabilityCache();
    }
    setStep(2, true);
    void renderCalendar().then(focusStepHeading);
  }
});

let contactCheckEpoch = 0;
let contactCheckTimer = 0;
let contactCheckAbort = new AbortController();
let contactSlotState: "checking" | "verified" | "unavailable" | "error" =
  "checking";
let contactVerifiedKey = "";
function contactSelectionKey() {
  return [
    availabilityContext(),
    selectedSlot,
    visitorAddress,
    visitorAddressVerification?.source,
    visitorAddressVerification?.source === "osm"
      ? visitorAddressVerification.token
      : "",
  ].join("|");
}

function stopSlotMonitoring(): void {
  contactCheckEpoch++;
  contactCheckAbort.abort();
  clearTimeout(contactCheckTimer);
  contactVerifiedKey = "";
  window.clearInterval(slotRevalidationTimer);
  if (slotVisibilityHandler)
    document.removeEventListener("visibilitychange", slotVisibilityHandler);
  slotVisibilityHandler = null;
}

function escapeHtml(value: unknown): string {
  return String(value ?? "").replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      })[character]!,
  );
}

function problemMessage(error: unknown): string {
  if (isTestRecipientRejection(error))
    return ui(
      "error.testRecipientNotAllowed",
      "Rezervace nebyla potvrzena. V testovacím režimu musí být všechny e-mailové adresy účastníků a příjemců povolené organizátorem. Organizátor je může zkontrolovat v Settings → Integrations → Full testing. Zadané údaje zůstaly zachované.",
      "The reservation was not confirmed. In test mode, all attendee and recipient email addresses must be approved by the organiser. The organiser can review them in Settings → Integrations → Full testing. Your entered details have been preserved.",
    );
  const problem = error as ProblemDetails | undefined;
  const status = problem?.status;
  const type = problem?.type?.split("/").pop();
  if (type === "booking-places-disabled")
    return ui(
      "error.placesDisabled",
      "Výběr míst je dočasně nedostupný. Zkuste to prosím později.",
      "Meeting place selection is temporarily unavailable. Please try again later.",
    );
  if (type === "booking-city-unresolved")
    return ui(
      "error.cityUnresolved",
      "Organizátor musí upřesnit město nebo místo tohoto dne. Termíny zatím nelze nabídnout.",
      "The organiser needs to confirm this day's city or venue before times can be offered.",
    );
  if (status === 404)
    return ui(
      "error.notPublished",
      "Rezervace zatím není zveřejněna.",
      "Booking is not published yet.",
    );
  if (type === "turnstile-failed")
    return ui(
      "error.verificationExpired",
      "Bezpečnostní ověření vypršelo. Počkejte prosím na nové ověření a zkuste to znovu.",
      "Security verification expired. Wait for a new verification and try again.",
    );
  if (status === 403)
    return ui(
      "error.forbidden",
      "Rezervaci teď nelze z této stránky otevřít.",
      "This booking cannot be opened from this page.",
    );
  if (
    status === 409 &&
    problem?.detail ===
      "The idempotency key is already used for another booking."
  )
    return ui(
      "error.submissionChanged",
      "Předchozí pokus je spojený s jinou rezervací. Pro aktuální výběr je připraven nový pokus. Zkontrolujte údaje a potvrďte rezervaci znovu; nic nebylo automaticky odesláno.",
      "The previous attempt belongs to a different booking. A new attempt is ready for your current selection. Review the details and confirm again; nothing has been resubmitted automatically.",
    );
  if (
    status === 409 &&
    problem?.detail?.startsWith("This CityDay is no longer available.")
  )
    return ui(
      "error.cityDayChanged",
      "Tento CityDay už není zveřejněný. Vraťte se na nabídku rezervací a vyberte aktuálně dostupný den.",
      "This CityDay is no longer published. Return to booking options and select an available day.",
    );
  if (
    status === 409 &&
    ![
      "The selected time is no longer available.",
      "This city-day time is no longer available.",
      "The selected time was booked by someone else.",
      "A required participant is already booked at this time.",
    ].includes(problem?.detail ?? "")
  )
    return ui(
      "error.bookingConditionsChanged",
      "Rezervaci se nepodařilo dokončit, protože se změnily její podmínky. Obnovte nabídku míst a časů. Zadané kontaktní údaje zůstaly zachované.",
      "The booking could not be completed because its conditions changed. Refresh the available places and times. Your contact details have been preserved.",
    );
  if (status === 409)
    return ui(
      "error.slotTaken",
      "Tento termín už mezitím obsadil někdo jiný. Vyberte prosím jiný.",
      "This time has just been booked. Please choose another.",
    );
  if (type === "calendar-provider-unavailable")
    return ui(
      "error.calendarUnavailable",
      "Kalendář je dočasně nedostupný. Zkuste to prosím za chvíli.",
      "The calendar is temporarily unavailable. Please try again shortly.",
    );
  if (type === "attachment-scanner-unavailable")
    return ui(
      "error.scannerUnavailable",
      "Příloha je uložená, ale bezpečnostní kontrola zatím není dostupná. Zkuste kontrolu zopakovat za chvíli; soubor se nebude nahrávat znovu.",
      "Your attachment is saved, but its security check is unavailable. Retry shortly; the file will not be uploaded again.",
    );
  if (type === "attachment-storage-unavailable")
    return ui(
      "error.storageUnavailable",
      "Uložení přílohy je dočasně nedostupné. Ostatní údaje ve formuláři zůstaly zachované.",
      "Attachment storage is temporarily unavailable. Your other form details have been preserved.",
    );
  if (type === "attachment-rejected")
    return ui(
      "error.attachmentRejected",
      "Příloha neprošla kontrolou typu nebo bezpečnosti. Vyberte prosím jiný soubor.",
      "The attachment failed its file type or security check. Please choose another file.",
    );
  if (type === "booking-crm-unavailable")
    return ui(
      "error.crmUnavailable",
      "Rezervaci teď nelze dokončit kvůli dočasně nedostupnému CRM. Zadané údaje zůstaly zachované.",
      "Booking cannot be completed while CRM is unavailable. Your details have been preserved.",
    );
  return ui(
    "error.requestFailed",
    "Požadavek se nepodařilo dokončit. Zkuste to prosím znovu.",
    "The request could not be completed. Please try again.",
  );
}

function dateKey(value: Date, timeZone = attendeeTimeZone): string {
  return calendarDateKey(value, timeZone);
}
function monthKey(value: Date, timeZone = attendeeTimeZone): string {
  return calendarMonthKey(value, timeZone);
}

function formatWhen(value: string, timeZone = attendeeTimeZone): string {
  return displayZonedDateTime(value, timeZone);
}

function formatConfirmationWhen(value: string, timeZone: string): string {
  return (
    new Intl.DateTimeFormat(locale === "cs" ? "cs-CZ" : "en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
      timeZone,
    }).format(new Date(value)) +
    " · " +
    timeZone
  );
}

function formatTime(value: string, zone = attendeeTimeZone): string {
  return new Intl.DateTimeFormat(locale === "cs" ? "cs-CZ" : "en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: zone,
  }).format(new Date(value));
}

function zoneLabel(zone: string): string {
  const label = new Intl.DateTimeFormat("cs-CZ", {
    timeZone: zone,
    timeZoneName: "longOffset",
  })
    .formatToParts(
      selectedSlot
        ? new Date(selectedSlot)
        : new Date(`${selectedDay || `${visibleMonth}-15`}T12:00:00Z`),
    )
    .find((part) => part.type === "timeZoneName")?.value;
  return `${zone} · ${label ?? "UTC"}`;
}

function focusView(): void {
  const heading = app.querySelector<HTMLElement>("h1,h2");
  if (heading && !app.contains(document.activeElement)) {
    heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
  }
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function t(
  key: string,
  fallback: string,
  values: Record<string, string> = {},
): string {
  let text = profile?.catalog?.[key] ?? fallback;
  for (const [name, value] of Object.entries(values))
    text = text.replaceAll(`{${name}}`, value);
  return text;
}

function installLocaleSwitcher(): void {
  const header = document.querySelector<HTMLElement>(".site-header");
  if (!header || header.querySelector(".locale-switcher")) return;
  header.insertAdjacentHTML(
    "beforeend",
    `<div class="locale-switcher" aria-label="Language"><button type="button" data-locale="cs" aria-pressed="${locale === "cs"}">CZ</button><button type="button" data-locale="en" aria-pressed="${locale === "en"}">EN</button></div>`,
  );
  header
    .querySelectorAll<HTMLButtonElement>("[data-locale]")
    .forEach((button) =>
      button.addEventListener("click", () => {
        const next = button.dataset.locale as Locale;
        localStorage.setItem("booking-locale", next);
        const url = new URL(window.location.href);
        url.searchParams.set("lang", next);
        window.location.assign(url);
      }),
    );
}

function syncLocaleSwitcher(): void {
  updatePublicShell();
  document
    .querySelectorAll<HTMLButtonElement>("[data-locale]")
    .forEach((button) =>
      button.setAttribute(
        "aria-pressed",
        String(button.dataset.locale === locale),
      ),
    );
}

function updatePublicShell(catalog = profile?.catalog ?? {}) {
  const label = (key: string, cs: string, en: string) =>
    catalog[key] ?? (locale === "cs" ? cs : en);
  const set = (selector: string, value: string) => {
    const element = document.querySelector(selector);
    if (element) element.textContent = value;
  };
  set(
    ".skip-link",
    label("shell.skip", "Přeskočit k rezervaci", "Skip to booking"),
  );
  set(
    ".header-context",
    label("home.title", "Rezervace schůzky", "Book a meeting"),
  );
  const shared = sharedAvailabilityReturn();
  const context = document.querySelector(".header-context");
  if (shared && context) {
    const returnLink = document.createElement("a");
    returnLink.href = shared;
    returnLink.className = "shared-availability-return";
    returnLink.textContent = label(
      "sharing.back",
      "Zpět na sdílenou dostupnost",
      "Back to shared availability",
    );
    context.replaceChildren(returnLink);
  }
  set("footer a", label("shell.home", "Zpět na osobní web", "Back to website"));
  set(
    "footer span",
    label(
      "shell.timezone",
      "Časy odpovídají vybranému časovému pásmu.",
      "Times use the selected time zone.",
    ),
  );
  document
    .querySelector(".wordmark")
    ?.setAttribute(
      "aria-label",
      label(
        "shell.homeLabel",
        `${hostDisplayName} – domovská stránka`,
        `${hostDisplayName} – home`,
      ),
    );
  document
    .querySelector(".locale-switcher")
    ?.setAttribute("aria-label", label("shell.language", "Jazyk", "Language"));
}

function ui(key: string, cs: string, en: string): string {
  return t(key, locale === "cs" ? cs : en);
}

function isChosenSlot(slot: { startsAt: string; cityKey?: string }): boolean {
  if (slot.startsAt !== selectedSlot) return false;
  if (slot.cityKey)
    return citySlotOrigins.get(slot.cityKey)?.id === selectedCityDay?.id;
  return true;
}

function sharedAvailabilityReturn(): string | null {
  const value = new URL(location.href).searchParams.get("shared");
  if (!value || !value.startsWith(`${bookingBasePath}/`)) return null;
  const target = new URL(value, location.origin);
  return target.origin === location.origin &&
    target.pathname === `${bookingBasePath}/availability`
    ? target.pathname + target.search
    : null;
}

function progress(step: 1 | 2 | 3): string {
  const labels = [
    selectedCityDay?.meetingTypes.length === 1 || fixedCityDay()
      ? ui("progress.options", "Možnosti rezervace", "Booking options")
      : ui("progress.type", "Typ schůzky", "Meeting type"),
    selectedType?.locations.some((location) => location.kind === "in_person")
      ? ui("progress.placeTime", "Místo a čas", "Place and time")
      : ui("progress.time", "Čas", "Time"),
    ui("progress.contacts", "Kontaktní údaje", "Contact details"),
  ];
  return `<nav class="booking-progress" aria-label="${escapeHtml(ui("progress.label", "Kroky rezervace", "Booking steps"))}"><ol class="progress">${labels.map((label, index) => `<li class="${index < step ? "done" : ""}" ${index + 1 === step ? 'aria-current="step"' : ""}><span class="progress-line" aria-hidden="true"></span>${index + 1 < step ? `<button type="button" data-booking-step="${index + 1}"><span aria-hidden="true">←</span><span class="progress-text">${escapeHtml(label)}</span></button>` : `<span class="progress-label">${escapeHtml(label)}</span>`}</li>`).join("")}</ol></nav>`;
}
function focusStepHeading(): void {
  const heading = app.querySelector<HTMLElement>(
    ".booking-step-header h2, .city-place-heading h2",
  );
  if (heading) {
    heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
  }
}
function stepHeader(step: 2 | 3): string {
  return `<header class="booking-step-header">${progress(step)}<h2>${escapeHtml(step === 2 && selectedCityDay ? ui("places.chooseMeetingPlace", "Vyberte místo setkání", "Choose a meeting place") : t(step === 2 ? "calendar.chooseDate" : "form.yourDetails", step === 2 ? "Vyberte datum" : "Vaše kontaktní údaje"))}</h2></header>`;
}

app.addEventListener("click", (event) => {
  const trigger = (event.target as Element).closest<HTMLButtonElement>(
    "[data-edit-timezone]",
  );
  if (!trigger || submitting || document.querySelector("#timezone-dialog"))
    return;
  const dialog = document.createElement("dialog");
  dialog.id = "timezone-dialog";
  dialog.className = "booking-dialog timezone-dialog";
  dialog.setAttribute("aria-labelledby", "timezone-dialog-title");
  dialog.innerHTML = `<h2 id="timezone-dialog-title" tabindex="-1" autofocus>${escapeHtml(ui("form.timezone", "Časové pásmo", "Time zone"))}</h2><div class="timezone-dialog-picker"></div><div class="management-actions"><button type="button" class="button" data-save-zone>${escapeHtml(ui("common.save", "Uložit", "Save"))}</button><button type="button" class="button secondary" data-cancel-zone>${escapeHtml(ui("common.cancel", "Zrušit", "Cancel"))}</button></div>`;
  let chosen = attendeeTimeZone;
  mountTimezonePicker(
    dialog.querySelector<HTMLElement>(".timezone-dialog-picker")!,
    {
      current: attendeeTimeZone,
      zones: supportedTimeZones,
      text: t,
      locale,
      label: zoneLabel,
      onSelect: (zone) => {
        chosen = zone;
      },
    },
  );
  dialog
    .querySelector("[data-cancel-zone]")!
    .addEventListener("click", () => dialog.close());
  dialog.querySelector("[data-save-zone]")!.addEventListener("click", () => {
    if (chosen !== attendeeTimeZone) {
      saveJourney();
      attendeeTimeZone = chosen;
      if (selectedSlot) selectedDay = dateKey(new Date(selectedSlot));
      else if (selectedCityDay?.startsAt)
        selectedDay = dateKey(new Date(selectedCityDay.startsAt));
      if (selectedDay) visibleMonth = selectedDay.slice(0, 7);
      saveJourney();
      renderSelectedPlaceSummary();
      if (currentStep === 2) void renderCalendar(true);
      else
        app
          .querySelector(".selected-time")
          ?.replaceChildren(
            document.createTextNode(
              formatWhen(selectedSlot) + " · " + zoneLabel(attendeeTimeZone),
            ),
          );
    }
    dialog.close();
  });
  dialog.addEventListener("close", () => {
    dialog.remove();
    (trigger.isConnected
      ? trigger
      : app.querySelector<HTMLButtonElement>("[data-edit-timezone]")
    )?.focus({ preventScroll: true });
  });
  document.body.append(dialog);
  dialog.showModal();
  dialog
    .querySelector<HTMLElement>("#timezone-dialog-title")
    ?.focus({ preventScroll: true });
});
function summaryDateMarkup(): string {
  if (selectedSlot)
    return `<div class="summary-start"><dt>${escapeHtml(ui("public.start", "Začátek", "Start"))}</dt><dd><time datetime="${escapeHtml(selectedSlot)}">${escapeHtml(displayZonedDateTime(selectedSlot, attendeeTimeZone))}</time><small>${escapeHtml(zoneLabel(attendeeTimeZone))} <button type="button" class="summary-timezone-edit" data-edit-timezone>${escapeHtml(ui("timezone.edit", "Upravit", "Edit"))}</button></small></dd></div>`;
  if (selectedDay)
    return `<div class="summary-start"><dt>${escapeHtml(ui("public.date", "Datum", "Date"))}</dt><dd><time datetime="${escapeHtml(selectedDay)}">${escapeHtml(displayCalendarDate(selectedDay))}</time><small>${escapeHtml(zoneLabel(attendeeTimeZone))} <button type="button" class="summary-timezone-edit" data-edit-timezone>${escapeHtml(ui("timezone.edit", "Upravit", "Edit"))}</button></small></dd></div>`;
  return `<div class="summary-start"><dt>${escapeHtml(ui("public.date", "Datum", "Date"))}</dt><dd>${escapeHtml(ui("calendar.chooseDate", "Vyberte datum", "Choose a date"))}<small>${escapeHtml(zoneLabel(attendeeTimeZone))} <button type="button" class="summary-timezone-edit" data-edit-timezone>${escapeHtml(ui("timezone.edit", "Upravit", "Edit"))}</button></small></dd></div>`;
}
function meetingSummary(): string {
  if (!selectedType) return "";
  const summaryImageUrl = selectedCityDay
    ? cityDayArtworkUrl(selectedCityDay)
    : bookingArtworkUrl(selectedType.image);
  const selectedLocation = selectedType.locations.find(
    (item) => item.id === selectedLocationId,
  );
  return `<section class="meeting-summary ${summaryImageUrl ? "" : "without-artwork"}"><div class="meeting-summary-media" aria-hidden="true">${summaryImageUrl ? `<img src="${escapeHtml(summaryImageUrl)}" alt="" />` : ""}</div><div><p class="eyebrow">${selectedCityDay && selectedLocation?.kind !== "phone" ? `${escapeHtml(t("home.inCity", "Osobně v {city}", { city: selectedCityDay.city }))}` : selectedType.locations.every((item) => item.kind === "phone") ? escapeHtml(ui("meeting.phone", "Telefonát", "Phone call")) : selectedType.locations.some((item) => item.kind === "teams") ? (selectedType.locations.every((item) => item.kind === "teams") ? escapeHtml(locale === "cs" ? "Online setkání" : "Online meeting") : escapeHtml(t("meeting.onlineOrPersonal", "Online nebo osobně"))) : escapeHtml(t("meeting.personal", "Osobní setkání"))}</p><h1>${escapeHtml(selectedCityDay?.title || selectedType.name)}</h1>${selectedCityDay ? `<p class="meeting-summary-type">${escapeHtml(selectedType.name)}</p>` : ""}<p>${escapeHtml(selectedType.description)}</p></div><dl>${summaryDateMarkup()}<div><dt>${escapeHtml(t("meeting.length", "Délka"))}</dt><dd>${selectedType.durationMinutes} min</dd></div><div><dt>${escapeHtml(t("meeting.place", "Místo"))}</dt><dd>${escapeHtml(selectedLocation?.kind === "phone" ? ui("meeting.phone", "Telefonát", "Phone call") : selectedCityDay ? ("usesFixedAddress" in selectedType && selectedType.usesFixedAddress ? selectedLocation?.address || selectedCityDay.city : selectedCityDay.city) : cityFilter || selectedLocation?.address || selectedLocation?.label || t("meeting.chooseBelow", "Vyberete níže"))}</dd></div></dl></section>`;
}

function errorIllustration(error: unknown): string {
  const value =
    error && typeof error === "object"
      ? (error as { status?: number; name?: string })
      : {};
  const illustration =
    isMaintenance(error) || value.status === 503
      ? "503-maintenance"
      : value.status === 401
        ? "session-expired"
        : value.status === 403
          ? "403-access-denied"
          : value.status === 404
            ? "404-not-found"
            : value.status === 410
              ? "link-expired"
              : value.status === 429
                ? "429-too-many-requests"
                : value.status === 408 ||
                    value.status === 504 ||
                    value.name === "TimeoutError"
                  ? "request-timeout"
                  : error instanceof TypeError
                    ? "connection-lost"
                    : "500-server-error";
  return `<img class="error-illustration error-illustration-hero" src="/booking/error-illustrations/${illustration}.svg" alt="" />`;
}

function renderError(error: unknown, retry: () => void): void {
  if (isMaintenance(error)) {
    stopFormProtection();
    const title =
      locale === "cs"
        ? "Rezervace jsou dočasně pozastavené."
        : "Booking is temporarily paused.";
    const message =
      locale === "cs"
        ? "Právě probíhá údržba rezervační služby. Prosím, vraťte se později. Děkujeme za pochopení."
        : "The booking service is undergoing maintenance. Please come back later. Thank you for your understanding.";
    app.innerHTML = `<section class="card confirmation">${errorIllustration(error)}<h1>${escapeHtml(title)}</h1><p>${escapeHtml(message)}</p><a class="button" href="${escapeHtml(hostWebsite || `${bookingBasePath}/`)}">${escapeHtml(locale === "cs" ? "Zpět na osobní web" : "Back to website")}</a></section>`;
    focusView();
    return;
  }
  const status =
    error && typeof error === "object" && "status" in error
      ? Number(error.status)
      : 0;
  const eyebrow =
    status === 404
      ? "404"
      : status === 410
        ? ui("error.expiredEyebrow", "Odkaz vypršel", "Link expired")
        : t("error.unavailable", "Rezervace není dostupná");
  const heading =
    status === 404
      ? ui(
          "error.notFoundHeading",
          "Tuto stránku jsme nenašli.",
          "We could not find this page.",
        )
      : status === 410
        ? ui(
            "error.expiredHeading",
            "Platnost odkazu skončila.",
            "This link has expired.",
          )
        : t("error.heading", "Něco se nepodařilo.");
  app.innerHTML = `<section class="card error-card">${errorIllustration(error)}<p class="eyebrow">${escapeHtml(eyebrow)}</p><h1>${escapeHtml(heading)}</h1><p>${escapeHtml(problemMessage(error))}</p><button class="button" id="retry">${escapeHtml(t("error.retry", "Zkusit znovu"))}</button></section>`;
  document
    .querySelector<HTMLButtonElement>("#retry")
    ?.addEventListener("click", retry);
  focusView();
}

function isMaintenance(
  error: unknown,
): error is { type: string; detail: string } {
  return Boolean(
    error &&
    typeof error === "object" &&
    "type" in error &&
    typeof error.type === "string" &&
    error.type.endsWith("/booking-maintenance") &&
    "detail" in error &&
    typeof error.detail === "string",
  );
}
async function confirmedMaintenance(): Promise<boolean> {
  try {
    const { data } = await api.GET(
      "/booking/public/{profileSlug}/service-status",
      {
        params: { path: { profileSlug } },
        cache: "no-store",
      },
    );
    return data?.maintenance === true;
  } catch {
    return false;
  }
}
const maintenanceProblem = {
  type: "https://api.palavio.com/problems/booking-maintenance",
  detail: "Maintenance",
};
async function loadProfile(): Promise<void> {
  const revision = ++loadRevision;
  try {
    const response = await api.GET("/booking/public-configuration", {
      params: { query: { baseUrl: location.origin + bookingBasePath } },
    });
    if (revision !== loadRevision) return;
    if (response.error || !response.data) {
      renderError(response.error, () => void loadProfile());
      return;
    }
    hostPublished = response.data.published;
    profileSlug = response.data.profileSlug;
    hostWebsite = response.data.personalWebsite ?? "";
    hostDisplayName = response.data.displayName;
    const brand = document.querySelector(".wordmark-name");
    if (brand) brand.textContent = response.data.displayName;
    if (logo) logo.hidden = profileSlug !== "michal";
    document.title = `${response.data.displayName} | Booking`;
    for (const element of document.querySelectorAll<HTMLAnchorElement>(
      ".wordmark, footer a",
    )) {
      element.href = hostWebsite || `${bookingBasePath}/`;
      element.setAttribute("aria-label", response.data.displayName);
    }
  } catch (error) {
    if (revision !== loadRevision) return;
    renderError(error, () => void loadProfile());
    return;
  }

  visitorAddressRevision++; // Cancel old lookup responses without discarding a confirmed address.
  homeAbort.abort();
  calendarAbort.abort();
  calendarAbort = new AbortController();
  dayOffer = null;
  dayPlaceSlots.clear();
  monthOrigins = [];
  const requestedDate = new URL(location.href).searchParams.get("date") ?? "";
  placeView = /^\d{4}-\d{2}-\d{2}$/.test(requestedDate);
  selectedDay = placeView ? requestedDate : "";

  app.innerHTML = `<section class="card" aria-busy="true"><h1>${escapeHtml(ui("loading.title", "Rezervace schůzky", "Book a meeting"))}</h1><div class="calendar-progress" role="status"><span class="calendar-progress-track" aria-hidden="true"></span>${escapeHtml(ui("loading.profile", "Načítám nabídku…", "Loading booking options…"))}</div></section>`;
  cityFilter = new URLSearchParams(location.search).get("city") ?? "";
  filteredCityDays = [];
  citySlotOrigins.clear();
  const pathParts = bookingRelativePath(location.pathname)
    .split("/")
    .filter(Boolean);
  selectedCityDay = undefined;
  selectedType = undefined;
  try {
    if (!hostPublished && pathParts[0] === "manage" && pathParts[1])
      return void loadManagement(pathParts[1]);
    if (!hostPublished && pathParts[0] === "access" && pathParts[1])
      return renderManagementAccess(pathParts[1]);
    const maintenance = await confirmedMaintenance();
    if (revision !== loadRevision) return;
    if (maintenance)
      return renderError(maintenanceProblem, () => void loadProfile());
    const { data, error } = await api.GET("/booking/public/{profileSlug}", {
      params: {
        path: { profileSlug },
        query: { lang: locale, includeCityDays: "false" },
      },
    });
    if (error) throw error;
    if (revision !== loadRevision) return;
    profile = data;
    if (pathParts[0] === "access" && pathParts[1])
      return renderManagementAccess(pathParts[1]);
    if (pathParts[0] === "proposal" && pathParts[1])
      return renderBookingProposal(
        app,
        api,
        pathParts[1],
        locale,
        updatePublicShell,
        (problem) => {
          if (!isMaintenance(problem)) return false;
          renderError(problem, () => void loadProfile());
          return true;
        },
      );
    if (!browserTimeZone && !draft?.timeZone) attendeeTimeZone = data.timezone;
    locale = data.locale;
    document.documentElement.lang = locale;
    localStorage.setItem("booking-locale", locale);
    syncLocaleSwitcher();
    if (pathParts[0] === "availability") return renderSharedAvailability();
    if (pathParts[0] === "manage" && pathParts[1])
      return void loadManagement(pathParts[1]);
    if (pathParts[0] === "city" && pathParts[1]) {
      const {
        data: cityDay,
        error: cityError,
        response: cityResponse,
      } = await api.GET("/booking/public/{profileSlug}/city/{cityDaySlug}", {
        params: {
          path: { profileSlug, cityDaySlug: pathParts[1] },
          query: { lang: locale },
        },
      });
      if (cityError) {
        if (cityResponse.status === 404) {
          stopFormProtection();
          app.innerHTML = `<section class="card error-card"><h1>${escapeHtml(ui("cityDay.unpublished", "Tento CityDay již není v nabídce.", "This CityDay is no longer available."))}</h1><p>${escapeHtml(ui("cityDay.chooseAnother", "Vyberte prosím jiný den nebo typ schůzky.", "Please choose another day or meeting type."))}</p><a class="button" href="${bookingBasePath}/">${escapeHtml(ui("cityDay.backToBooking", "Vybrat jinou schůzku", "Choose another meeting"))}</a></section>`;
          focusView();
          return;
        }
        const redirect = String(
          (cityError as ProblemDetails).detail ?? "",
        ).match(/^CITY_DAY_REDIRECT:(.+)$/)?.[1];
        if (redirect) {
          window.location.replace(
            `${bookingBasePath}/city/${encodeURIComponent(redirect)}`,
          );
          return;
        }
        throw cityError;
      }
      selectedCityDay = cityDay;
      cityDayInformation = cityDay;
      watchCityDayEnd();
      if (cityDayEnded()) return renderEndedCityDay();
      selectedCityPlaceId =
        draft?.path === location.pathname ? (draft.cityPlaceId ?? "") : "";
      if (revision !== loadRevision) return;
      visibleMonth = (
        cityDay.startsAt
          ? dateKey(new Date(cityDay.startsAt))
          : cityDay.localDate
      ).slice(0, 7);
      const typeSlug =
        new URLSearchParams(location.search).get("meetingType") ??
        history.state?.typeSlug ??
        (draft?.path === location.pathname ? draft.typeSlug : "");
      selectedType = cityDay.meetingTypes.find(
        (type) => type.slug === typeSlug,
      );
      if (
        selectedType &&
        (new URLSearchParams(location.search).has("meetingType") ||
          (history.state?.bookingStep ?? draft?.step ?? 1) > 1)
      )
        return restoreSelection();
      return renderCityDay();
    }
    selectedCityPlaceId = "";
    selectedType = data.meetingTypes.find((type) => type.slug === pathParts[0]);
    if (pathParts[0] && !selectedType) {
      const { data: directType, error: directError } = await api.GET(
        "/booking/public/{profileSlug}/{meetingTypeSlug}",
        {
          params: {
            path: { profileSlug, meetingTypeSlug: pathParts[0] },
            query: { lang: locale },
          },
        },
      );
      if (directError) {
        if (directError.status !== 404) throw directError;
        const target = new URL(`${bookingBasePath}/`, location.origin);
        target.searchParams.set("lang", locale);
        target.searchParams.set("timeZone", attendeeTimeZone);
        history.replaceState({}, "", target);
        renderMeetingTypes();
        const note = document.createElement("p");
        note.className = "notice";
        note.setAttribute("role", "status");
        note.textContent = ui(
          "meeting.linkUnavailable",
          "Tento typ schůzky již není dostupný. Vyberte si prosím z aktuální nabídky.",
          "This meeting type is no longer available. Please choose from the current offer.",
        );
        app.prepend(note);
        return;
      }
      selectedType = directType;
      if (selectedType.slug !== pathParts[0]) {
        const canonical = new URL(location.href);
        canonical.pathname =
          `${bookingBasePath}/` + encodeURIComponent(selectedType.slug);
        for (const key of [...canonical.searchParams.keys()])
          if (
            ![
              "lang",
              "timeZone",
              "date",
              "city",
              "place",
              "locationId",
              "time",
            ].includes(key)
          )
            canonical.searchParams.delete(key);
        if (
          !selectedType.locations.some(
            (x) => x.id === canonical.searchParams.get("locationId"),
          )
        )
          canonical.searchParams.delete("locationId");
        history.replaceState({}, "", canonical);
      }
    }
    if (selectedType) {
      selectedLocationId = selectedType.locations[0]?.id ?? "";
      if (
        selectedType.locations.every((item) => item.kind === "in_person") &&
        !rescheduleActionToken
      ) {
        // Start the prepared default month alongside its city catalog. The shared
        // request cache deduplicates fetchMonth; no second calendar calculation.
        if (
          !cityFilter &&
          !placeView &&
          !new URL(location.href).searchParams.has("locationId")
        ) {
          const bounds = monthBounds(visibleMonth);
          void api
            .GET(
              "/booking/public/{profileSlug}/{meetingTypeSlug}/availability",
              {
                params: {
                  path: { profileSlug, meetingTypeSlug: selectedType.slug },
                  query: {
                    from: bounds.from,
                    to: bounds.to,
                    includeCityDays: "true",
                    timeZone: attendeeTimeZone,
                    locationId: selectedLocationId || undefined,
                  },
                },
              },
            )
            .catch(() => {});
        }
        // The catalog itself enforces permitted types.
        const catalog = await api.GET(
          "/booking/public/{profileSlug}/{meetingTypeSlug}/cities",
          {
            params: {
              path: { profileSlug, meetingTypeSlug: selectedType.slug },
              query: { catalogOnly: "true" },
            },
          },
        );
        if (catalog.error) throw catalog.error;
        homeCityName = catalog.data.homeCity?.name ?? "";
        homeCityImage = catalog.data.homeCity?.image ?? null;
        if (cityFilter === homeCityName) cityFilter = "";
        if (revision !== loadRevision) return;
        // The catalog already applies allowed meeting types. Availability supplies
        // current place policy only for the month/day the visitor actually opens.
        filteredCityDays = catalog.data.cityDays.map((day) => ({
          ...day,
          meetingTypes: [selectedType!],
          locationHints: [],
          attribution: "",
        }));
        if (
          cityFilter &&
          !filteredCityDays.some((day) => day.publicCity?.name === cityFilter)
        )
          cityFilter = "";
        const firstCityDate = filteredCityDays.find(
          (day) => day.publicCity?.name === cityFilter,
        );
        if (cityFilter && firstCityDate)
          visibleMonth = (
            firstCityDate.startsAt
              ? dateKey(new Date(firstCityDate.startsAt))
              : firstCityDate.localDate
          ).slice(0, 7);
      } else cityFilter = "";
      return restoreSelection();
    }
    renderMeetingTypes();
  } catch (error) {
    const maintenance = await confirmedMaintenance();
    if (revision !== loadRevision) return;
    renderError(
      maintenance ? maintenanceProblem : error,
      () => void loadProfile(),
    );
  }
}

async function restoreSelection(): Promise<void> {
  const requestedLocation = new URL(location.href).searchParams.get(
    "locationId",
  );
  if (
    (!history.state?.bookingStep ||
      new URL(location.href).searchParams.get("shortcut") === "1") &&
    selectedType?.locations.some((l) => l.id === requestedLocation)
  )
    selectedLocationId = requestedLocation!;
  // A refreshed CityDay can restore its type directly from history, bypassing
  // the initial type picker. Re-establish its mode before resolving places.
  if (!selectedType?.locations.some((item) => item.id === selectedLocationId))
    selectedLocationId = selectedType?.locations[0]?.id ?? "";
  const needsPlace = selectedType?.locations.some(
    (item) => item.kind === "in_person",
  );
  if (
    draft?.path === location.pathname &&
    draft.typeSlug === selectedType?.slug
  ) {
    selectedCityPlaceId = needsPlace ? (draft.cityPlaceId ?? "") : "";
    if (!needsPlace) {
      cityFilter = "";
      placeView = false;
    }
    const restoredAddress = draft.fields.guestLocationProposal ?? "";
    if (restoredAddress !== visitorAddress) clearAddressConfirmation();
    visitorAddress = restoredAddress;
    visitorLocationDetails = draft.fields.guestLocationDetails ?? "";
    visitorLocationDetailsAddress =
      draft.fields.visitorLocationDetailsAddress ?? restoredAddress;
    visitorAddressQuery =
      draft.fields.visitorAddressQuery ||
      visitorAddressQuery ||
      restoredAddress;
    if (!validAddressConfirmation() && restoredAddress && !visitorAddressNotice)
      visitorAddressNotice = ui(
        "places.verifyRestored",
        "Po obnovení stránky ověřte prosím adresu znovu.",
        "Please verify the address again after reloading the page.",
      );
    // A grouped calendar is never itself a booking origin. A City Day slot
    // must be selected before entering the existing concrete City Day route.
    selectedSlot = cityFilter && !selectedCityDay ? "" : draft.slot;
    if (selectedType?.locations.some((item) => item.id === draft!.locationId))
      selectedLocationId = draft.locationId;
    if (selectedSlot) {
      selectedDay = dateKey(new Date(selectedSlot));
      visibleMonth = selectedDay.slice(0, 7);
    }
    const requestedStep = history.state?.bookingStep ?? draft.step;
    if (requestedStep === 3 && selectedSlot) {
      if (
        selectedType &&
        selectedSlot &&
        selectedType.locations.some((item) => item.id === selectedLocationId) &&
        (selectedCityPlaceId !== "other" || validAddressConfirmation())
      )
        return renderReservationForm();
      selectedSlot = "";
    }
  } else {
    selectedSlot = "";
    selectedDay = "";
  }
  if (!needsPlace) {
    selectedCityPlaceId = "";
    visitorAddress = "";
    visitorLocationDetails = "";
    visitorLocationDetailsAddress = "";
    visitorAddressQuery = "";
    clearAddressConfirmation();
    cityFilter = "";
    placeView = false;
    dayOffer = null;
  }
  const previousMonth = history.state?.calendarMonth;
  if (typeof previousMonth === "string" && /^\d{4}-\d{2}$/.test(previousMonth))
    visibleMonth = previousMonth;
  const previousDay = history.state?.calendarDay;
  if (
    typeof previousDay === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(previousDay)
  )
    selectedDay = previousDay;
  const date = new URL(location.href).searchParams.get("date");
  if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    selectedDay = date;
    visibleMonth = date.slice(0, 7);
    placeView = Boolean(needsPlace);
  }
  const requestedPlace = new URL(location.href).searchParams.get("place");
  if (requestedPlace && /^[0-9a-f-]{36}$/i.test(requestedPlace) && needsPlace) {
    selectedCityPlaceId = requestedPlace;
  }
  const requestedTime = new URL(location.href).searchParams.get("time");
  if (
    requestedTime &&
    (!history.state?.bookingStep ||
      new URL(location.href).searchParams.get("shortcut") === "1") &&
    /^\d{4}-\d{2}-\d{2}T/.test(requestedTime) &&
    Number.isFinite(Date.parse(requestedTime))
  )
    selectedSlot = requestedTime;
  if (selectedSlot) {
    // The instant is authoritative when a zone change crosses midnight;
    // the original CityDay date in the link must not hide the selected slot.
    selectedDay = dateKey(new Date(selectedSlot));
    visibleMonth = selectedDay.slice(0, 7);
  }
  await renderCalendar();
}

function renderMeetingTypes(focus = true): void {
  clearShortcutSelection();
  stopSlotMonitoring();
  stopFormProtection();
  selectedType = undefined;
  selectedSlot = "";
  setStep(1);
  selectedCityDay = undefined;
  rescheduleActionToken = null;
  app.innerHTML = `<section class="hero"><div class="hero-copy"><p class="eyebrow">${escapeHtml(t("home.title", "Rezervace schůzky"))}</p><h1>${escapeHtml(ui("home.chooseMeeting", "Potkejme se", "Let's meet"))}</h1><p>${escapeHtml(["Vyberte si vhodný termín pro naši schůzku.", "Choose a suitable time for our meeting.", ""].includes(profile.introduction) ? ui("public.onlineOrInPersonFindTheWayToMeetThatSuitsYou", "Online nebo osobně. Najděte způsob setkání, který vám vyhovuje.", "Online or in person. Find the way to meet that suits you.") : profile.introduction)}</p></div><div class="hero-note"><strong>${escapeHtml(ui("home.noteTitle", "Rovnou do kalendáře.", "Straight to your calendar."))}</strong><span>${escapeHtml(ui("home.noteBody", "Dostupné časy vycházejí z mého kalendáře. Po rezervaci vám přijde pozvánka.", "Available times come from my calendar. After booking, you’ll receive an invitation."))}</span></div></section><section class="type-grid" aria-label="${escapeHtml(t("home.meetingTypesLabel", "Typy schůzek"))}">${profile.cityDays.map((day) => `<a class="type-card city-day-type-card" href="${bookingBasePath}/city/${encodeURIComponent(day.slug)}"><span>${escapeHtml(t("home.inCity", "Osobně v {city}", { city: day.city }))} · ${escapeHtml(displayDate(day.localDate))}</span><h2>${escapeHtml(t("home.cityMeeting", "Setkání v {city}", { city: day.city }))}</h2><p>${escapeHtml(t("home.cityDescription", "Vyberte si osobní setkání během mého dne ve městě."))}</p><strong>${escapeHtml(slotCountLabel(day.availableSlotCount ?? 0))} →</strong></a>`).join("")}${profile.meetingTypes.map((type) => `<a class="type-card" href="${bookingBasePath}/${encodeURIComponent(type.slug)}">${type.image?.cardUrl ? `<picture>${type.image.vectorUrl ? `<source type="image/svg+xml" srcset="${escapeHtml(type.image.vectorUrl)}"/>` : ""}<img class="type-card-image" src="${escapeHtml(type.image.largeUrl || type.image.cardUrl)}" alt="${escapeHtml(type.name)}" loading="lazy"/></picture>` : ""}<span>${escapeHtml(type.locations.map((item) => item.label).join(" · "))}</span><h2>${escapeHtml(type.name)}</h2><p>${escapeHtml(type.description)}</p><strong>${escapeHtml(t("home.duration", "{minutes} minut", { minutes: String(type.durationMinutes) }))} →</strong></a>`).join("")}</section>`;
  const grid = app.querySelector<HTMLElement>(".type-grid")!;
  const standard = Array.from(
    grid.querySelectorAll<HTMLAnchorElement>(
      ".type-card:not(.city-day-type-card)",
    ),
  );
  const days = Array.from(
    grid.querySelectorAll<HTMLAnchorElement>(".city-day-type-card"),
  );
  const section = (title: string, cards: HTMLElement[]) => {
    if (!cards.length) return;
    const node = document.createElement("section");
    node.className = "meeting-section";
    const heading = document.createElement("h2");
    heading.textContent = title;
    const list = document.createElement("div");
    list.className = cards[0]?.classList.contains("city-day-type-card")
      ? "city-day-list"
      : "type-grid";
    list.append(...cards);
    node.append(heading, list);
    if (list.className === "city-day-list") {
      node.classList.add("city-days-section");
      const introduction = document.createElement("p");
      introduction.className = "city-days-introduction";
      introduction.textContent = ui(
        "home.cityDaysIntroduction",
        "V těchto dnech se můžeme potkat osobně i mimo Brno, na následujících místech.",
        "On these days, we can also meet in person outside Brno at the locations below.",
      );
      heading.after(introduction);
      const refresh = document.createElement("button");
      refresh.type = "button";
      refresh.className = "button city-days-refresh";
      refresh.hidden = true;
      refresh.textContent =
        locale === "cs" ? "Obnovit přehled" : "Refresh overview";
      refresh.onclick = () => void refreshHomeOverview(true);
      const feedback = document.createElement("p");
      feedback.id = "city-days-refresh-status";
      feedback.setAttribute("role", "status");
      node.append(refresh, feedback);
    }
    grid.before(node);
  };
  for (const card of days) {
    const day = profile.cityDays.find((item) =>
      card.pathname.endsWith("/" + encodeURIComponent(item.slug)),
    )!;
    card.dataset.date = day.localDate;
    const title = card.querySelector("h2")!;
    title.textContent = day.title ?? day.city;
    const description = card.querySelector("p")!;
    if (day.location) description.textContent = day.location;
    else description.remove();
    const place = document.createElement("div");
    place.className = "city-day-place";
    title.before(place);
    place.append(title);
    if (day.location) place.append(description);
    card.querySelector("span")!.innerHTML =
      `<time datetime="${escapeHtml(day.localDate)}">${escapeHtml(displayDate(day.localDate))}</time>`;
  }
  section(ui("public.meetingTypes", "Typy schůzek", "Meeting types"), standard);
  section(
    "City Days",
    days.sort((a, b) => a.dataset.date!.localeCompare(b.dataset.date!)),
  );
  grid.remove();
  decorateTypeCards();
  if (focus) focusView();
  void refreshHomeOverview(true);
}

let homeRefreshPending = false;
let homeAbort = new AbortController();
async function refreshHomeOverview(manual = false): Promise<void> {
  const isHome = () =>
    bookingRelativePath(location.pathname) === "" && !selectedType;
  if (
    !isHome() ||
    document.visibilityState !== "visible" ||
    !navigator.onLine ||
    (homeRefreshPending && !manual)
  )
    return;
  homeAbort.abort();
  homeAbort = new AbortController();
  const controller = homeAbort;
  homeRefreshPending = true;
  const revision = loadRevision,
    language = locale;
  const current = () =>
    !controller.signal.aborted &&
    revision === loadRevision &&
    language === locale &&
    isHome();
  let section = app.querySelector<HTMLElement>(".city-days-section");
  if (!section) {
    section = document.createElement("section");
    section.className = "meeting-section city-days-section";
    app.append(section);
  }
  if (!section.querySelector(".city-day-list"))
    section.innerHTML = `<h2>City Days</h2><p class="city-days-introduction">${escapeHtml(ui("home.cityDaysIntroduction", "V těchto dnech se můžeme potkat osobně i mimo Brno, na následujících místech.", "On these days, we can also meet in person outside Brno at the locations below."))}</p><p role="status" id="city-days-refresh-status" class="city-days-progress"></p><div class="city-day-list"></div>`;
  const list = section.querySelector(".city-day-list")!;
  const feedback = section.querySelector("#city-days-refresh-status")!;
  const retry = (parent: Element, fn: () => void) => {
    const attempts = Number(
      (parent as HTMLElement).dataset.refreshAttempts ?? 0,
    );
    (parent as HTMLElement).dataset.refreshAttempts = String(attempts + 1);
    setTimeout(
      () => {
        if (current() && !document.hidden && navigator.onLine) fn();
      },
      Math.max([2, 5, 10, 30][attempts] ?? 60, availabilityRetrySeconds()) *
        1000,
    );
  };
  try {
    const index = await api.GET("/booking/public/{profileSlug}/city-days", {
      params: { path: { profileSlug } },
      signal: controller.signal,
      cache: "no-store",
    });
    if (!current()) return;
    if (index.error) throw index.error;
    const cityImage = (day: (typeof index.data.items)[number]) => {
      const url = cityDayArtworkUrl(day);
      return url
        ? `<img class="city-day-artwork" src="${escapeHtml(url)}" alt=""/>`
        : "";
    };
    // Replace legacy profile cards once the authoritative index is available.
    // Keep existing overview rows so their artwork and refresh state survive.
    list
      .querySelectorAll(":scope > .city-day-type-card")
      .forEach((card) => card.remove());
    let complete = 0;
    const rows = index.data.items.map((entry) => {
      const day = entry;
      const previous = list.querySelector<HTMLElement>(
        `[data-day-id="${CSS.escape(day.id)}"]`,
      );
      const row = previous ?? document.createElement("article");
      row.className = "city-day-overview-row";
      row.dataset.dayId = day.id;
      const prepared =
        typeof day.availableSlotCount === "number" &&
        day.availabilityState === "checked" &&
        (!day.availabilityFreshness ||
          day.availabilityFreshness.state === "current");
      if (prepared) {
        const full = day.availableSlotCount === 0;
        row.innerHTML = `<${full ? "article" : "a"} class="type-card city-day-type-card ${cityImage(day) ? "has-city-artwork" : ""}" data-date="${escapeHtml(day.localDate)}" ${full ? "" : `href="${bookingBasePath}/city/${encodeURIComponent(day.slug)}"`}><time datetime="${escapeHtml(day.localDate)}">${escapeHtml(displayDate(day.localDate))}</time>${cityImage(day)}<div class="city-day-place"><h2>${escapeHtml(day.title)}</h2>${day.location ? `<p>${escapeHtml(day.location)}</p>` : ""}</div><strong>${escapeHtml(day.availableSlotCount ? slotCountLabel(day.availableSlotCount ?? 0) : ui("home.noDaysSlots", "Nyn\u00ed bez voln\u00fdch term\u00edn\u016f", "No available times now"))}${full ? "" : " \u2192"}</strong></${full ? "article" : "a"}>`;
        complete++;
      } else if (!previous)
        row.innerHTML = `<article class="type-card city-day-type-card ${cityImage(day) ? "has-city-artwork" : ""}"><time>${escapeHtml(displayDate(day.localDate))}</time>${cityImage(day)}<div class="city-day-place"><h2>${escapeHtml(day.title)}</h2></div><strong role="status">${escapeHtml(day.availableSlotCount ? `${slotCountLabel(day.availableSlotCount ?? 0)} \u00b7 ${locale === "cs" ? "orienta\u010dn\u011b" : "provisional"}` : ui("home.checkingDay", "Dostupnost se obnovuje...", "Availability is refreshing..."))}</strong></article>`;
      else {
        const label = row.querySelector("strong");
        if (label)
          label.textContent = day.availableSlotCount
            ? `${slotCountLabel(day.availableSlotCount)} · ${locale === "cs" ? "orientačně" : "provisional"}`
            : ui(
                "home.checkingDay",
                "Dostupnost se obnovuje…",
                "Availability is refreshing…",
              );
      }
      list.append(row);
      return { day, row, prepared };
    });
    list.querySelectorAll<HTMLElement>("[data-day-id]").forEach((row) => {
      if (!rows.some((r) => r.day.id === row.dataset.dayId)) row.remove();
    });
    let active = 0;
    const waiters: Array<() => void> = [];
    const load = async ({
      day,
      row,
    }: Pick<(typeof rows)[number], "day" | "row">) => {
      if (active >= 3)
        await new Promise<void>((resolve) => waiters.push(resolve));
      active++;
      const release = () => {
        active--;
        waiters.shift()?.();
      };
      if (!current()) {
        release();
        return;
      }
      row.setAttribute("aria-busy", "true");
      try {
        const detail = await api.GET(
          "/booking/public/{profileSlug}/city/{cityDaySlug}/overview",
          {
            params: {
              path: { profileSlug, cityDaySlug: day.slug },
              query: { lang: locale },
            },
            signal: controller.signal,
          },
        );
        if (detail.error) throw detail.error;
        const stale =
          detail.data.availabilityFreshness?.state !== undefined &&
          detail.data.availabilityFreshness.state !== "current";
        if (!current()) return;
        const result = {
          ...detail.data,
          availableSlotCount: detail.data.availableSlotCount,
          availabilityState: stale
            ? ("needs_check" as const)
            : detail.data.availabilityState,
          availabilityCheckedAt: detail.data.availabilityCheckedAt,
        };
        profile.cityDays = profile.cityDays
          .filter((x) => x.id !== result.id)
          .concat(result);
        const label = stale
          ? result.availableSlotCount > 0
            ? `${slotCountLabel(result.availableSlotCount)} · ${locale === "cs" ? "orientačně" : "provisional"}`
            : locale === "cs"
              ? "Dostupnost se obnovuje"
              : "Availability is refreshing"
          : result.availableSlotCount > 0
            ? slotCountLabel(result.availableSlotCount)
            : ui(
                "home.noDaysSlots",
                "Nyní bez volných termínů",
                "No available times now",
              );
        const full =
          !stale &&
          (result.inPersonFull === true ||
            (result.availabilityState === "checked" &&
              result.availableSlotCount === 0));
        row.innerHTML = `<${full ? "article" : "a"} class="type-card city-day-type-card ${cityImage(day) ? "has-city-artwork" : ""}" data-date="${escapeHtml(day.localDate)}" ${full ? "" : `href="${bookingBasePath}/city/${encodeURIComponent(day.slug)}"`}><time datetime="${escapeHtml(day.localDate)}">${escapeHtml(displayDate(day.localDate))}</time>${cityImage(day)}<div class="city-day-place"><h2>${escapeHtml(result.title)}</h2>${result.location ? `<p>${escapeHtml(result.location)}</p>` : ""}</div><strong>${escapeHtml(label)}${full ? "" : " →"}</strong></${full ? "article" : "a"}>`;
        if (stale) {
          retry(row, () => void load({ day, row }));
          row.insertAdjacentHTML(
            "beforeend",
            `<p class="availability-refresh" role="status">${escapeHtml(locale === "cs" ? "Dříve ověřené termíny · obnovuji dostupnost…" : "Previously checked times · refreshing…")}</p>`,
          );
        } else delete row.dataset.refreshAttempts;
      } catch (error) {
        if (!current()) return;
        row.innerHTML = `<article class="type-card city-day-type-card ${cityImage(day) ? "has-city-artwork" : ""}"><time>${escapeHtml(displayDate(day.localDate))}</time>${cityImage(day)}<div class="city-day-place"><h2>${escapeHtml(day.title)}</h2></div><p role="alert">${escapeHtml(problemMessage(error))}</p></article>`;
        retry(row, () => void load({ day, row }));
      } finally {
        release();
        if (current()) {
          row.removeAttribute("aria-busy");
          complete++;
          feedback.textContent =
            ui("home.checkedDays", "Zkontrolováno", "Checked") +
            " " +
            Math.min(complete, rows.length) +
            " / " +
            rows.length;
        }
      }
    };
    const reloadTimers = new Map<string, ReturnType<typeof setTimeout>>();
    cacheUpdates.addEventListener(
      "refresh",
      (event) => {
        if (!current()) return;
        const { key, state } = (
          event as CustomEvent<{ key: string; state: string }>
        ).detail;
        const match = rows.find(({ day }) =>
          key.includes(`/city/${encodeURIComponent(day.slug)}/availability`),
        );
        if (!match) return;
        if (state === "updated") {
          clearTimeout(reloadTimers.get(match.day.id));
          reloadTimers.set(
            match.day.id,
            setTimeout(() => {
              if (current()) void load(match);
            }, 100),
          );
        } else if (state === "failed") {
          const notice = match.row.querySelector(".availability-refresh");
          if (notice) {
            notice.textContent =
              locale === "cs"
                ? "Obnovení se nezdařilo. Zobrazené termíny jsou starší."
                : "Refresh failed. Displayed times are older.";
            retry(notice, () => void load(match));
          }
        }
      },
      { signal: controller.signal },
    );
    feedback.textContent =
      complete === rows.length
        ? ""
        : ui(
            "home.checkingDays",
            "Obnovuji zm\u011bn\u011bn\u00e9 CityDays...",
            "Refreshing changed CityDays…",
          );
    await limited(
      rows.filter((row) => !row.prepared),
      load,
    );
    if (current()) {
      if (!rows.length)
        feedback.textContent = ui(
          "home.noDays",
          "Nyní nejsou zveřejněné žádné CityDays.",
          "No CityDays are currently published.",
        );
    }
  } catch {
    if (current()) {
      feedback.textContent = ui(
        "home.daysFailed",
        "CityDays se nepodařilo načíst. Typy schůzek můžete vybírat dál.",
        "CityDays could not be loaded. Meeting types remain available.",
      );
      retry(section, () => void refreshHomeOverview(true));
    }
  } finally {
    if (controller === homeAbort) homeRefreshPending = false;
  }
}
window.addEventListener("focus", () => void refreshHomeOverview());
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") void refreshHomeOverview();
});

function decorateTypeCards(): void {
  app.querySelectorAll<HTMLElement>(".type-card").forEach((card) => {
    card.classList.toggle("has-image", Boolean(card.querySelector("img")));
    card.classList.toggle(
      "has-vector",
      Boolean(card.querySelector('source[type="image/svg+xml"]')),
    );
    const img = card.querySelector("img");
    if (img) {
      img.alt = "";
      img.setAttribute("aria-hidden", "true");
    }
    card.querySelector("picture")?.setAttribute("class", "type-card-media");
    card.querySelector("img")?.addEventListener(
      "error",
      () => {
        card.querySelector("picture")?.remove();
        card.classList.remove("has-image");
      },
      { once: true },
    );
  });
}

function displayDate(value: string): string {
  return displayCalendarDate(value);
}

function slotCountLabel(count: number): string {
  const category = new Intl.PluralRules(locale).select(count);
  return t(
    `slots.available.${category}`,
    locale === "en"
      ? `{count} ${count === 1 ? "available time" : "available times"}`
      : `{count} ${count === 1 ? "volný termín" : count > 1 && count < 5 ? "volné termíny" : "volných termínů"}`,
    { count: String(count) },
  );
}

function calendarCountLabel(count: number): string {
  const category = new Intl.PluralRules(locale).select(count);
  return t(
    `slots.count.${category}`,
    locale === "en"
      ? `{count} ${count === 1 ? "time" : "times"}`
      : `{count} ${count === 1 ? "termín" : count > 1 && count < 5 ? "termíny" : "termínů"}`,
    { count: String(count) },
  );
}

let disposeCityTimeline: (() => void) | undefined;
let cityOverviewLoading = false;
let cityOverviewRetryTimer = 0;
let cityOverviewAttempts = 0;
function scheduleCityOverview() {
  clearTimeout(cityOverviewRetryTimer);
  if (
    currentStep !== 1 ||
    !selectedCityDay ||
    document.hidden ||
    !navigator.onLine
  )
    return;
  if (
    selectedCityDay.availabilityState === "checked" &&
    selectedCityDay.shortcuts
  ) {
    cityOverviewAttempts = 0;
    return;
  }
  cityOverviewRetryTimer = window.setTimeout(
    () => {
      if (currentStep === 1 && !document.hidden && navigator.onLine)
        void refreshCityOverview();
    },
    Math.max(
      [2, 5, 10, 30][cityOverviewAttempts++] ?? 60,
      availabilityRetrySeconds(),
    ) * 1000,
  );
}
for (const event of ["visibilitychange", "online"])
  (event === "visibilitychange" ? document : window).addEventListener(
    event,
    () => scheduleCityOverview(),
  );
async function refreshCityOverview(): Promise<void> {
  if (!selectedCityDay || cityOverviewLoading) return;
  cityOverviewLoading = true;
  const slug = selectedCityDay.slug;
  const panel = document.querySelector<HTMLElement>("#city-timeline")!;
  const status = document.createElement("p");
  status.setAttribute("role", "status");
  status.textContent = ui(
    "cityDay.checkOverview",
    "Ověřuji přehled a možnosti rezervace…",
    "Checking the schedule and booking options…",
  );
  if (!panel) {
    cityOverviewLoading = false;
    return;
  }
  panel.prepend(status);
  try {
    const response = await api.GET(
      "/booking/public/{profileSlug}/city/{cityDaySlug}/overview",
      {
        params: {
          path: { profileSlug, cityDaySlug: slug },
          query: { lang: locale },
        },
        signal: AbortSignal.timeout(20000),
      },
    );
    if (response.error) throw response.error;
    if (
      currentStep !== 1 ||
      selectedCityDay?.slug !== slug ||
      !panel.isConnected
    )
      return;
    selectedCityDay = { ...selectedCityDay, ...response.data };
    cityDayInformation = selectedCityDay;
    renderCityDay();
  } catch {
    if (!panel.isConnected) return;
    status.textContent = ui(
      "cityDay.overviewFailed",
      "Aktuální možnosti se nepodařilo ověřit. Obnovení proběhne automaticky.",
      "Current options could not be verified. They will refresh automatically.",
    );
  } finally {
    cityOverviewLoading = false;
    scheduleCityOverview();
  }
}

function clearShortcutSelection(): void {
  const url = new URL(location.href);
  if (!url.searchParams.has("shortcut")) return;
  for (const key of ["shortcut", "meetingType", "locationId", "place", "time"])
    url.searchParams.delete(key);
  history.replaceState(history.state, "", url);
}

function fixedCityDay(): boolean {
  return Boolean(
    selectedCityDay?.placeOptions?.fixedPlaceId &&
    !selectedCityDay.placeOptions.allowOther,
  );
}

function directCityDay(): boolean {
  // Availability freshness cannot select a different page layout.
  return Boolean(selectedCityDay?.meetingTypes.length);
}

function availableCityTypes() {
  return (
    selectedCityDay?.meetingTypes.filter(
      (type) =>
        selectedCityDay?.availabilityState === "checked" &&
        selectedCityDay.typeAvailability?.some(
          (item) =>
            item.meetingTypeSlug === type.slug && item.state === "available",
        ),
    ) ?? []
  );
}

async function openFixedCityDay(): Promise<void> {
  const day = selectedCityDay;
  if (!day) return;
  const revision = loadRevision;
  const types = [...day.meetingTypes].sort(
    (a, b) =>
      a.durationMinutes - b.durationMinutes || a.slug.localeCompare(b.slug),
  );
  const preferred =
    types.find((type) =>
      day.typeAvailability?.some(
        (item) =>
          item.meetingTypeSlug === type.slug && item.state === "available",
      ),
    ) ?? types[0];
  if (
    !preferred ||
    revision !== loadRevision ||
    selectedCityDay?.slug !== day.slug
  )
    return;
  selectedType = preferred;
  selectedLocationId = preferred.locations[0]?.id ?? "";
  selectedCityPlaceId = day.placeOptions?.fixedPlaceId ?? "";
  selectedDay = day.localDate;
  selectedSlot = "";
  const url = new URL(location.href);
  url.searchParams.set("meetingType", preferred.slug);
  url.searchParams.set("date", day.localDate);
  if (selectedCityPlaceId) url.searchParams.set("place", selectedCityPlaceId);
  else url.searchParams.delete("place");
  history.replaceState(
    { ...history.state, bookingStep: 2, typeSlug: preferred.slug },
    "",
    url,
  );
  await renderCalendar();
}

function mountFixedCityDayTypes(): void {
  document.querySelector(".cityday-type-switch")?.remove();
  if (
    !selectedCityDay ||
    selectedCityDay.meetingTypes.length < 2 ||
    rescheduleActionToken
  )
    return;
  const grid = document.querySelector(".calendar-and-slots");
  if (!grid) return;
  const choices = document.createElement("fieldset");
  choices.className = "cityday-type-switch";
  const legend = document.createElement("legend");
  legend.textContent = ui(
    "cityDay.meetingTypesLabel",
    "Typ osobního setkání",
    "Meeting type",
  );
  choices.append(legend);
  for (const type of [...selectedCityDay.meetingTypes].sort(
    (a, b) => a.durationMinutes - b.durationMinutes,
  )) {
    const state = selectedCityDay.typeAvailability?.find(
      (item) => item.meetingTypeSlug === type.slug,
    )?.state;
    if (state === "unavailable" && type.slug !== selectedType?.slug) continue;
    const label = document.createElement("label");
    const input = document.createElement("input");
    input.type = "radio";
    input.name = "cityday-meeting-type";
    input.value = type.slug;
    input.checked = type.slug === selectedType?.slug;
    label.append(input);
    const source =
      type.image?.vectorUrl || type.image?.largeUrl || type.image?.cardUrl;
    if (source) {
      const image = document.createElement("img");
      image.src = source;
      image.alt = "";
      image.className = "city-choice-thumbnail";
      label.append(image);
    }
    label.append(
      document.createTextNode(
        type.name.includes(String(type.durationMinutes))
          ? type.name
          : `${type.name} · ${type.durationMinutes} min`,
      ),
    );
    input.onchange = () => {
      if (!input.checked || type.slug === selectedType?.slug || submitting)
        return;
      const url = new URL(location.href);
      url.searchParams.set("meetingType", type.slug);
      url.searchParams.set("date", selectedCityDay!.localDate);
      if (selectedCityPlaceId && selectedCityPlaceId !== "other")
        url.searchParams.set("place", selectedCityPlaceId);
      else url.searchParams.delete("place");
      for (const key of ["time", "shortcut", "locationId"])
        url.searchParams.delete(key);
      saveJourney();
      selectedSlot = "";
      selectedType = type;
      selectedLocationId = type.locations[0]?.id ?? "";
      history.pushState({ bookingStep: 2, typeSlug: type.slug }, "", url);
      const summary = app.querySelector(".meeting-summary");
      if (summary) summary.outerHTML = meetingSummary();
      saveJourney();
      void renderCalendar(true);
    };
    choices.append(label);
  }
  grid.querySelector(".city-place-heading")?.after(choices);
}

function renderCityDay(): void {
  if (cityDayEnded()) return renderEndedCityDay();
  if (!selectedCityDay) return renderMeetingTypes();
  if (directCityDay() && selectedCityDay.meetingTypes.length) {
    void openFixedCityDay();
    return;
  }
  // Older API versions do not expose the timeline yet.
  if (!selectedCityDay.timeline && selectedCityDay.meetingTypes.length === 1) {
    selectedType = selectedCityDay.meetingTypes[0];
    selectedLocationId = selectedType?.locations[0]?.id ?? "";
    selectedSlot = "";
    selectedDay = "";
    setStep(2);
    void renderCalendar();
    return;
  }
  stopSlotMonitoring();
  stopFormProtection();
  selectedType = undefined;
  selectedSlot = "";
  setStep(1);
  const cityImageUrl = cityDayArtworkUrl(selectedCityDay);
  app.innerHTML = `<section class="hero city-day-hero ${cityImageUrl ? "has-identity-artwork" : ""}">${cityImageUrl ? `<img class="city-day-hero-artwork" src="${escapeHtml(cityImageUrl)}" alt="" />` : ""}<div class="hero-copy"><p class="eyebrow">${escapeHtml(t("home.inCity", "Osobně v {city}", { city: selectedCityDay.city }))}</p><h1>${escapeHtml(selectedCityDay.title || displayDate(selectedCityDay.localDate))}</h1><p>${escapeHtml(displayDate(selectedCityDay.localDate))}</p><p>${escapeHtml(t("cityDay.description", "Vyberte typ osobního setkání. Nabízíme pouze časy, které zohledňují můj kalendář a cestovní rezervu."))}</p></div><div class="hero-note"><strong>${escapeHtml(!selectedCityDay.meetingTypes.length ? ui("cityDay.unavailable", "Termíny nyní nejsou k dispozici.", "Appointments are currently unavailable.") : selectedCityDay.availabilityState === "needs_check" || !selectedCityDay.shortcuts ? ui("cityDay.needsCheck", "Dostupnost čeká na ověření.", "Availability is awaiting a check.") : t("cityDay.verifiedSlots", "{count} ověřených termínů.", { count: String(selectedCityDay.availableSlotCount) }))}</strong><span>${escapeHtml(t("cityDay.locationLater", "Místo může být potvrzeno později podle okolních schůzek."))}</span></div></section>${!selectedCityDay.meetingTypes.length ? `<p class="notice" role="status">${escapeHtml(ui("cityDay.noTypes", "Pro tento den nyní nejsou dostupné žádné typy schůzek. Vyberte prosím jinou možnost rezervace.", "No meeting types are currently available for this day. Please choose another booking option."))}</p>` : ""}<section class="city-location-hints" id="city-timeline"></section><section class="type-grid" aria-label="${escapeHtml(t("cityDay.meetingTypesLabel", "Typy osobních setkání"))}">${availableCityTypes()
    .map(
      (type) =>
        `<button class="type-card city-template" data-city-template="${escapeHtml(type.slug)}"><span>${escapeHtml(t("home.inCity", "Osobně v {city}", { city: selectedCityDay!.city }))}</span><h2>${escapeHtml(type.name)}</h2><p>${escapeHtml(type.description)}</p><strong>${escapeHtml(t("home.duration", "{minutes} minut", { minutes: String(type.durationMinutes) }))} →</strong></button>`,
    )
    .join(
      "",
    )}</section><a class="back-link city-back" href="${bookingBasePath}/">← ${escapeHtml(t("cityDay.allOptions", "Všechny možnosti rezervace"))}</a>`;
  app.querySelectorAll<HTMLElement>("[data-city-template]").forEach((card) => {
    const type = selectedCityDay!.meetingTypes.find(
      (item) => item.slug === card.dataset.cityTemplate,
    );
    if (type && "image" in type && type.image?.cardUrl)
      card.insertAdjacentHTML(
        "beforeend",
        `<picture><img class="type-card-image" src="${escapeHtml(type.image.vectorUrl || type.image.largeUrl || type.image.cardUrl)}" alt="${escapeHtml(type.name)}" loading="lazy"/></picture>`,
      );
  });
  disposeCityTimeline?.();
  disposeCityTimeline = mountCityDayTimeline(
    document.querySelector<HTMLElement>("#city-timeline")!,
    selectedCityDay,
    locale,
    mapyTileApiKey,
    (shortcut: Shortcut) => {
      const target = new URL(location.href);
      target.searchParams.set("meetingType", shortcut.meetingTypeSlug);
      target.searchParams.set("locationId", shortcut.locationId);
      target.searchParams.set("place", shortcut.placeId);
      target.searchParams.set("time", shortcut.startsAt);
      target.searchParams.set("date", selectedCityDay!.localDate);
      target.searchParams.set("shortcut", "1");
      void navigateBooking(target);
    },
    cityMapCenter,
  );
  if (
    selectedCityDay.capacity?.state === "blocked" ||
    selectedCityDay.bookingRestrictions?.length
  ) {
    const notice = document.createElement("p");
    notice.className = "notice";
    notice.setAttribute("role", "status");
    notice.textContent = ui(
      "cityDay.blocked",
      "Rezervace nyn\u00ed omezuje nastaven\u00ed dostupnosti. Tento den nen\u00ed ozna\u010den jako napln\u011bn\u00fd.",
      "Availability is blocked by booking or venue settings. This day is not marked full.",
    );
    document.querySelector(".type-grid")?.before(notice);
  }
  if (selectedCityDay.inPersonFull) {
    const description = document.querySelector(
      ".city-day-hero .hero-copy > p:last-child",
    );
    if (description)
      description.textContent =
        locale === "cs"
          ? "Osobní program je naplněný. Níže najdete přehled dne."
          : "The personal programme is full. The day’s schedule is shown below.";
    const hint = document.querySelector(".city-day-hero .hero-note > span");
    if (hint)
      hint.textContent =
        locale === "cs"
          ? "Pro další schůzku prosím vyberte jiný den."
          : "Please choose another day for a meeting.";
    const notice = document.createElement("p");
    notice.className = "notice";
    notice.setAttribute("role", "status");
    notice.textContent = ui(
      "cityDay.full",
      "Tento den je již naplněný. Vyberte prosím jiný den.",
      "This day is fully booked. Please choose another day.",
    );
    document.querySelector(".type-grid")?.replaceWith(notice);
  }
  if (
    selectedCityDay.typeAvailability?.some(
      (item) => item.state === "requires_address",
    ) &&
    !availableCityTypes().length
  ) {
    const notice = document.createElement("p");
    notice.className = "notice";
    notice.textContent = ui(
      "cityDay.addressRequired",
      "Případný termín na vlastní adrese je nutné nejprve ověřit.",
      "Any time at your own address must be verified first.",
    );
    document.querySelector(".type-grid")?.before(notice);
    for (const item of selectedCityDay.typeAvailability.filter(
      (item) => item.state === "requires_address",
    )) {
      const type = selectedCityDay.meetingTypes.find(
        (t) => t.slug === item.meetingTypeSlug,
      )!;
      const button = document.createElement("button");
      button.type = "button";
      button.className = "text-button";
      button.textContent =
        ui(
          "cityDay.verifyAddress",
          "Ověřit vlastní adresu",
          "Check your own address",
        ) +
        " · " +
        type.name;
      button.onclick = () => {
        selectedType = type;
        selectedLocationId =
          type.locations.find((l) => l.kind === "in_person")?.id ?? "";
        selectedCityPlaceId = "other";
        selectedDay = selectedCityDay!.localDate;
        void renderCalendar();
      };
      notice.append(button);
    }
  }
  scheduleCityOverview();
  decorateTypeCards();
  document
    .querySelectorAll<HTMLButtonElement>("[data-city-template]")
    .forEach((button) =>
      button.addEventListener("click", () => {
        selectedType = selectedCityDay?.meetingTypes.find(
          (type) => type.slug === button.dataset.cityTemplate,
        );
        selectedLocationId = selectedType?.locations[0]?.id ?? "";
        if (selectedType) {
          selectedSlot = "";
          selectedDay = "";
          setStep(2, true);
          void renderCalendar();
        }
      }),
    );
  focusView();
}

function slotsByDay() {
  const values = new Map<string, Slot[]>();
  for (const slot of [...slots].sort((a, b) =>
    a.startsAt.localeCompare(b.startsAt),
  )) {
    if (
      rescheduleActionToken &&
      reschedulingReservation &&
      Date.parse(slot.startsAt) ===
        Date.parse(reschedulingReservation.startsAt) &&
      (selectedCityPlaceId || null) ===
        (reschedulingReservation.cityPlaceId || null)
    )
      continue;
    const key = dateKey(new Date(slot.startsAt));
    if (
      !(values.get(key) ?? []).some(
        (existing) => existing.startsAt === slot.startsAt,
      )
    )
      values.set(key, [...(values.get(key) ?? []), slot]);
  }
  return values;
}

function mountOriginalReservationMarkers(): void {
  if (!rescheduleActionToken || !reschedulingReservation) return;
  app.querySelector(".scheduling-card")?.classList.add("rescheduling");
  const originalDay = dateKey(new Date(reschedulingReservation.startsAt));
  const originalTime = formatTime(reschedulingReservation.startsAt);
  const day = app.querySelector<HTMLButtonElement>(
    `[data-day="${originalDay}"]`,
  );
  if (day) {
    day.classList.add("original-reservation-day");
    day.querySelector("small")?.remove();
    day.disabled = false;
    const text = ui(
      "reschedule.originalDay",
      "Původní termín · {time}",
      "Original booking · {time}",
    ).replace("{time}", originalTime);
    day.setAttribute("aria-label", `${originalDay}, ${text}`);
    const tag = document.createElement("span");
    tag.className = "original-reservation-tag";
    tag.innerHTML = `<span class="original-tag-label">${escapeHtml(ui("reschedule.originalLabel", "Původní termín", "Original booking"))}</span><time datetime="${escapeHtml(reschedulingReservation.startsAt)}">${escapeHtml(originalTime)}</time>`;
    day.append(tag);
  }
  const legend = app.querySelector(".calendar-legend");
  if (legend && !legend.querySelector(".legend-original")) {
    const entry = document.createElement("span");
    entry.className = "legend-original";
    entry.textContent = ui(
      "reschedule.originalLabel",
      "Původní termín",
      "Original booking",
    );
    legend.append(entry);
  }
  app.querySelector(".original-reservation-slot")?.remove();
  if (selectedDay === originalDay) {
    const row = document.createElement("div");
    row.className = "original-reservation-slot";
    row.setAttribute("role", "note");
    row.innerHTML = `<time datetime="${escapeHtml(reschedulingReservation.startsAt)}">${escapeHtml(originalTime)}–${escapeHtml(formatTime(reschedulingReservation.endsAt))}</time><span>${escapeHtml(ui("reschedule.currentBooking", "Vaše současná rezervace", "Your current booking"))}</span>`;
    app.querySelector(".day-slots > h3, .day-slots-heading")?.after(row);
  }
  if (rescheduleDayErrors.has(selectedDay)) {
    showRescheduleDayError();
  }
  const toolbar = app.querySelector(".schedule-toolbar > div");
  if (!app.querySelector("#return-original-time") && toolbar) {
    const back = document.createElement("button");
    back.id = "return-original-time";
    back.type = "button";
    back.className = "text-button";
    back.textContent = ui(
      "reschedule.returnOriginal",
      "Zpět na původní termín",
      "Back to the original booking",
    );
    back.onclick = () => {
      void loadRescheduleSlots(
        rescheduleActionToken!,
        reschedulingReservation!,
        attendeeTimeZone,
      )
        .then(focusStepHeading)
        .catch((error) =>
          renderError(error, () => void loadManagement(rescheduleActionToken!)),
        );
    };
    toolbar.append(back);
  }
  if (
    rescheduleContextNotice &&
    !app.querySelector(".reschedule-context-notice")
  ) {
    const notice = document.createElement("p");
    notice.className = "reschedule-context-notice";
    notice.textContent = rescheduleContextNotice;
    toolbar?.append(notice);
  }
}

function selectedRescheduleDayId(): string | undefined {
  return rescheduleAvailability?.cityDays.find(
    (day) => day.slug === selectedCityDay?.slug,
  )?.id;
}

function renderSelectedPlaceSummary(): void {
  renderSelectedPlaceSummaryView({
    document,
    dateMarkup: summaryDateMarkup(),
    kind: selectedType?.locations.find(
      (location) => location.id === selectedLocationId,
    )?.kind,
    options: currentPlaceOptions(),
    selectedId: selectedCityPlaceId,
    fallbackPlace: informationalFixedPlace(),
    phoneLabel: ui("meeting.phone", "Telefonát", "Phone call"),
    approvalLabel: ui(
      "places.approval",
      "Místo musí potvrdit hostitel.",
      "The host must approve the place.",
    ),
    escape: escapeHtml,
  });
}

function onlyVenue(options = currentPlaceOptions()): string | null {
  if (!options || options.allowOther) return null;
  const places = options.places.filter((place) => place.selectable);
  if (options.fixedPlaceId)
    return places.some((place) => place.id === options.fixedPlaceId)
      ? options.fixedPlaceId
      : null;
  return places.length === 1 ? places[0]!.id : null;
}

function showRescheduleDayError() {
  availabilityStatus(
    ui(
      "reschedule.dayUnavailable",
      "Dostupnost vybraného dne teď nelze ověřit. Ostatní připravené dny můžete dál procházet.",
      "The selected day's availability cannot be verified right now. You can continue browsing the other prepared days.",
    ),
  );
  const notice = app.querySelector<HTMLElement>(".availability-status");
  if (!notice) return;
  notice.classList.add("reschedule-day-error");
  notice.setAttribute("role", "alert");
  notice
    .querySelectorAll("button:not([data-reschedule-day-retry])")
    .forEach((button) => button.remove());
  if (notice.querySelector("[data-reschedule-day-retry]")) return;
  const retry = document.createElement("button");
  retry.dataset.rescheduleDayRetry = "true";
  retry.type = "button";
  retry.className = "text-button";
  retry.textContent = ui(
    "reschedule.retryDay",
    "Ověřit tento den znovu",
    "Check this day again",
  );
  retry.onclick = () => void renderCalendar(true);
  notice.append(retry);
}

function calendarMarkup(selectedDay = ""): string {
  if (rescheduleActionToken)
    return (
      dateCalendarMarkup(selectedDay) +
      rescheduleDayPickerMarkup() +
      (selectedCityDay ? calendarVenueMarkup(selectedDay) : "")
    );
  return calendarVenueMarkup(selectedDay);
}

let rescheduleTargetRevision = 0;
let reschedulePendingDate = "";

function resetRescheduleTargetChoice(): void {
  rescheduleTargetRevision++;
  reschedulePendingDate = "";
  // A target lookup can outlive an earlier day check. Fence that renderer
  // before waiting for the next target, and prevent selecting its old slots.
  calendarAbort.abort();
  calendarRevision++;
  app.querySelector(".scheduling-card")?.setAttribute("aria-busy", "true");
}

function rescheduleDayCandidateSlugs(day: string): string[] {
  const dates = rescheduleAvailability?.cityAvailability ?? [];
  return [
    ...new Set(
      dates
        .filter((candidate) =>
          candidate.slots.some(
            (slot) => dateKey(new Date(slot.startsAt)) === day,
          ),
        )
        .map((candidate) => candidate.slug),
    ),
  ];
}

function rescheduleDayPickerMarkup(): string {
  if (
    !reschedulingReservation?.cityDaySlug ||
    !rescheduleAvailability?.cityDays.length
  )
    return "";
  const candidates = reschedulePendingDate
    ? rescheduleDayCandidateSlugs(reschedulePendingDate)
    : [];
  const choices = candidates.length
    ? rescheduleAvailability.cityDays.filter((day) =>
        candidates.includes(day.slug),
      )
    : rescheduleAvailability.cityDays;
  const selected = selectedCityDay?.slug ?? "";
  return `<label class="reschedule-city-day">${escapeHtml(ui("reschedule.cityDay", "Den a město", "Day and city"))}<select id="reschedule-city-day">${!selected ? `<option value="" selected disabled>${escapeHtml(ui("reschedule.chooseDay", "Vyberte den a město", "Choose a day and city"))}</option>` : ""}${choices.map((day) => `<option value="${escapeHtml(day.slug)}" ${day.slug === selected ? "selected" : ""}>${escapeHtml(day.name)} · ${escapeHtml(displayDate(day.localDate))}</option>`).join("")}</select></label>`;
}

async function selectRescheduleCityDay(
  slug: string,
  civilDate?: string,
): Promise<void> {
  const token = rescheduleActionToken;
  const reservation = reschedulingReservation;
  const chosen = rescheduleAvailability?.cityDays.find(
    (day) => day.slug === slug,
  );
  if (!token || !reservation || !chosen) return;
  const desiredDate = civilDate ?? reschedulePendingDate;
  resetRescheduleTargetChoice();
  const revision = rescheduleTargetRevision;
  try {
    if (selectedCityDay?.slug !== slug) {
      const result = await api.GET(
        "/booking/public/{profileSlug}/city/{cityDaySlug}",
        {
          params: {
            path: { profileSlug: reservation.profileSlug, cityDaySlug: slug },
            query: { lang: locale },
          },
          signal: AbortSignal.timeout(10000),
        },
      );
      if (
        revision !== rescheduleTargetRevision ||
        token !== rescheduleActionToken
      )
        return;
      if (result.error) throw result.error;
      const type = result.data.meetingTypes.find(
        (item) => item.slug === reservation.meetingTypeSlug,
      );
      if (
        !type ||
        !type.locations.some((location) => location.id === selectedLocationId)
      ) {
        throw new Error(
          ui(
            "reschedule.formatChanged",
            "Tento den již nenabízí původní formu schůzky. Vaše rezervace zůstává beze změny.",
            "This day no longer offers the original meeting format. Your booking is unchanged.",
          ),
        );
      }
      selectedCityDay = result.data;
      selectedType = type;
      const options = result.data.placeOptions;
      selectedCityPlaceId = options?.places.some(
        (place) => place.id === selectedCityPlaceId && place.selectable,
      )
        ? selectedCityPlaceId
        : (onlyVenue(options) ?? "");
    }
    reschedulePendingDate = "";
    selectedSlot = "";
    selectedDay =
      desiredDate ||
      (selectedCityDay?.startsAt
        ? dateKey(new Date(selectedCityDay.startsAt))
        : chosen.localDate);
    visibleMonth = selectedDay.slice(0, 7);
    await renderCalendar(true);
  } catch (error) {
    if (
      revision !== rescheduleTargetRevision ||
      token !== rescheduleActionToken
    )
      return;
    renderError(error, () => void loadManagement(token));
  }
}

function calendarVenueMarkup(selectedDay = ""): string {
  const options = currentPlaceOptions();
  if (
    selectedCityDay &&
    selectedType?.locations.find(
      (location) => location.id === selectedLocationId,
    )?.kind === "phone"
  )
    return `<section class="fixed-place"><p>${escapeHtml(ui("calendar.phoneDay", "Vyberte čas telefonátu. Osobní místo není potřeba.", "Choose a time for your phone call. No physical venue is needed."))}</p>${rescheduleActionToken ? "" : availabilityStatusMarkup()}</section>`;
  if (!selectedCityDay && !placeView) return dateCalendarMarkup(selectedDay);
  if (!options)
    return `<section class="fixed-place"><h3>${escapeHtml(ui("places.title", "Místo setkání", "Meeting place"))}</h3><p>${escapeHtml(monthOrigins.find((source) => source.slots.some((slot) => dateKey(new Date(slot.startsAt)) === selectedDay))?.day.location ?? selectedType?.locations.find((x) => x.id === selectedLocationId)?.address ?? selectedType?.locations.find((x) => x.id === selectedLocationId)?.label ?? "")}</p>${rescheduleActionToken ? "" : availabilityStatusMarkup()}</section>`;
  if (
    selectedCityDay &&
    informationalFixedPlace() &&
    !options.places.some((place) => place.selectable)
  )
    return `<section class="city-place-picker fixed-venue"><div class="city-place-visual">${cityDayMapMarkup()}</div>${rescheduleActionToken ? "" : availabilityStatusMarkup()}</section>`;
  const fixedVenue = Boolean(onlyVenue(options));
  const mapped = options.places.filter(
    (place) =>
      place.selectable && place.latitude !== null && place.longitude !== null,
  );
  // The reschedule calendar wrapper owns this selector, including hidden originals.
  const dayChoices = "";
  const map =
    (mapped.length || visitorCoordinates || options.cityName) && mapyTileApiKey
      ? `<div id="city-place-map" class="city-place-map" aria-label="${escapeHtml(ui("places.map", "Mapa míst setkání", "Meeting-place map"))}"></div>${fixedVenue || !mapped.length ? "" : `<button type="button" class="text-button" id="show-all-places">${escapeHtml(ui("places.showAll", "Zobrazit všechna místa", "Show all places"))}</button><p class="map-attribution-note">${escapeHtml(ui("places.mapHint", "Místo můžete vybrat také špendlíkem v mapě.", "You can also choose a place from the map."))}</p>`}`
      : mapped.length
        ? `<p class="notice">${escapeHtml(ui("places.mapUnavailable", "Mapa nyní není dostupná. Místo můžete stále vybrat ze seznamu.", "The map is currently unavailable. You can still choose a place from the list."))}</p>`
        : "";
  const cards = options.places.filter(
    (place) =>
      place.selectable &&
      (place.id.startsWith("fixed:") ||
        (place.latitude !== null && place.longitude !== null)),
  );
  if (onlyVenue(options)) {
    const fixed = cards.find((p) => p.id === onlyVenue(options));
    if (fixed) selectedCityPlaceId = fixed.id;
    return `<section class="city-place-picker fixed-venue">${dayChoices}<div class="city-place-visual">${map}</div>${rescheduleActionToken ? "" : availabilityStatusMarkup()}</section>`;
  }
  const placeEvidence =
    rawPlaceOptions()?.places.filter((p) => p.selectable) ?? [];
  const refreshingPlaces = placeEvidence.some(
    (p) => p.availability && p.availability.state !== "current",
  );
  const placesNotice =
    (placeChangeNotice
      ? `<p class="notice" role="status">${escapeHtml(placeChangeNotice)}</p>`
      : "") +
    (refreshingPlaces
      ? `<p class="notice" role="status">${escapeHtml(ui("places.refreshing", "Dostupnost míst ověřujeme. Nabídka se obnoví automaticky.", "We are checking venue availability. The choices will update automatically."))}</p>`
      : "");
  const noStandardPlaces =
    !refreshingPlaces && !cards.length && !informationalFixedPlace()
      ? `<p class="notice" role="status">${escapeHtml(ui("places.noStandard", "Standardní místa pro tento den nyní nejsou dostupná.", "Standard venues are not currently available for this day."))}</p>`
      : "";
  const picker = `<section class="city-place-picker" aria-label="${escapeHtml(ui("places.title", "Místo setkání", "Meeting place"))}">${selectedCityDay || placeView ? "" : `<h3>${escapeHtml(ui("places.title", "Místo setkání", "Meeting place"))}</h3><p>${escapeHtml(ui("places.hint", "Vyberte místo. Nabídka časů zohlední jeho otevírací dobu.", "Choose a place. Times will respect its opening hours."))}</p>`}${dayChoices}<div class="city-place-layout"><div class="city-place-visual">${map}</div><div class="city-place-list">${placesNotice}${noStandardPlaces}${cards.map((place) => renderPlaceCard({ place, selected: selectedCityPlaceId === place.id, recommended: Boolean(place.selectable && (selectedSlot ? slots.find((slot) => slot.startsAt === selectedSlot)?.preferredPlaceIds?.[0] === place.id : place.recommendedDates?.includes(selectedDay || selectedCityDay?.localDate || ""))), labels: { recommended: ui("places.recommended", "Doporučené místo", "Recommended place"), fixed: ui("places.fixed", "Tento den se potkáme jen na vybraném místě.", "This day is limited to the selected venue."), unknown: ui("places.unknown", "Otevírací doba není ověřena.", "Opening hours are not configured."), inactive: ui("places.inactive", "Místo není aktivní.", "This place is inactive.") }, escape: escapeHtml })).join("")}${options.allowOther && !rescheduleActionToken ? `<div class="other-place-card"><button type="button" class="city-place-option" data-city-place="other" aria-pressed="${selectedCityPlaceId === "other"}">${options.otherImage ? `<img class="place-choice-thumbnail" src="${escapeHtml(options.otherImage.vectorUrl ?? options.otherImage.previewUrl ?? options.otherImage.cardUrl)}" alt=""/>` : ""}<strong>${escapeHtml(ui("places.other", "Jiné místo — navrhněte vlastní", "Other — suggest a place"))}</strong><small>${escapeHtml(ui("places.approval", "Místo musí potvrdit hostitel.", "The host must approve the place."))}</small></button>${ownAddressMarkup()}</div>` : ""}</div></div>${rescheduleActionToken ? "" : availabilityStatusMarkup()}</section>`;
  return picker;
}

// City context only: this lookup never confirms a visitor address.
const cityCenters = new Map<string, Promise<[number, number] | null>>();
function cityMapCenter(name: string): Promise<[number, number] | null> {
  const standard = standardCityMapCenters[name.trim().toLocaleLowerCase()];
  if (standard) return Promise.resolve(standard);
  if (!cityCenters.has(name)) {
    const query = new URLSearchParams({
      query: name,
      type: "regional.municipality",
      limit: "5",
      lang: "cs",
      apikey: mapyTileApiKey,
    });
    cityCenters.set(
      name,
      fetch(`https://api.mapy.com/v1/geocode?${query}`, {
        referrerPolicy: "origin",
      })
        .then(async (response) => {
          if (!response.ok) throw new Error("City map unavailable");
          const data = (await response.json()) as {
            items?: Array<{
              name: string;
              position: { lat: number; lon: number };
            }>;
          };
          const matches =
            data.items?.filter(
              (item) =>
                item.name.toLocaleLowerCase() === name.toLocaleLowerCase(),
            ) ?? [];
          if (matches.length !== 1) return null;
          const { lat, lon } = matches[0]!.position;
          return Number.isFinite(lat) &&
            Math.abs(lat) <= 90 &&
            Number.isFinite(lon) &&
            Math.abs(lon) <= 180
            ? ([lat, lon] as [number, number])
            : null;
        })
        .catch(() => {
          cityCenters.delete(name);
          return null;
        }),
    );
  }
  return cityCenters.get(name)!;
}

const cityPlaceMarkers = new Map<string, L.Marker>();
let renderedPlaceOffer = "";
function placeOfferKey(): string {
  // A cold list and a verified empty list render differently even when both hide every venue.
  return JSON.stringify([rawPlaceOptions(), placeChangeNotice]);
}
function updatePlaceSelection(): void {
  updatePlaceSelectionView({
    document,
    options: currentPlaceOptions(),
    selectedId: selectedCityPlaceId,
    markers: cityPlaceMarkers,
    ownAddressMarkup,
    wireVisitorAddress,
    onSummaryUpdate: renderSelectedPlaceSummary,
  });
}

async function chooseCityPlace(id: string): Promise<void> {
  if (id === selectedCityPlaceId) return;
  clearShortcutSelection();
  selectedCityPlaceId = id;
  visitorAddressRevision++;
  placeChangeNotice = "";
  selectedSlot = "";
  savePlaceUrl();
  updatePlaceSelection();
  saveJourney();
  await renderCalendar(true);
}
let cityMapLayers: ReturnType<typeof drawCityDayMap> | undefined;
let cityMapContentKey = "";
const cityMapViewRequests = new WeakMap<L.Map, (intent: () => void) => void>();
function cityMapViewRequest(map: L.Map) {
  const existing = cityMapViewRequests.get(map);
  if (existing) return existing;
  let zooming = false;
  let pending: (() => void) | undefined;
  let frame = 0;
  const flush = () => {
    frame = 0;
    if (zooming || !pending) return;
    const intent = pending;
    pending = undefined;
    intent();
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(flush);
  };
  map.on("zoomstart", () => {
    zooming = true;
  });
  map.on("zoomend", () => {
    zooming = false;
    schedule();
  });
  map.once("unload", () => {
    cancelAnimationFrame(frame);
    pending = undefined;
    cityMapViewRequests.delete(map);
  });
  const request = (intent: () => void) => {
    pending = intent;
    // A zoom control may already have queued its zoomstart for this frame.
    schedule();
  };
  cityMapViewRequests.set(map, request);
  return request;
}
type CityMapOverview = PublicMapContext &
  Partial<
    Pick<
      PublicCityDayDetail,
      "slug" | "shortcuts" | "availabilityState" | "availabilityFreshness"
    >
  >;
const mapOverviews = new Map<
  string,
  { at: number; value: CityMapOverview; placesKey: string }
>();
function mapPublicPlacesKey() {
  return JSON.stringify(
    (
      rawPlaceOptions()?.places ??
      informationalPlaceOptions()?.places ??
      []
    ).map(({ id, name, fullName, address, latitude, longitude }) => ({
      id,
      name,
      fullName,
      address,
      latitude,
      longitude,
    })),
  );
}
const mapOverviewAttempts = new Map<string, number>();
const mapOverviewRequests = new Map<string, Promise<void>>();
function rememberMapOverview(
  key: string,
  value: CityMapOverview,
  placesKey = mapPublicPlacesKey(),
) {
  mapOverviews.delete(key);
  mapOverviews.set(key, {
    at: Date.now(),
    value,
    placesKey,
  });
  while (mapOverviews.size > AVAILABILITY_CACHE_LIMIT) {
    const oldest = mapOverviews.keys().next().value!;
    mapOverviews.delete(oldest);
    mapOverviewAttempts.delete(oldest);
  }
}
function mapPresenceOrigin() {
  if (selectedCityDay) return selectedCityDay;
  return (
    monthOrigins.find(
      ({ day }) =>
        day.localDate === selectedDay &&
        (!cityFilter ||
          day.city === cityFilter ||
          day.publicCity?.name === cityFilter),
    )?.day ??
    filteredCityDays.find(
      (day) =>
        day.localDate === selectedDay &&
        (!cityFilter ||
          day.city === cityFilter ||
          day.publicCity?.name === cityFilter),
    )
  );
}
function currentMapPresence(): CityMapOverview | undefined {
  const origin = mapPresenceOrigin();
  if (!origin) return undefined;
  // Management capabilities can expose a newer, reservation-specific projection.
  // A public overview from an earlier step must never override that authority.
  if (rescheduleActionToken)
    return origin.timeline !== undefined ? origin : undefined;
  const cached = mapOverviews.get(
    `${loadRevision}|${profileSlug}|${origin.slug}|${locale}`,
  );
  const evidence =
    cached?.value ?? (origin.timeline !== undefined ? origin : undefined);
  if (
    !evidence ||
    (evidence.timelineState !== "stale" &&
      (!cached || cached.placesKey === mapPublicPlacesKey()))
  )
    return evidence;
  const publicPlaces =
    rawPlaceOptions()?.places ?? informationalPlaceOptions()?.places ?? [];
  return {
    ...evidence,
    timeline: evidence.timeline?.filter(
      (item) =>
        item.kind === "travel" ||
        item.precision === "area" ||
        Boolean(
          item.placeId &&
          publicPlaces.some(
            (place) =>
              place.id === item.placeId &&
              (item.label === place.name || item.label === place.fullName) &&
              item.address === place.address &&
              item.point?.latitude === place.latitude &&
              item.point?.longitude === place.longitude,
          ),
        ),
    ),
  };
}
async function ensureMapPresence(retryPrepared = false) {
  const origin = mapPresenceOrigin();
  if (
    currentStep !== 2 ||
    document.hidden ||
    !navigator.onLine ||
    rescheduleActionToken ||
    !origin ||
    selectedType?.locations.find((item) => item.id === selectedLocationId)
      ?.kind !== "in_person"
  )
    return;
  const key = `${loadRevision}|${profileSlug}|${origin.slug}|${locale}`;
  const cached = mapOverviews.get(key);
  if (
    mapOverviewRequests.has(key) ||
    (!retryPrepared &&
      cached &&
      Date.now() - cached.at < AVAILABILITY_HANDOFF_TTL_MS)
  )
    return;
  const revision = loadRevision,
    day = selectedDay,
    requestPlacesKey = mapPublicPlacesKey();
  const current = () =>
    currentStep === 2 &&
    revision === loadRevision &&
    day === selectedDay &&
    mapPresenceOrigin()?.slug === origin.slug;
  const retry = () => {
    const attempts = mapOverviewAttempts.get(key) ?? 0;
    mapOverviewAttempts.set(key, attempts + 1);
    setTimeout(
      () => {
        if (!current()) return;
        // Read the prepared projection again without forcing a calendar calculation.
        if (!document.hidden && navigator.onLine) void ensureMapPresence(true);
      },
      Math.max([2, 5, 10, 30][attempts] ?? 60, availabilityRetrySeconds()) *
        1000,
    );
  };
  const request = (async () => {
    try {
      const result = await api.GET(
        "/booking/public/{profileSlug}/city/{cityDaySlug}/overview",
        {
          params: {
            path: { profileSlug, cityDaySlug: origin.slug },
            query: { lang: locale },
          },
          signal: AbortSignal.timeout(20000),
        },
      );
      if (!current()) return;
      if (result.error) throw result.error;
      rememberMapOverview(key, result.data, requestPlacesKey);
      mountCityPlaceMap();
      if (
        result.data.timelineState === "stale" ||
        (result.data.availabilityFreshness &&
          result.data.availabilityFreshness.state !== "current")
      )
        retry();
      else mapOverviewAttempts.delete(key);
    } catch {
      if (!current()) return;
      const previous = mapOverviews.get(key);
      rememberMapOverview(
        key,
        {
          ...(previous?.value ?? { timeZone: origin.timeZone, timeline: [] }),
          timelineState: "stale",
        },
        previous?.placesKey ?? requestPlacesKey,
      );
      mountCityPlaceMap();
      retry();
    } finally {
      mapOverviewRequests.delete(key);
    }
  })();
  mapOverviewRequests.set(key, request);
  await request;
}
for (const event of ["visibilitychange", "online", "focus"])
  (event === "visibilitychange" ? document : window).addEventListener(
    event,
    () => {
      if (!document.hidden && navigator.onLine && currentStep === 2)
        void ensureMapPresence();
    },
  );
function updateCityMapSchedule(
  visual: HTMLElement | null,
  context: CityMapOverview | undefined,
  places: NonNullable<ReturnType<typeof currentPlaceOptions>>["places"],
  informational: boolean,
) {
  const panel = document.querySelector<HTMLElement>(
    ".scheduling-card .day-slots",
  );
  if (panel)
    mountDaySchedule(panel, context, {
      date: selectedDay,
      timeZone: attendeeTimeZone,
      locale,
      selectedPlaceId: selectedCityPlaceId,
    });
  if (visual) {
    const scheduleKey = JSON.stringify([
      context?.timeline,
      context?.timelineState,
      context?.timeZone,
      context?.stayMergeGapMinutes,
      context?.shortcuts,
      context?.availabilityState,
      context?.availabilityFreshness?.state,
      places.map((place) => [place.id, place.selectable]),
      locale,
      selectedType?.slug,
      selectedDay,
      informational,
      fixedCityDay(),
    ]);
    if (visual.dataset.scheduleKey === scheduleKey) return;
    visual.dataset.scheduleKey = scheduleKey;
    let schedule = visual.querySelector<HTMLElement>(".city-map-schedule");
    if (!schedule) {
      schedule = document.createElement("section");
      schedule.className = "city-map-schedule";
      visual.append(schedule);
    }
    if (context || mapPresenceOrigin())
      renderCityMapSchedule(
        schedule,
        context ?? { timeZone: attendeeTimeZone, timelineState: "stale" },
        locale,
        { showStays: false },
      );
    else schedule.replaceChildren();
  }
}
function mountCityPlaceMap(readOnly = false): void {
  const element = document.querySelector<HTMLElement>("#city-place-map");
  const retained =
    cityPlaceMap && cityPlaceMap.getContainer() === element
      ? cityPlaceMap
      : null;
  const focusedPlaceId = [...cityPlaceMarkers].find(([, marker]) =>
    marker.getElement()?.contains(document.activeElement),
  )?.[0];
  const available = currentPlaceOptions();
  const informational =
    readOnly ||
    Boolean(
      selectedCityDay &&
      informationalFixedPlace() &&
      !available?.places.some((place) => place.selectable),
    );
  const options = informational ? informationalPlaceOptions() : available;
  if (!options) {
    if (cityPlaceMap && !document.contains(cityPlaceMap.getContainer())) {
      cityMapLayers?.dispose();
      cityMapLayers = undefined;
      cityPlaceMarkers.clear();
      cityPlaceMap.remove();
      cityPlaceMap = null;
      cityMapContentKey = "";
    }
    return;
  }
  const places = (
    informational
      ? options.places
      : (rawPlaceOptions()?.places ?? options.places)
  )
    .filter(
      (place) =>
        (!informational || place.id === options.fixedPlaceId) &&
        place.latitude !== null &&
        place.longitude !== null,
    )
    .map((place) => ({
      ...place,
      selectable: !informational && place.selectable && offeredPlace(place),
    }));
  const context = currentMapPresence();
  const visual =
    element?.closest<HTMLElement>(".city-place-visual") ??
    document.querySelector<HTMLElement>(".city-place-visual");
  updateCityMapSchedule(visual, context, places, informational);
  if (!readOnly) void ensureMapPresence();
  if (!element || (!places.length && !visitorCoordinates && !options.cityName))
    return;
  const contentKey = JSON.stringify([
    places.map(
      ({
        id,
        name,
        fullName,
        address,
        latitude,
        longitude,
        selectable,
        pinImage,
      }) => ({
        id,
        name,
        fullName,
        address,
        latitude,
        longitude,
        selectable,
        pinImage,
      }),
    ),
    context?.timeline,
    context?.timelineState,
    context?.stayMergeGapMinutes,
    context?.timeZone,
    locale,
    selectedCityPlaceId,
    visitorCoordinates,
    visitorAddress,
    options.profilePin,
    options.otherPin,
  ]);
  if (retained && cityMapContentKey === contentKey) {
    retained.invalidateSize({ pan: false });
    return;
  }
  cityMapLayers?.dispose();
  cityMapLayers = undefined;
  for (const marker of cityPlaceMarkers.values())
    if (cityPlaceMap?.hasLayer(marker)) cityPlaceMap.removeLayer(marker);
  cityPlaceMarkers.clear();
  if (!retained) {
    cityPlaceMap?.remove();
    cityPlaceMap = null;
  }
  cityMapContentKey = contentKey;
  const map = retained ?? L.map(element, { scrollWheelZoom: false });
  cityPlaceMap = map;
  const requestView = cityMapViewRequest(map);
  if (!retained) {
    const tiles = L.tileLayer(
      `https://api.mapy.com/v1/maptiles/basic/256/{z}/{x}/{y}?apikey=${encodeURIComponent(mapyTileApiKey)}`,
      {
        minZoom: 0,
        maxZoom: 19,
        // The page-wide no-referrer policy protects management capability URLs. Mapy.com browser
        // keys still need the site origin, so tiles opt in without leaking a booking path or token.
        referrerPolicy: "origin",
        attribution:
          '<a href="https://api.mapy.com/copyright" target="_blank" rel="noopener">&copy; Seznam.cz a.s. a další</a>',
      },
    );
    let failedTiles = 0;
    tiles.on("tileerror", () => {
      failedTiles += 1;
      if (failedTiles < 3 || cityPlaceMap !== map) return;
      map.remove();
      cityPlaceMap = null;
      const fallback = document.createElement("p");
      fallback.className = "notice";
      fallback.setAttribute("role", "status");
      fallback.textContent = ui(
        "places.mapUnavailable",
        "Mapa nyní není dostupná. Adresa místa zůstává zobrazena.",
        "The map is currently unavailable. The place address is still displayed.",
      );
      element.replaceWith(fallback);
    });
    tiles.addTo(map);
    const LogoControl = L.Control.extend({
      options: { position: "bottomleft" as const },
      onAdd() {
        const container = L.DomUtil.create("div", "mapy-logo-control");
        const link = L.DomUtil.create("a", "", container);
        link.setAttribute("href", "https://mapy.com/");
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener");
        link.setAttribute("aria-label", "Mapy.com");
        link.innerHTML =
          '<img src="https://api.mapy.com/img/api/logo.svg" alt="Mapy.com" />';
        L.DomEvent.disableClickPropagation(link);
        return container;
      },
    });
    new LogoControl().addTo(map);
  }
  cityMapLayers = drawCityDayMap(
    map,
    places,
    context ?? { timeZone: attendeeTimeZone },
    {
      locale,
      selectedPlaceId: selectedCityPlaceId,
      profilePin: options.profilePin,
      choosePlace: informational
        ? undefined
        : (id) => {
            void chooseCityPlace(id);
          },
    },
  );
  const bounds = cityMapLayers.bounds;
  for (const [id, marker] of cityMapLayers.placeMarkers)
    cityPlaceMarkers.set(id, marker);
  if (!informational && visitorCoordinates && selectedCityPlaceId === "other") {
    const point = L.latLng(
      visitorCoordinates.latitude,
      visitorCoordinates.longitude,
    );
    bounds.extend(point);
    const url =
      options.otherPin?.vectorUrl ??
      options.otherPin?.previewUrl ??
      options.otherPin?.cardUrl;
    const icon = L.divIcon({
      className: "city-place-marker-shell",
      html: url
        ? `<span class="city-place-marker-image selected"><img src="${escapeHtml(url)}" alt=""/></span>`
        : otherPinFallback(),
      iconSize: [42, 50],
      iconAnchor: [21, 48],
    });
    cityPlaceMarkers.set(
      "other",
      L.marker(point, { icon, title: visitorAddress, zIndexOffset: 1000 })
        .addTo(map)
        .bindTooltip(visitorAddress),
    );
  }
  const showAll = () => {
    if (!bounds.isValid()) {
      void cityMapCenter(options.cityName).then((point) => {
        if (
          cityPlaceMap !== map ||
          cityMapContentKey !== contentKey ||
          bounds.isValid()
        )
          return;
        if (point) map.setView(point, 12, { animate: false });
        else {
          map.remove();
          cityPlaceMap = null;
          element.className = "notice";
          element.setAttribute("role", "status");
          element.textContent =
            locale === "cs"
              ? "Mapu města se nepodařilo načíst. Adresu můžete přesto zadat."
              : "The city map could not be loaded. You can still enter an address.";
        }
      });
    } else if (bounds.getNorthEast().equals(bounds.getSouthWest()))
      map.setView(bounds.getCenter(), 15, { animate: false });
    else
      map.fitBounds(bounds.pad(0.2), {
        animate: false,
        maxZoom: 15,
        paddingTopLeft: [64, 100],
        paddingBottomRight: [56, 48],
      });
  };
  if (!retained || !Number.isFinite(map.getZoom())) showAll();
  else map.invalidateSize({ pan: false });
  if (focusedPlaceId) {
    const pin = cityPlaceMarkers.get(focusedPlaceId)?.getElement();
    if (pin && pin.tabIndex >= 0) pin.focus({ preventScroll: true });
  }
  const image = cityPlaceMarkers
    .get("other")
    ?.getElement()
    ?.querySelector("img");
  if (image) {
    const fallback = () => {
      image.parentElement?.replaceWith(
        document.createRange().createContextualFragment(otherPinFallback()),
      );
    };
    image.addEventListener("error", fallback, { once: true });
    if (image.complete && image.naturalWidth === 0) fallback();
  }
  document.querySelector("#show-all-places")?.addEventListener("click", () =>
    requestView(() => {
      if (cityPlaceMap === map && cityMapContentKey === contentKey) showAll();
    }),
  );
}

function scheduledCityDays(day: string): PublicCityDayDetail[] {
  const city = cityFilter || homeCityName;
  return filteredCityDays.filter(
    (item) =>
      item.publicCity?.name === city &&
      (item.startsAt ? dateKey(new Date(item.startsAt)) : item.localDate) <=
        day &&
      (item.endsAt
        ? dateKey(new Date(Date.parse(item.endsAt) - 1))
        : item.localDate) >= day,
  );
}

function dateCalendarMarkup(selectedDay = ""): string {
  const bounds = monthBounds(visibleMonth);
  const values = slotsByDay();
  const firstWeekday = new Date(
    Date.UTC(bounds.year, bounds.month - 1, 1),
  ).getUTCDay();
  const leading = (firstWeekday + 6) % 7;
  const days = new Date(Date.UTC(bounds.year, bounds.month, 0)).getUTCDate();
  const title = new Intl.DateTimeFormat(locale === "cs" ? "cs-CZ" : "en-GB", {
    month: "long",
    year: "numeric",
  }).format(new Date(Date.UTC(bounds.year, bounds.month - 1, 1)));
  const today = dateKey(new Date());
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + (selectedType?.bookingWindowDays ?? 60));
  const maximumMonth = monthKey(maxDate);
  const weekdays =
    locale === "cs"
      ? ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"]
      : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return `<div class="calendar-shell"><div class="calendar-heading"><button class="icon-nav" id="previous-month" aria-label="${escapeHtml(t("calendar.previousMonth", "Předchozí měsíc"))}" ${visibleMonth <= monthKey(new Date()) ? "disabled" : ""}>←</button><h2>${escapeHtml(title)}</h2><button class="icon-nav" id="next-month" aria-label="${escapeHtml(t("calendar.nextMonth", "Následující měsíc"))}" ${visibleMonth >= maximumMonth ? "disabled" : ""}>→</button></div><p class="calendar-legend"><span class="legend-none">${escapeHtml(ui("public.unavailable", "Bez termínů", "Unavailable"))}</span><span class="legend-few">${escapeHtml(ui("public.12Times", "1–2 termíny", "1–2 times"))}</span><span class="legend-many">${escapeHtml(ui("public.moreTimes", "Více termínů", "More times"))}</span></p>${standardPlaceOptions && !cityFilter ? `<p class="muted">${escapeHtml(locale === "cs" ? "Počty zahrnují všechna dostupná místa." : "Counts include all available places.")}</p>` : ""}<div class="weekdays" aria-hidden="true">${weekdays.map((item) => `<span>${item}</span>`).join("")}</div><div class="month-grid">${Array.from({ length: leading }, () => '<span class="calendar-empty"></span>').join("")}${Array.from(
    { length: days },
    (_, index) => {
      const day = index + 1;
      const key = `${visibleMonth}-${String(day).padStart(2, "0")}`;
      const count = new Set(
        (values.get(key) ?? []).map((slot) => slot.startsAt),
      ).size;
      const hasCityDay = scheduledCityDays(key).length > 0;
      const level = count === 0 ? "none" : count <= 2 ? "few" : "many";
      const maximumDate = dateKey(maxDate);
      const isPast = key < today;
      const isToday = key === today;
      const isOutside = key > maximumDate;
      const disabled =
        isPast ||
        isOutside ||
        (count === 0 && !hasCityDay && !uncheckedDates.has(key));
      const stateLabel = isPast
        ? t("calendar.past", "minulý den")
        : isOutside
          ? t("calendar.outside", "mimo rezervační období")
          : uncheckedDates.has(key)
            ? count > 0 || storedDates.has(key)
              ? `${calendarCountLabel(count)} · ${locale === "cs" ? "orientačně" : "provisional"}`
              : ui("calendar.unchecked", "Ověřujeme", "Checking")
            : count === 0
              ? hasCityDay
                ? ui(
                    "calendar.cityDayZero",
                    "0 volných termínů",
                    "0 available times",
                  )
                : t("calendar.noSlots", "bez termínu")
              : calendarCountLabel(count);
      const dayLabel = new Intl.DateTimeFormat(
        locale === "cs" ? "cs-CZ" : "en-GB",
        {
          dateStyle: "full",
          timeZone: "UTC",
        },
      ).format(new Date(`${key}T12:00:00Z`));
      const todayLabel = t("calendar.today", "Dnes");
      return `<button class="calendar-day ${level} ${hasCityDay ? "scheduled-cityday" : ""} ${isPast ? "past" : ""} ${isToday ? "today" : ""} ${isOutside ? "outside-window" : ""} ${key === selectedDay ? "selected" : ""}" data-day="${key}" aria-label="${escapeHtml(`${dayLabel}, ${isToday ? `${todayLabel}, ` : ""}${stateLabel}`)}" ${disabled ? "disabled" : ""}><span>${day}${isToday ? `<b class="today-label">${escapeHtml(todayLabel)}</b>` : ""}</span><small>${escapeHtml(stateLabel)}</small></button>`;
    },
  ).join("")}</div>${availabilityStatusMarkup()}</div>`;
}

function routingProofHeaders(daySlug?: string) {
  if (selectedCityPlaceId !== "other" || !validAddressConfirmation()) return {};
  const token = daySlug
    ? visitorAddressTokens.get(daySlug)?.token
    : visitorAddressTokens.get(`type:${selectedType?.slug}`)?.token;
  return token ? { "X-Booking-Address-Proof": token } : {};
}
async function fetchMonth(
  ignorePlace = false,
  reconciled = false,
  force = false,
): Promise<void> {
  if (!selectedType) return;
  if (force) clearAvailabilityCache();
  const queryKey = () =>
    [
      selectedType?.slug,
      selectedLocationId,
      selectedCityDay?.slug,
      selectedCityPlaceId,
      visitorAddressRevision,
      visitorAddressVerification?.source === "osm"
        ? visitorAddressVerification.token
        : "",
      rescheduleActionToken,
      attendeeTimeZone,
      visibleMonth,
      cityFilter,
    ].join("|");
  const requestedKey = queryKey();
  const activeSignal = calendarAbort.signal;
  if (rescheduleActionToken) {
    const targetDay = rescheduleAvailability?.cityDays.find(
      (day) => day.slug === selectedCityDay?.slug,
    );
    const { data, error } = await api.GET(
      "/booking/public/reservations/{actionToken}/availability",
      {
        params: {
          path: { actionToken: rescheduleActionToken },
          query: {
            from: monthBounds(visibleMonth).from,
            to: monthBounds(visibleMonth).to,
            timeZone: attendeeTimeZone,
            locationId: selectedLocationId || undefined,
            ...(targetDay ? { cityDayId: targetDay.id } : {}),
            ...(selectedCityPlaceId
              ? { cityPlaceId: selectedCityPlaceId }
              : {}),
          },
        },
        signal: scheduleSignal(),
      },
    );
    if (error) throw error;
    if (!activeSignal.aborted && requestedKey === queryKey()) {
      const cityEntries = data.cityAvailability ?? [];
      slots = reschedulingReservation?.cityDaySlug
        ? cityEntries.flatMap((entry) => entry.slots)
        : data.slots;
      const targetOptions = selectedCityDay
        ? (cityEntries.find((entry) => entry.slug === selectedCityDay?.slug)
            ?.placeOptions ??
          selectedCityDay.placeOptions ??
          null)
        : data.placeOptions;
      rescheduleAvailability = { ...data, placeOptions: targetOptions };
      collectFreshness(data);
      for (const day of rescheduleDayErrors)
        if (day.startsWith(visibleMonth)) uncheckedDates.add(day);
      monthOrigins = [];
      dayOffer = null;
      standardPlaceOptions = targetOptions;
      standardPlaceTypeId = selectedType.slug;
      if (selectedCityDay)
        selectedCityDay = { ...selectedCityDay, placeOptions: targetOptions };
    }
    return;
  }
  if (selectedCityDay) {
    const { data, error } = await api.GET(
      "/booking/public/{profileSlug}/city/{cityDaySlug}/availability",
      {
        headers: {
          ...routingProofHeaders(selectedCityDay.slug),
        },
        params: {
          path: { profileSlug, cityDaySlug: selectedCityDay.slug },
          query: {
            meetingType: selectedType.slug,
            locationId: selectedLocationId || undefined,
            timeZone: attendeeTimeZone,
            ...(selectedCityDay.placeOptions && selectedCityPlaceId
              ? { place: selectedCityPlaceId }
              : {}),
          },
        },
        signal: scheduleSignal(),
      },
    );
    if (error) throw error;
    if (!activeSignal.aborted && requestedKey === queryKey()) {
      collectFreshness(data);
      slots = data.slots;
      selectedCityDay = { ...selectedCityDay, placeOptions: data.placeOptions };
      if (
        selectedCityPlaceId &&
        selectedCityPlaceId !== "other" &&
        !data.placeOptions?.places.some(
          (place) =>
            place.id === selectedCityPlaceId &&
            (place.availability?.state !== "current" || offeredPlace(place)),
        )
      ) {
        selectedCityPlaceId = "";
        selectedSlot = "";
        placeChangeNotice = ui(
          "places.changed",
          "Původní místo už pro tuto délku nemá volný termín. Vyberte jiné místo.",
          "The previous venue has no available time for this duration. Choose another venue.",
        );
        savePlaceUrl();
        saveJourney();
        slots = [];
        if (!reconciled) await fetchMonth(false, true);
        return;
      }
      const permitted =
        data.placeOptions?.places.filter(
          (place) => place.selectable && offeredPlace(place),
        ) ?? [];
      if (
        !reconciled &&
        !placeChangeNotice &&
        !selectedCityPlaceId &&
        permitted.length === 1
      ) {
        selectedCityPlaceId = permitted[0]!.id;
        // A fixed-place CityDay must obtain its slots through the same
        // place-aware availability check as an explicit list/map choice.
        // The first response discovers the only permitted place; the second
        // response authoritatively applies its hours and eligibility.
        if (!reconciled) await fetchMonth(false, true);
      }
    }
    return;
  }
  const bounds = monthBounds(visibleMonth);
  const { data, error } = await api.GET(
    "/booking/public/{profileSlug}/{meetingTypeSlug}/availability",
    {
      headers: ignorePlace ? {} : routingProofHeaders(),
      params: {
        path: { profileSlug, meetingTypeSlug: selectedType.slug },
        query: {
          from: bounds.from,
          to: bounds.to,
          includeCityDays: "true",
          city: cityFilter || undefined,
          // The bulk place-day request already raises this day's priority.
          priorityDate: placeView ? undefined : selectedDay || undefined,
          timeZone: attendeeTimeZone,
          locationId: selectedLocationId || undefined,
          cityPlaceId:
            !ignorePlace &&
            selectedCityPlaceId &&
            !selectedCityPlaceId.startsWith("fixed:")
              ? selectedCityPlaceId
              : undefined,
        },
      },
      signal: scheduleSignal(),
    },
  );
  if (error) throw error;
  const homeDays = (data.cityDays ?? []).flatMap((entry) => {
    const day = filteredCityDays.find((day) => day.slug === entry.slug);
    return day
      ? [{ ...entry, day: { ...day, placeOptions: entry.placeOptions } }]
      : [];
  });
  if (!activeSignal.aborted && requestedKey === queryKey()) {
    monthOrigins = homeDays;
    citySlotOrigins.clear();
    collectFreshness(data);
    for (const entry of homeDays) collectFreshness(entry);
    slots = [...data.slots];
    for (const { day, slots: available } of homeDays)
      for (const slot of available) {
        const key = `${day.slug}|${slot.startsAt}`;
        citySlotOrigins.set(key, day);
        slots.push({ ...slot, cityKey: key });
      }
    standardPlaceOptions = data.placeOptions ?? null;
    standardPlaceTypeId = selectedType.slug;
    if (
      !ignorePlace &&
      standardPlaceOptions &&
      selectedCityPlaceId &&
      !(selectedCityPlaceId === "other" && standardPlaceOptions.allowOther) &&
      !standardPlaceOptions.places.some(
        (p) => p.selectable && p.id === selectedCityPlaceId,
      )
    ) {
      selectedCityPlaceId = "";
      if (!reconciled) await fetchMonth(false, true);
    }
  }
}

function locationChoice(): string {
  if (
    rescheduleActionToken ||
    (selectedCityDay &&
      selectedType?.locations.every((item) => item.kind === "in_person"))
  )
    return "";
  if (
    selectedType &&
    !selectedCityDay &&
    !rescheduleActionToken &&
    selectedType.locations.every((item) => item.kind === "in_person") &&
    (filteredCityDays.length || homeCityName)
  ) {
    const cities = [
      ...new Set(
        filteredCityDays
          .map((day) => day.publicCity?.name)
          .filter((city): city is string => Boolean(city)),
      ),
    ].sort((a, b) => a.localeCompare(b, locale));
    const choices = [
      ...(homeCityName
        ? [{ value: "", name: homeCityName, image: homeCityImage }]
        : []),
      ...cities
        .filter((city) => city !== homeCityName)
        .map((city) => ({
          value: city,
          name: city,
          image:
            filteredCityDays.find((day) => day.publicCity?.name === city)
              ?.publicCity?.image ?? null,
        })),
    ];
    return `<fieldset class="city-filter"><legend>${escapeHtml(locale === "cs" ? "Město setkání" : "Meeting city")}</legend><div class="city-radio-row">${choices.map((city) => `<label><input type="radio" name="meeting-city" value="${escapeHtml(city.value)}" ${cityFilter === city.value ? "checked" : ""} />${city.image ? `<img class="city-choice-thumbnail" src="${escapeHtml(city.image.vectorUrl ?? city.image.previewUrl ?? city.image.cardUrl)}" alt=""/>` : ""}<span>${escapeHtml(city.name)}</span></label>`).join("")}</div></fieldset>${cityFilter ? "" : ordinaryLocationChoice()}`;
  }
  return ordinaryLocationChoice();
}
function ordinaryLocationChoice(): string {
  if (!selectedType || selectedType.locations.length <= 1) return "";
  return `<fieldset class="location-choice"><legend>${escapeHtml(t("meeting.how", "Jak se potkáme?"))}</legend>${selectedType.locations.map((location) => `<label><input type="radio" name="public-location" value="${location.id}" ${location.id === selectedLocationId ? "checked" : ""}/><span><strong>${escapeHtml(location.label)}</strong>${location.address ? `<small>${escapeHtml(location.address)}</small>` : ""}</span></label>`).join("")}</fieldset>`;
}

async function openScheduleDay(day: string): Promise<void> {
  if (!day) return;
  if (rescheduleActionToken) {
    selectedDay = day;
    selectedSlot = "";
    if (reschedulingReservation?.cityDaySlug) {
      const candidates = rescheduleDayCandidateSlugs(day);
      if (candidates.length === 1) {
        await selectRescheduleCityDay(candidates[0]!, day);
        return;
      }
      if (
        candidates.length > 1 &&
        !candidates.includes(selectedCityDay?.slug ?? "")
      ) {
        rescheduleTargetRevision++;
        reschedulePendingDate = day;
        selectedCityDay = undefined;
        selectedDay = "";
        await renderCalendar(true);
        document
          .querySelector<HTMLSelectElement>("#reschedule-city-day")
          ?.focus();
        return;
      }
    }
    await renderCalendar(true);
    return;
  }
  if (
    selectedType?.locations.find((x) => x.id === selectedLocationId)?.kind !==
    "in_person"
  ) {
    renderMonthContents(day);
    // Choosing a day changes the refresh context even when no new request is
    // needed. Re-arm polling so the previous context's timer cannot abandon it.
    scheduleAvailabilityRefresh();
    return;
  }
  selectedDay = day;
  selectedSlot = "";
  selectedCityPlaceId = "";
  placeView = true;
  dayOffer = null;
  const url = new URL(location.href);
  url.searchParams.set("date", day);
  history.pushState({ bookingStep: 2 }, "", url);
  saveJourney();
  await renderCalendar();
}

async function discoverDayPlaces(day: string, prefetch = false): Promise<void> {
  if (!selectedType) return;
  const signal = prefetch ? new AbortController().signal : scheduleSignal(),
    typeSlug = selectedType.slug,
    zone = attendeeTimeZone;
  const locationId = selectedLocationId;
  const sources: Array<{
    origin?: PublicCityDayDetail;
    options: PublicCityDayDetail["placeOptions"];
    slots: Slot[];
  }> = monthOrigins
    .filter(
      (source) =>
        (source.day.startsAt
          ? dateKey(new Date(source.day.startsAt))
          : source.day.localDate) <= day &&
        (source.day.endsAt
          ? dateKey(new Date(Date.parse(source.day.endsAt) - 1))
          : source.day.localDate) >= day,
    )
    .map((source) => ({
      origin: source.day,
      options: source.day.placeOptions,
      slots: source.slots,
    }));
  if (!cityFilter)
    sources.unshift({
      options: basePlaceOptions(),
      slots: slots.filter((slot) => !slot.cityKey),
    });
  const directoryOffer = sources.find((source) => source.options)?.options;
  const offered =
    directoryOffer ??
    (sources.filter((source) =>
      source.slots.some((slot) => dateKey(new Date(slot.startsAt)) === day),
    ).length > 1
      ? {
          cityId: sources[0]?.origin?.publicCity?.id ?? "fixed",
          cityName: cityFilter || homeCityName || "",
          timeZone: zone,
          fixedPlaceId: null,
          allowOther: false,
          cityImage: null,
          profilePin: null,
          otherImage: null,
          otherPin: null,
          places: [],
        }
      : null);
  if (!offered) {
    if (prefetch) return;
    dayOffer = null;
    slots = slots.filter((slot) => dateKey(new Date(slot.startsAt)) === day);
    return;
  }
  // Permission to propose an address is independent of whether that address has slots.
  const allowOther = sources.some((source) => source.options?.allowOther);
  // One bulk day response for standard venues, using the same evidence as monthly counts.
  const standard = sources.some((source) => !source.origin && source.options)
    ? await api.GET(
        "/booking/public/{profileSlug}/{meetingTypeSlug}/availability",
        {
          params: {
            path: { profileSlug, meetingTypeSlug: typeSlug },
            query: {
              from: day,
              to: day,
              timeZone: zone,
              locationId: locationId || undefined,
            },
          },
          headers: { "X-Booking-Route-Preview": "1" },
          signal,
        },
      )
    : null;
  if (standard?.error) throw standard.error;
  if (standard?.data && !prefetch) collectFreshness(standard.data);
  const checks = sources.flatMap((source) =>
    source.options
      ? [
          ...source.options.places
            .filter(
              (p) =>
                p.selectable && p.latitude !== null && p.longitude !== null,
            )
            .map((p) => ({ source, id: p.id })),
          ...(source.options.allowOther &&
          selectedCityPlaceId === "other" &&
          validAddressConfirmation()
            ? [{ source, id: "other" }]
            : []),
        ]
      : [],
  );
  const answers = await limited(checks, async ({ source, id }) => {
    let available: Slot[];
    let verified = true;
    if (source.origin) {
      const result = await api.GET(
        "/booking/public/{profileSlug}/city/{cityDaySlug}/availability",
        {
          params: {
            path: { profileSlug, cityDaySlug: source.origin.slug },
            query: { meetingType: typeSlug, timeZone: zone, place: id },
          },
          headers: {
            ...(prefetch ? { "X-Booking-Prefetch": "1" } : {}),
            ...(prefetch || id !== selectedCityPlaceId
              ? { "X-Booking-Route-Preview": "1" }
              : {}),
            ...(id === "other" ? routingProofHeaders(source.origin.slug) : {}),
          },
          signal,
        },
      );
      if (result.error) throw result.error;
      if (!prefetch) collectFreshness(result.data);
      available = result.data.slots;
      verified =
        result.data.availabilityFreshness?.complete !== false &&
        result.data.availabilityFreshness?.state !== "refreshing" &&
        result.data.availabilityFreshness?.state !== "error";
    } else if (id !== "other" && standard?.data?.placeAvailability) {
      const evidence = standard.data.placeAvailability.find(
        (p) => p.placeId === id,
      );
      available = evidence?.slots ?? [];
      verified =
        evidence?.availabilityFreshness.complete === true &&
        evidence.availabilityFreshness.state === "current";
    } else {
      const result = await api.GET(
        "/booking/public/{profileSlug}/{meetingTypeSlug}/availability",
        {
          params: {
            path: { profileSlug, meetingTypeSlug: typeSlug },
            query: {
              from: day,
              to: day,
              timeZone: zone,
              locationId: locationId || undefined,
              cityPlaceId: id,
            },
          },
          headers: {
            ...(prefetch ? { "X-Booking-Prefetch": "1" } : {}),
            ...(prefetch || id !== selectedCityPlaceId
              ? { "X-Booking-Route-Preview": "1" }
              : {}),
            ...(id === "other" ? routingProofHeaders() : {}),
          },
          signal,
        },
      );
      if (result.error) throw result.error;
      if (!prefetch) collectFreshness(result.data);
      available = result.data.slots;
    }
    return {
      source,
      id,
      verified,
      available: available.filter(
        (slot) => dateKey(new Date(slot.startsAt)) === day,
      ),
    };
  });
  if (prefetch) return;
  signal.throwIfAborted();
  const byPlace = new Map<string, Slot[]>();
  const places = new Map<
    string,
    NonNullable<typeof offered>["places"][number]
  >();
  for (const { source, id, available, verified } of answers) {
    if (!available.length && verified) continue;
    const result = available.map((slot) => {
      if (!source.origin) return slot;
      const key = `${source.origin.slug}|${slot.startsAt}`;
      citySlotOrigins.set(key, source.origin);
      return { ...slot, cityKey: key };
    });
    byPlace.set(id, [...(byPlace.get(id) ?? []), ...result]);
    const place = source.options?.places.find((p) => p.id === id);
    if (place) places.set(id, place);
  }
  const fixedByAddress = new Map<string, string>();
  for (const source of sources.filter((source) => !source.options)) {
    const available = source.slots.filter(
      (slot) => dateKey(new Date(slot.startsAt)) === day,
    );
    if (!available.length) continue;
    let id = `fixed:${source.origin?.slug ?? "standard"}`;
    const location = selectedType.locations.find(
      (item) => item.id === locationId,
    );
    const address =
      source.origin?.location || location?.address || location?.label || "";
    id = fixedByAddress.get(address) ?? id;
    fixedByAddress.set(address, id);
    places.set(id, {
      id,
      name: address,
      fullName: address,
      address,
      mapyUrl: "",
      image: null,
      pinImage: null,
      latitude: null,
      longitude: null,
      selectable: true,
      reason: "available",
      recommendedDates: [],
    });
    byPlace.set(id, [
      ...(byPlace.get(id) ?? []),
      ...available.map((slot) => {
        if (!source.origin) return slot;
        const key = `${source.origin.slug}|${slot.startsAt}`;
        citySlotOrigins.set(key, source.origin);
        return { ...slot, cityKey: key };
      }),
    ]);
  }
  if (allowOther && !byPlace.has("other")) byPlace.set("other", []);
  if (byPlace.size === 1) selectedCityPlaceId = [...byPlace.keys()][0]!;
  dayPlaceSlots = byPlace;
  dayOffer = {
    ...offered,
    places: [...places.values()],
    allowOther,
    fixedPlaceId:
      sources.length === 1 ? (sources[0]?.options?.fixedPlaceId ?? null) : null,
  };
  if (dayOffer.fixedPlaceId && byPlace.has(dayOffer.fixedPlaceId))
    selectedCityPlaceId = dayOffer.fixedPlaceId;
  if (selectedCityPlaceId && !byPlace.has(selectedCityPlaceId))
    selectedCityPlaceId = "";
  slots = selectedCityPlaceId
    ? (byPlace.get(selectedCityPlaceId) ?? [])
    : [...byPlace.values()].flat();
}

function wireMeetingCities(): void {
  document
    .querySelectorAll<HTMLInputElement>('input[name="meeting-city"]')
    .forEach((input) => {
      input.disabled = false;
      input.onchange = () => {
        cityFilter = input.value;
        placeView = false;
        dayOffer = null;
        selectedCityPlaceId = "";
        standardPlaceOptions = null;
        app
          .querySelector(".scheduling-card")
          ?.classList.remove("cityday-places");
        clearAddressConfirmation();
        visitorCoordinates = null;
        visitorAddressRevision++;
        selectedSlot = "";
        selectedDay = "";
        const url = new URL(location.href);
        url.searchParams.delete("date");
        if (cityFilter) url.searchParams.set("city", cityFilter);
        else url.searchParams.delete("city");
        history.replaceState(history.state, "", url);
        const first = filteredCityDays.find(
          (day) => day.publicCity?.name === cityFilter,
        );
        if (first)
          visibleMonth = (
            first.startsAt ? dateKey(new Date(first.startsAt)) : first.localDate
          ).slice(0, 7);
        saveJourney();
        void renderCalendar();
      };
    });
}

async function offerShorterCityDayMeetings(revision: number): Promise<void> {
  const current = selectedType,
    panel = app.querySelector(".day-slots");
  const days = selectedCityDay
    ? [selectedCityDay]
    : scheduledCityDays(selectedDay);
  if (
    !current ||
    !panel ||
    !selectedDay ||
    !days.length ||
    (selectedCityPlaceId === "other" && !validAddressConfirmation()) ||
    slotsByDay().get(selectedDay)?.length ||
    rescheduleActionToken ||
    availabilityState !== "current" ||
    panel.querySelector("[data-city-day-full]") ||
    bookingNotice?.unavailableReason === "minimum_notice"
  )
    return;
  const chosenDay = selectedDay,
    place = selectedCityPlaceId,
    activeSignal = calendarAbort.signal;
  const section = document.createElement("div");
  section.className = "shorter-meetings";
  section.setAttribute("aria-live", "polite");
  panel.querySelector(".shorter-meetings")?.remove();
  const noTimes = panel.querySelector('[role="status"]');
  if (noTimes && !noTimes.classList.contains("availability-status"))
    noTimes.textContent = ui(
      "cityDay.noTimesForMinutes",
      "Pro {minutes}minutovou schůzku nejsou volné termíny.",
      "There are no available times for a {minutes}-minute meeting.",
    ).replace("{minutes}", String(current.durationMinutes));
  section.textContent = ui(
    "cityDay.checkingAlternatives",
    "Ověřujeme jiné délky schůzky…",
    "Checking other meeting lengths…",
  );
  panel.append(section);
  const isCurrent = (value: {
    availabilityFreshness?: AvailabilityFreshness;
  }) =>
    !value.availabilityFreshness ||
    (value.availabilityFreshness.state === "current" &&
      value.availabilityFreshness.complete !== false);
  let failed = false;
  const results = await Promise.all(
    days.map(async (catalogDay) => {
      try {
        // The month catalog deliberately carries only the selected type. Load the
        // permitted day types only when a visitor needs alternatives for that day.
        const detail =
          selectedCityDay?.slug === catalogDay.slug
            ? selectedCityDay
            : (
                await api.GET(
                  "/booking/public/{profileSlug}/city/{cityDaySlug}",
                  {
                    params: {
                      path: { profileSlug, cityDaySlug: catalogDay.slug },
                      query: { lang: locale },
                    },
                    signal: activeSignal,
                  },
                )
              ).data;
        if (!detail) throw new Error("Day unavailable");
        const candidates = detail.meetingTypes.filter(
          (type) =>
            type.durationMinutes !== current.durationMinutes &&
            type.locations.some((location) => location.kind === "in_person"),
        );
        return (
          await Promise.all(
            candidates.map(async (type) => {
              const overview = detail.typeAvailability?.find(
                (item) => item.meetingTypeSlug === type.slug,
              );
              if (isCurrent(detail) && overview?.state === "unavailable")
                return [];
              return (
                await Promise.all(
                  type.locations
                    .filter((location) => location.kind === "in_person")
                    .map(async (location) => {
                      const load = async (venue?: string) => {
                        const result = await api.GET(
                          "/booking/public/{profileSlug}/city/{cityDaySlug}/availability",
                          {
                            headers: { "X-Booking-Route-Preview": "1" },
                            params: {
                              path: { profileSlug, cityDaySlug: detail.slug },
                              query: {
                                meetingType: type.slug,
                                locationId: location.id,
                                timeZone: attendeeTimeZone,
                                ...(venue ? { place: venue } : {}),
                              },
                            },
                            signal: activeSignal,
                          },
                        );
                        if (
                          result.error ||
                          !result.data ||
                          !isCurrent(result.data)
                        )
                          throw new Error("Unverified alternative");
                        return result.data;
                      };
                      const base = await load();
                      const venues =
                        base.placeOptions?.places.filter(
                          (item) => item.selectable,
                        ) ?? [];
                      const checks = await limited(venues, async (venue) => ({
                        id: venue.id,
                        value: await load(venue.id),
                      }));
                      const availablePlaces = checks
                        .filter((entry) =>
                          entry.value.slots.some(
                            (slot) =>
                              dateKey(new Date(slot.startsAt)) === chosenDay,
                          ),
                        )
                        .map((entry) => entry.id);
                      // Unknown custom addresses never constitute a verified alternative.
                      const fixed =
                        !base.placeOptions &&
                        base.slots.some(
                          (slot) =>
                            dateKey(new Date(slot.startsAt)) === chosenDay,
                        );
                      if (!availablePlaces.length && !fixed) return [];
                      return [
                        {
                          day: { ...detail, placeOptions: base.placeOptions },
                          type,
                          locationId: location.id,
                          retainedPlace: availablePlaces.includes(place)
                            ? place
                            : "",
                          differentPlace: Boolean(
                            place && !availablePlaces.includes(place) && !fixed,
                          ),
                        },
                      ];
                    }),
                )
              ).flat();
            }),
          )
        ).flat();
      } catch {
        failed = true;
        return [];
      }
    }),
  );
  if (
    activeSignal.aborted ||
    revision !== calendarRevision ||
    !section.isConnected
  )
    return;
  section.replaceChildren();
  for (const result of results.flat()) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "button";
    button.dataset.shorterMeeting = result.type.slug;
    button.dataset.cityDay = result.day.slug;
    button.textContent = `${result.type.name} · ${result.type.durationMinutes} min${days.length > 1 ? " · " + result.day.title : ""}${result.differentPlace ? " · " + ui("cityDay.otherVenue", "na jiném místě", "at another venue") : ""}`;
    button.onclick = () => {
      if (revision !== calendarRevision) return;
      const keepMap = selectedCityDay?.slug === result.day.slug;
      saveJourney();
      selectedCityDay = result.day;
      placeView = false;
      dayOffer = null;
      dayPlaceSlots.clear();
      selectedType = result.type;
      selectedLocationId = result.locationId;
      selectedCityPlaceId = result.retainedPlace;
      selectedDay = chosenDay;
      selectedSlot = "";
      if (place === "other") clearAddressConfirmation();
      const url = new URL(
        `${bookingBasePath}/city/${encodeURIComponent(result.day.slug)}`,
        location.origin,
      );
      url.searchParams.set("lang", locale);
      url.searchParams.set("meetingType", result.type.slug);
      url.searchParams.set("date", chosenDay);
      url.searchParams.set("timeZone", attendeeTimeZone);
      if (result.retainedPlace)
        url.searchParams.set("place", result.retainedPlace);
      history.pushState(
        { bookingStep: 2, typeSlug: result.type.slug },
        "",
        url,
      );
      saveJourney();
      app.querySelector(".meeting-summary")?.remove();
      app.insertAdjacentHTML("afterbegin", meetingSummary());
      void renderCalendar(keepMap);
    };
    section.append(button);
  }
  if (failed) {
    const notice = document.createElement("p");
    notice.textContent = ui(
      "cityDay.alternativesUnverified",
      "Jiné délky nyní nelze ověřit. Dostupnost obnovíme automaticky.",
      "Other meeting lengths cannot currently be verified. Availability will refresh automatically.",
    );
    section.append(notice);
    alternativesUnverified = true;
    availabilityRefreshAfter = Math.max(availabilityRefreshAfter, 5);
    scheduleAvailabilityRefresh();
  }
  if (!section.childElementCount) section.remove();
}

function mountShortcutConfirmation(): void {
  document.querySelector("#confirm-shortcut")?.remove();
  if (
    !selectedCityDay ||
    !selectedSlot ||
    !selectedCityPlaceId ||
    selectedCityPlaceId === "other" ||
    new URL(location.href).searchParams.get("shortcut") !== "1"
  )
    return;
  const button = document.createElement("button");
  button.type = "button";
  button.id = "confirm-shortcut";
  button.className = "button";
  button.textContent = ui(
    "calendar.confirmPlaceTime",
    "Potvrdit místo a čas",
    "Confirm place and time",
  );
  button.onclick = () => {
    if (
      !selectedSlot ||
      !slots.some(isChosenSlot) ||
      availabilityState !== "current"
    )
      return;
    button.disabled = true;
    setStep(3, true);
    renderReservationForm();
  };
  document.querySelector(".day-slots")?.append(button);
}

function calendarRenderSignature(): string {
  return JSON.stringify([
    slots,
    [...uncheckedDates],
    [...storedDates],
    availabilityState,
    selectedDay,
    selectedCityPlaceId,
    selectedSlot,
    dayOffer,
    alternativesUnverified,
  ]);
}

async function renderCalendar(
  preservePlaces = false,
  background = false,
): Promise<void> {
  if (cityDayEnded()) return renderEndedCityDay();
  const placeOptions = currentPlaceOptions();
  if (!background) availabilityFailure = undefined;
  if (placeOptions && !(rescheduleActionToken && !selectedCityDay)) {
    const permitted = placeOptions.places.filter((place) => place.selectable);
    if (
      !(selectedCityPlaceId === "other" && placeOptions.allowOther) &&
      !permitted.some((place) => place.id === selectedCityPlaceId)
    )
      selectedCityPlaceId = "";
    if (!selectedCityPlaceId && onlyVenue(placeOptions))
      selectedCityPlaceId = onlyVenue(placeOptions)!;
    if (!permitted.length && placeOptions.allowOther)
      selectedCityPlaceId = "other";
  }
  if (!selectedType) return renderMeetingTypes();
  const revision = ++calendarRevision;
  const priorRender = calendarRenderSignature();
  if (!background) {
    refreshSessionStarted = performance.now();
    refreshTimedOut = false;
    visibleCheckStarted = refreshSessionStarted;
    visibleCheckContext = availabilityContext();
    clearTimeout(verificationDeadlineTimer);
    verificationDeadlineTimer = window.setTimeout(() => {
      if (
        currentStep !== 2 ||
        (selectionVerified() &&
          app.querySelector(".scheduling-card")?.getAttribute("aria-busy") !==
            "true")
      )
        return;
      refreshTimedOut = true;
      clearTimeout(availabilityRetryTimer);
      calendarAbort.abort();
      updateAvailabilityIndicator();
    }, 120000);
  }
  alternativesUnverified = false;
  clearTimeout(noticeTimer);
  bookingNotice = undefined;
  clearTimeout(availabilityRetryTimer);
  uncheckedDates = new Set();
  storedDates = new Set();
  availabilityState = "current";
  availabilityRefreshAfter = 0;
  stopSlotMonitoring();
  stopFormProtection();
  setStep(2);
  calendarAbort.abort();
  calendarAbort = new AbortController();
  const existing = app.querySelector(".scheduling-card");
  if (!existing) {
    app.innerHTML = `${meetingSummary()}<section class="card scheduling-card ${selectedCityDay && currentPlaceOptions() ? "cityday-places" : ""}">${stepHeader(2)}<div class="schedule-toolbar"><div></div></div><div class="calendar-choices">${locationChoice()}</div><div class="calendar-and-slots">${dateCalendarMarkup(selectedDay)}<section class="day-slots"><h3>${escapeHtml(t("calendar.chooseDate", "Vyberte den"))}</h3></section></div></section>`;
    focusView();
  }
  wireMonthNavigation();
  const loadingCard = app.querySelector<HTMLElement>(".scheduling-card")!;
  loadingCard.querySelector(".availability-failure")?.remove();
  loadingCard.querySelector(".loading")?.remove();
  loadingCard.querySelector(".calendar-progress")?.remove();
  if (!rescheduleActionToken || !background)
    loadingCard.setAttribute("aria-busy", "true");
  if (!background)
    loadingCard
      .querySelectorAll<HTMLButtonElement>(".slot, .calendar-day")
      .forEach((button) => (button.disabled = true));
  if (!background) availabilityStatus();
  if (!background) {
    loadingCard
      .querySelectorAll(
        '.day-slots > [role="status"]:not(.availability-status), .day-slots > .availability-unverified, .day-slots > .shorter-meetings',
      )
      .forEach((e) => e.remove());
    // Old slots belong to the previous selection until a matching response arrives.
    loadingCard
      .querySelectorAll(".slot-list")
      .forEach((list) => list.replaceChildren());
  }
  const loadingTimer = window.setTimeout(() => {
    if (background || revision !== calendarRevision) return;
    if (visibleCheckStarted && performance.now() - visibleCheckStarted < 30000)
      availabilityStatus(
        ui(
          "calendar.checking",
          `Ověřujeme termíny pro ${selectedType?.durationMinutes}minutovou schůzku…`,
          `Checking times for a ${selectedType?.durationMinutes}-minute meeting…`,
        ),
        true,
      );
    clearTimeout(visibleCheckTimer);
    visibleCheckTimer = window.setTimeout(() => {
      if (revision !== calendarRevision || currentStep !== 2) return;
      availabilityStatus(
        ui(
          "calendar.longWait",
          "Ov\u011b\u0159en\u00ed dostupnosti trv\u00e1 d\u00e9le.",
          "Availability verification is taking longer.",
        ),
      );
    }, 29850);
  }, 150);
  wireMeetingCities();
  try {
    const previousPlaceId = selectedCityPlaceId;
    await fetchMonth(placeView && !selectedCityDay);
    if (cityDayEnded()) return renderEndedCityDay();
    if (revision !== calendarRevision || !app.querySelector(".scheduling-card"))
      return;
    if (
      selectedCityDay &&
      !selectedSlot &&
      slots.length &&
      !slots.some((slot) => dateKey(new Date(slot.startsAt)) === selectedDay)
    ) {
      // A CityDay can start on the previous display date after a zone change.
      // Keep actual available times visible instead of showing that empty date.
      selectedDay = dateKey(new Date(slots[0]!.startsAt));
    }
    const day =
      selectedDay ||
      (selectedCityDay
        ? selectedCityDay.startsAt
          ? dateKey(new Date(selectedCityDay.startsAt))
          : selectedCityDay.localDate
        : "");
    if (placeView && !selectedCityDay && !rescheduleActionToken) {
      await discoverDayPlaces(day);
      if (selectedCityPlaceId && previousPlaceId !== selectedCityPlaceId)
        await discoverDayPlaces(day);
    }
    if (revision !== calendarRevision) return;
    if (rescheduleActionToken && day && !background) {
      clearTimeout(loadingTimer);
      renderMonthContents(day, preservePlaces);
      loadingCard.removeAttribute("aria-busy");
      const panel = loadingCard.querySelector<HTMLElement>(".day-slots");
      panel?.setAttribute("aria-busy", "true");
      panel?.querySelectorAll<HTMLButtonElement>(".slot").forEach((button) => {
        button.disabled = true;
      });
      availabilityStatus(
        ui(
          "reschedule.checkDay",
          "Ověřujeme vybraný den…",
          "Checking the selected day…",
        ),
        true,
      );
      app
        .querySelector(".availability-status > span")
        ?.classList.add("reschedule-day-check");
      const result = await api
        .GET("/booking/public/reservations/{actionToken}/availability", {
          params: {
            path: { actionToken: rescheduleActionToken },
            query: {
              date: day,
              timeZone: attendeeTimeZone,
              locationId: selectedLocationId || undefined,
              cityDayId: selectedRescheduleDayId(),
              cityPlaceId: selectedCityPlaceId || undefined,
            },
          },
          cache: "no-store",
          signal: scheduleSignal(),
        })
        .catch(() => null);
      if (
        revision !== calendarRevision ||
        currentStep !== 2 ||
        !app.querySelector(".scheduling-card")
      )
        return;
      if (!result || result.error) {
        uncheckedDates.add(day);
        rescheduleDayErrors.add(day);
        availabilityRefreshAfter = Math.max(availabilityRefreshAfter, 5);
      } else {
        const fresh = result.data.availabilityFreshness;
        if (!fresh || (fresh.state === "current" && fresh.complete !== false)) {
          uncheckedDates.delete(day);
          rescheduleDayErrors.delete(day);
          storedDates.add(day);
          if (!uncheckedDates.size) availabilityState = "current";
        } else {
          uncheckedDates.add(day);
          rescheduleDayErrors.add(day);
        }
        slots = [
          ...slots.filter((slot) => dateKey(new Date(slot.startsAt)) !== day),
          ...result.data.slots,
        ];
        rescheduleAvailability = { ...rescheduleAvailability!, ...result.data };
        standardPlaceOptions = result.data.placeOptions;
        standardPlaceTypeId = selectedType.slug;
        if (selectedCityDay)
          selectedCityDay = {
            ...selectedCityDay,
            placeOptions: result.data.placeOptions,
          };
      }
    }
    if (
      selectedSlot &&
      (previousPlaceId !== selectedCityPlaceId ||
        (availabilityState === "current" && !slots.some(isChosenSlot)))
    ) {
      selectedSlot = "";
      const notice = document.createElement("p");
      notice.setAttribute("role", "status");
      notice.textContent = ui(
        "calendar.slotUnavailable",
        "Původně vybraný čas už není dostupný. Vyberte prosím jiný.",
        "Your previously selected time is unavailable. Please choose another.",
      );
      app.querySelector(".scheduling-card")?.prepend(notice);
    }
    const summaryCity = app.querySelector(
      ".meeting-summary dl > div:last-child dd",
    );
    if (
      summaryCity &&
      !currentPlaceOptions() &&
      selectedType.locations.find((x) => x.id === selectedLocationId)?.kind ===
        "in_person"
    )
      summaryCity.textContent =
        cityFilter ||
        homeCityName ||
        selectedType.locations.find((x) => x.id === selectedLocationId)
          ?.address ||
        "";
    clearTimeout(loadingTimer);
    // Metadata can choose the sole permitted venue without a new user selection.
    if (visibleCheckStarted) visibleCheckContext = availabilityContext();
    availabilityFailure = undefined;
    if (!background || priorRender !== calendarRenderSignature())
      renderMonthContents(day, preservePlaces);
    mountShortcutConfirmation();
    if (!rescheduleActionToken) void offerShorterCityDayMeetings(revision);
    // The server prepares permitted variants; visitors only request their
    // visible period, without competing with the worker for unrelated types.
  } catch (error) {
    clearTimeout(loadingTimer);
    if (revision !== calendarRevision) return;
    availabilityFailure = error;
    availabilityState = "error";
    availabilityRefreshAfter = availabilityRetrySeconds();
    if (!selectedCityDay && !placeView) {
      const bounds = monthBounds(visibleMonth);
      for (
        let d = new Date(bounds.from + "T00:00Z");
        d.toISOString().slice(0, 10) <= bounds.to;
        d.setUTCDate(d.getUTCDate() + 1)
      )
        uncheckedDates.add(d.toISOString().slice(0, 10));
    }
    renderMonthContents(selectedDay, true);
    loadingCard
      .querySelectorAll<HTMLButtonElement>(".slot")
      .forEach((b) => (b.disabled = true));
  } finally {
    clearTimeout(loadingTimer);
    if (revision === calendarRevision) {
      loadingCard.removeAttribute("aria-busy");
      updateAvailabilityIndicator();
      scheduleAvailabilityRefresh();
    }
  }
}

function renderMonthContents(day = "", preservePlaces = false): void {
  const verifiedCityDayFull =
    selectedCityDay?.inPersonFull === true &&
    selectedCityDay.availabilityState === "checked" &&
    (!selectedCityDay.availabilityFreshness ||
      selectedCityDay.availabilityFreshness.state === "current") &&
    availabilityState === "current" &&
    selectedType?.locations.find(
      (location) => location.id === selectedLocationId,
    )?.kind === "in_person";
  const focusedControl =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  const focusAttribute = ["data-day", "data-start", "id", "title"].find(
    (attr) => focusedControl?.hasAttribute(attr),
  );
  const focusSelector =
    focusAttribute && focusedControl
      ? `[${focusAttribute}="${CSS.escape(focusedControl.getAttribute(focusAttribute)!)}"]`
      : null;
  const focusedType =
    document.activeElement instanceof HTMLInputElement &&
    document.activeElement.name === "cityday-meeting-type"
      ? document.activeElement.value
      : null;
  const retainedStatus = app.querySelector<HTMLElement>(".availability-status");
  retainedStatus?.remove();
  const scrollTop = window.scrollY;
  const retainedPicker =
    preservePlaces && renderedPlaceOffer === placeOfferKey()
      ? document.querySelector<HTMLElement>(".city-place-picker")
      : null;
  const retainedMap =
    preservePlaces && !retainedPicker && cityPlaceMap
      ? document.querySelector<HTMLElement>("#city-place-map")
      : null;
  retainedMap?.remove();
  retainedPicker?.remove();
  renderedPlaceOffer = placeOfferKey();
  const placeHeading = document.querySelector<HTMLElement>(
    ".city-place-heading",
  );
  if (placeHeading && !placeView && !selectedCityDay) {
    const header = document.querySelector(".booking-step-header");
    placeHeading
      .querySelectorAll(".eyebrow, h2")
      .forEach((element) => header?.append(element));
  }
  placeHeading?.remove();
  selectedDay = day;
  renderSelectedPlaceSummary();
  const card = document.querySelector<HTMLElement>(".scheduling-card")!;
  card.querySelector(".calendar-progress")?.remove();
  card.querySelector<HTMLElement>(".loading, .calendar-and-slots")?.remove();
  card.querySelector("#timezone-picker")?.remove();
  card.querySelector(".calendar-timezone-row")?.remove();
  card.insertAdjacentHTML(
    "beforeend",
    `<div class="calendar-and-slots">${calendarMarkup(selectedDay)}<section class="day-slots"><h3>${selectedDay ? escapeHtml(new Intl.DateTimeFormat(locale === "cs" ? "cs-CZ" : "en-GB", { dateStyle: "full", timeZone: "UTC" }).format(new Date(`${selectedDay}T12:00:00Z`))) : escapeHtml(t("calendar.chooseDate", "Vyberte den"))}</h3>${selectedDay ? `<div class="slot-list">${(slotsByDay().get(selectedDay) ?? []).map((slot) => `<button class="slot ${slot.recommended ? "recommended" : ""} ${isChosenSlot(slot) ? "selected" : ""}" aria-pressed="${isChosenSlot(slot)}" data-start="${slot.startsAt}" ${slot.cityKey ? `data-city-key="${escapeHtml(slot.cityKey)}"` : ""}><span>${escapeHtml(formatTime(slot.startsAt))}</span>${!selectedCityPlaceId && slot.preferredPlaceIds?.[0] ? `<small>\u2713 ${escapeHtml(currentPlaceOptions()?.places.find((p) => p.id === slot.preferredPlaceIds?.[0])?.fullName || ui("places.recommended", "Doporu\u010den\u00e9 m\u00edsto", "Recommended place"))}</small>` : ""}${slot.recommended && !isChosenSlot(slot) ? `<small><span aria-hidden="true">✓ </span>${escapeHtml(slot.recommendation === "same_place" ? ui("calendar.samePlace", "Navazuje na schůzku na stejném místě", "Follows a meeting at the same place") : t("calendar.recommended", "Doporučený čas"))}</small>` : ""}</button>`).join("")}</div>` : `<p>${escapeHtml(t("calendar.emptyHint", "Po výběru dne se zde zobrazí ověřené volné časy."))}</p>`}</section></div>`,
  );
  if (retainedStatus) {
    const placeholder = card.querySelector(".availability-status");
    if (placeholder) placeholder.replaceWith(retainedStatus);
    else availabilityStatusHost(card)?.append(retainedStatus);
  }
  const travelSlots = slotsByDay().get(selectedDay) ?? [];
  if (
    travelSlots.some(
      (s) => s.travelStatus === "estimate" || s.travelStatus === "pending",
    )
  )
    card
      .querySelector(".day-slots")
      ?.insertAdjacentHTML(
        "beforeend",
        `<p class="travel-estimate">${escapeHtml(ui("travel.estimate", "Termíny počítají s odhadovanou dobou přesunu.", "These times include an estimated travel duration."))}</p>`,
      );
  if (
    selectedCityDay?.capacity?.state === "blocked" ||
    selectedCityDay?.bookingRestrictions?.length
  ) {
    const notice = document.createElement("p");
    notice.setAttribute("role", "status");
    notice.textContent = ui(
      "cityDay.availabilityBlocked",
      "Rezervace nyn\u00ed omezuje nastaven\u00ed dostupnosti.",
      "Availability is currently restricted by booking or venue settings.",
    );
    card.querySelector(".day-slots")?.append(notice);
  }
  if (selectedCityDay || placeView) {
    card.classList.add("cityday-places");
    const grid = card.querySelector(".calendar-and-slots")!;
    const heading = placeHeading ?? document.createElement("div");
    heading.className = "city-place-heading";
    if (!placeHeading)
      card
        .querySelectorAll(
          ".booking-step-header > .eyebrow, .booking-step-header > h2",
        )
        .forEach((element) => heading.append(element));
    grid.prepend(heading);
    if (!heading.querySelector(".city-place-intro"))
      heading.insertAdjacentHTML(
        "beforeend",
        `<p class="city-place-intro">${escapeHtml(ui("places.hint", "Vyberte místo. Nabídka časů zohlední jeho otevírací dobu.", "Choose a place. Times will respect its opening hours."))}</p>`,
      );
  }
  if (
    selectionVerified() &&
    selectedDay &&
    !slotsByDay().get(selectedDay)?.length
  )
    card
      .querySelector(".day-slots")
      ?.insertAdjacentHTML(
        "beforeend",
        `<p role="status">${escapeHtml(ui("calendar.noTimes", "Pro tento výběr nyní nejsou dostupné termíny. Zvolte jiné datum nebo místo.", "There are no available times for this selection. Choose another date or place."))}</p>`,
      );
  if (
    (selectionVerified() || verifiedCityDayFull) &&
    selectedCityDay &&
    selectedDay &&
    !slotsByDay().get(selectedDay)?.length
  ) {
    const panel = card.querySelector(".day-slots")!;
    panel.querySelector('[role="status"]')?.remove();
    const full = verifiedCityDayFull;
    const message = full
      ? ui(
          "cityDay.full",
          "Tento den je již naplněný. Vyberte prosím jiný den.",
          "This day is fully booked. Please choose another day.",
        )
      : ui(
          "cityDay.noTimesForDuration",
          "Pro {minutes}minutovou schůzku nejsou volné termíny.",
          "There are no available times for a {minutes}-minute meeting.",
        ).replace("{minutes}", String(selectedType?.durationMinutes ?? ""));
    panel.insertAdjacentHTML(
      "beforeend",
      `<p class="${full ? "notice" : ""}" ${full ? "data-city-day-full" : ""} role="status">${escapeHtml(message)}</p><p>${otherBookingOptions()}</p>`,
    );
  }
  const noticePanel = card.querySelector<HTMLElement>(".day-slots");
  if (noticePanel && bookingNotice) {
    const rule = document.createElement("p");
    rule.className = "booking-notice";
    rule.textContent = ui(
      "calendar.minimumNotice",
      `Rezervace nejméně {hours} ${new Intl.PluralRules("cs").select(bookingNotice.minimumNoticeHours) === "one" ? "hodinu" : new Intl.PluralRules("cs").select(bookingNotice.minimumNoticeHours) === "few" ? "hodiny" : "hodin"} předem.`,
      "Book at least {hours} hours ahead.",
    ).replace("{hours}", String(bookingNotice.minimumNoticeHours));
    noticePanel.append(rule);
    if (bookingNotice.unavailableReason === "minimum_notice") {
      noticePanel
        .querySelectorAll('[role="status"],.shorter-meetings')
        .forEach((e) => e.remove());
      noticePanel.insertAdjacentHTML(
        "beforeend",
        `<p role="status">${escapeHtml(ui("calendar.noticeClosed", "Pro tento den už uplynula lhůta pro rezervaci.", "The booking deadline for this day has passed."))}</p><p>${otherBookingOptions()}</p>`,
      );
    }
  }
  if (
    noticePanel &&
    Boolean(selectedDay) &&
    !selectionVerified() &&
    !slotsByDay().get(selectedDay)?.length &&
    bookingNotice?.unavailableReason !== "minimum_notice"
  ) {
    noticePanel.insertAdjacentHTML(
      "beforeend",
      `<p class="availability-unverified" role="status">${escapeHtml(ui("calendar.unverified", "Dostupnost zatím není ověřená. Termíny se zobrazí automaticky po dokončení kontroly.", "Availability has not been verified yet. Times will appear automatically when the check finishes."))}</p>`,
    );
  }
  const headingText = card.querySelector<HTMLElement>(
    ".city-place-heading h2, .booking-step-header h2",
  );
  const fixedVenue =
    (selectedCityDay || placeView) &&
    (!currentPlaceOptions() ||
      Boolean(onlyVenue()) ||
      Boolean(informationalFixedPlace()));
  if (fixedVenue) card.querySelector(".city-place-intro")?.remove();
  if (headingText)
    headingText.textContent = fixedVenue
      ? ui("calendar.chooseTime", "Vyberte čas", "Choose a time")
      : selectedCityDay || placeView
        ? ui(
            "calendar.choosePlaceTime",
            "Vyberte místo a čas",
            "Choose a place and time",
          )
        : t("calendar.chooseDate", "Vyberte datum");
  card.querySelector("#change-date")?.remove();
  if (placeView && !selectedCityDay) {
    const back = document.createElement("button");
    back.type = "button";
    back.id = "change-date";
    back.className = "change-date-link";
    back.textContent = ui("calendar.changeDate", "Změnit datum", "Change date");
    back.onclick = () => {
      if (submitting) return;
      saveJourney();
      placeView = false;
      dayOffer = null;
      selectedCityPlaceId = "";
      selectedSlot = "";
      const url = new URL(location.href);
      url.searchParams.delete("date");
      history.pushState({ bookingStep: 2 }, "", url);
      card.classList.remove("cityday-places");
      void renderCalendar().then(focusStepHeading);
    };
    const heading = card.querySelector(".day-slots > h3");
    if (heading) {
      const header = document.createElement("div");
      header.className = "day-slots-heading";
      heading.replaceWith(header);
      header.append(heading, back);
    }
  }
  if (!selectedCityDay && !placeView) {
    card.classList.remove("cityday-places");
  }
  if (retainedMap)
    card.querySelector("#city-place-map")?.replaceWith(retainedMap);
  if (retainedPicker) {
    card.querySelector(".city-place-picker")?.replaceWith(retainedPicker);
    updatePlaceSelection();
  }
  wireMonthNavigation();
  mountOriginalReservationMarkers();
  document
    .querySelectorAll<HTMLButtonElement>(".calendar-day:not(:disabled)")
    .forEach((button) => {
      button.addEventListener(
        "click",
        () => void openScheduleDay(button.dataset.day ?? ""),
      );
      const warm = () => {
        if (!selectedCityDay && !rescheduleActionToken)
          void discoverDayPlaces(button.dataset.day ?? "", true).catch(
            () => {},
          );
      };
      button.addEventListener("mouseenter", warm, { once: true });
      button.addEventListener("focus", warm, { once: true });
    });
  if (
    selectedCityDay &&
    !verifiedCityDayFull &&
    currentPlaceOptions() &&
    needsMeetingPlaceSelection()
  ) {
    mountPlaceSelectionPrompt({
      document,
      panel: card.querySelector(".day-slots"),
      mode: "city",
      message: ui(
        "places.chooseForTimes",
        "Vyberte místo na mapě nebo v seznamu. Potom nabídneme jeho volné časy.",
        "Choose a place on the map or in the list to see its available times.",
      ),
    });
  }
  if (selectedCityPlaceId === "other" && !validAddressConfirmation()) {
    const panel = card.querySelector(".day-slots")!;
    panel
      .querySelectorAll(".slot-list, [role='status'], .shorter-meetings")
      .forEach((node) => node.remove());
    const hint = document.createElement("p");
    hint.setAttribute("role", "status");
    hint.textContent = ui(
      "places.addressForTimes",
      "Nejprve zadejte a ověřte adresu. Potom nabídneme časy s ohledem na přesuny.",
      "Enter and check the address first. Then we will show times that allow for travel.",
    );
    panel.append(hint);
  }
  mountFixedCityDayTypes();
  mountCityPlaceMap();
  restoreRenderFocus(card, focusSelector, focusedControl);
  if (focusedType)
    card
      .querySelector<HTMLInputElement>(
        `input[name="cityday-meeting-type"][value="${CSS.escape(focusedType)}"]`,
      )
      ?.focus({ preventScroll: true });
  if (retainedPicker || retainedMap)
    window.scrollTo({ top: scrollTop, behavior: "instant" });
  const rescheduleDay = document.querySelector<HTMLSelectElement>(
    "#reschedule-city-day",
  );
  if (rescheduleDay)
    rescheduleDay.onchange = () =>
      void selectRescheduleCityDay(rescheduleDay.value);
  document.querySelectorAll<HTMLButtonElement>(".slot").forEach((button) => {
    const date = dateKey(new Date(button.dataset.start!));
    button.disabled =
      uncheckedDates.has(date) ||
      (Boolean(selectedCityDay) && !selectionVerified());
  });
  document.querySelectorAll<HTMLButtonElement>(".slot").forEach((button) =>
    button.addEventListener("click", async () => {
      if (
        selectedCityPlaceId === "other" &&
        visitorLocationDetails.length > 160
      ) {
        document
          .querySelector<HTMLInputElement>("#visitor-location-details")
          ?.reportValidity();
        return;
      }
      if (selectedCityPlaceId === "other" && !validAddressConfirmation()) {
        refreshOwnAddress();
        document.querySelector<HTMLInputElement>("#visitor-address")?.focus();
        const status = document.querySelector("#visitor-address-results");
        if (status)
          status.textContent =
            visitorAddressNotice ||
            ui(
              "places.confirmFirst",
              "Nejprve ověřte adresu a vyberte nalezený výsledek.",
              "Check the address and choose a search result first.",
            );
        return;
      }
      selectedSlot = button.dataset.start ?? "";
      rejectedSlot = "";
      if (needsMeetingPlaceSelection()) {
        selectedSlot = "";
        const picker =
          document.querySelector<HTMLElement>(".city-place-picker");
        picker?.setAttribute("tabindex", "-1");
        picker?.focus();
        return;
      }
      const cityOrigin = button.dataset.cityKey
        ? citySlotOrigins.get(button.dataset.cityKey)
        : undefined;
      if (
        selectedCityPlaceId === "other" &&
        visitorAddressVerification?.source === "osm"
      ) {
        const proof = visitorAddressTokens.get(
          cityOrigin?.slug ??
            selectedCityDay?.slug ??
            `type:${selectedType!.slug}`,
        );
        if (!proof || Date.parse(proof.expiresAt) <= Date.now()) {
          selectedSlot = "";
          clearAddressConfirmation();
          refreshOwnAddress();
          document.querySelector<HTMLInputElement>("#visitor-address")?.focus();
          const status = document.querySelector("#visitor-address-results");
          if (status)
            status.textContent = ui(
              "places.verifyForTime",
              "Pro tento termín je potřeba adresu znovu ověřit.",
              "Verify the address again for this time.",
            );
          return;
        }
        visitorAddressVerification = { source: "osm", token: proof.token };
        visitorAddressExpiresAt = Date.parse(proof.expiresAt);
      }
      if (selectedCityPlaceId.startsWith("fixed:")) {
        selectedCityPlaceId = "";
        dayOffer = null;
      }
      if (cityOrigin && selectedType) {
        const type = cityOrigin.meetingTypes.find(
          (item) => item.slug === selectedType!.slug,
        );
        if (!type) return;
        selectedCityDay = cityOrigin;

        selectedType = type;
        selectedLocationId = type.locations[0]?.id ?? "";
        // Use the existing City Day journey and server authorization for submission.
        const target = new URL(
          `${bookingBasePath}/city/${encodeURIComponent(cityOrigin.slug)}`,
          location.origin,
        );
        target.searchParams.set("lang", locale);
        history.pushState({ bookingStep: 3, typeSlug: type.slug }, "", target);
        setStep(3);
        renderReservationForm();
        return;
      }
      if (rescheduleActionToken) {
        return void reviewReschedule(rescheduleActionToken, selectedSlot);
      }
      setStep(3, true);
      renderReservationForm();
    }),
  );
  document
    .querySelectorAll<HTMLInputElement>('input[name="public-location"]')
    .forEach((input) =>
      input.addEventListener("change", () => {
        selectedLocationId = input.value;
        selectedSlot = "";
        selectedCityPlaceId = "";
        visitorAddress = "";
        visitorLocationDetails = "";
        visitorLocationDetailsAddress = "";
        visitorAddressQuery = "";
        clearAddressConfirmation();
        dayOffer = null;
        dayPlaceSlots.clear();
        placeView = false;
        saveJourney();
        void renderCalendar();
      }),
    );
  bindPlaceCards(document, chooseCityPlace);
  if (!selectedCityDay && needsMeetingPlaceSelection()) {
    mountPlaceSelectionPrompt({
      document,
      panel: document.querySelector(".day-slots"),
      mode: "personal",
      message: ui(
        "places.chooseFirst",
        "Nejprve vyberte místo setkání.",
        "Choose a meeting place first.",
      ),
    });
  }
  wireMeetingCities();
  wireVisitorAddress();
  if (availabilityFailure && availabilityState === "error")
    mountAvailabilityFailure({
      root: app,
      panel: card.querySelector<HTMLElement>(".day-slots"),
      error: availabilityFailure,
      translate: ui,
      problemMessage,
      cooldownSeconds: availabilityRetrySeconds(),
      getContext: availabilityContext,
      isActive: (context) =>
        currentStep === 2 && context === availabilityContext(),
      onRetry: async () => {
        clearAvailabilityCache();
        await renderCalendar(true);
      },
    });
}

function wireMonthNavigation() {
  const previous = document.querySelector<HTMLButtonElement>("#previous-month");
  const next = document.querySelector<HTMLButtonElement>("#next-month");
  if (previous) previous.onclick = () => void changeMonth(-1);
  if (next) next.onclick = () => void changeMonth(1);
}

async function changeMonth(delta: number) {
  visibleMonth = shiftMonth(visibleMonth, delta);
  selectedDay = "";
  selectedSlot = "";
  await renderCalendar();
}

function renderReservationForm(): void {
  if (selectedCityPlaceId === "other" && !validAddressConfirmation()) {
    void renderCalendar();
    return;
  }
  stopSlotMonitoring();
  stopFormProtection();
  setStep(3);
  const notesMode = selectedType?.notesMode ?? "optional";
  const attachmentMode = selectedType?.attachmentMode ?? "hidden";
  const notesField =
    notesMode === "hidden"
      ? ""
      : `<label class="full">${escapeHtml(ui("form.preparationQuestion", "Co bych měl před schůzkou vědět?", "What should I know before the meeting?"))} ${notesMode === "optional" ? `<span>(${escapeHtml(ui("form.optional", "volitelné", "optional"))})</span>` : `<span>(${escapeHtml(ui("form.required", "povinné", "required"))})</span>`}<textarea name="notes" maxlength="4000" ${notesMode === "required" ? "required" : ""}></textarea></label>`;
  const attachmentField =
    attachmentMode === "hidden"
      ? ""
      : `<div class="full attachment-control"><div class="attachment-drop" role="group">${escapeHtml(ui("form.attachment", "Podklad ke schůzce", "Meeting attachment"))} ${attachmentMode === "optional" ? `<span>(${escapeHtml(ui("form.attachmentOptional", "volitelné, nejvýše 10 MB", "optional, up to 10 MB"))})</span>` : `<span>(${escapeHtml(ui("form.attachmentRequired", "povinné, nejvýše 10 MB", "required, up to 10 MB"))})</span>`}<input hidden name="attachment" type="file" accept=".pdf,.docx,.xlsx,.pptx,.txt,.png,.jpg,.jpeg" data-required="${attachmentMode === "required"}"/><button type="button" class="attachment-choose">${escapeHtml(ui("form.chooseFile", "Vybrat soubor", "Choose file"))}</button><strong class="attachment-filename"></strong><span class="attachment-instructions">${escapeHtml(ui("form.attachmentDrop", "Soubor přetáhněte sem nebo jej vyberte z disku", "Drop a file here or choose it from your device"))}</span><small class="attachment-formats">PDF, DOCX, XLSX, PPTX, TXT, PNG, JPG · max 10 MB</small><small class="attachment-status" aria-live="polite"></small><progress class="attachment-progress" hidden></progress></div><button class="text-button attachment-remove" type="button" hidden>${escapeHtml(ui("form.removeAttachment", "Odstranit přílohu", "Remove attachment"))}</button></div>`;
  const preparationSection =
    notesField || attachmentField
      ? `<div class="form-section-heading full"><h2>${escapeHtml(ui("form.preparation", "Co bych měl před schůzkou vědět", "Before we meet"))}</h2><p>${escapeHtml(ui("form.preparationHelp", "Podklady se zobrazují jen tehdy, když jsou pro tento typ schůzky užitečné.", "Preparation is requested only when useful for this meeting type."))}</p></div>${notesField}${attachmentField}`
      : "";
  const cityLocationProposal =
    selectedCityPlaceId === "other"
      ? `<section class="full proposed-place-summary"><strong>${escapeHtml(t("form.locationProposal", "Navrhované místo"))}</strong><p>${escapeHtml(visitorAddress)}</p>${visitorLocationDetails ? `<p>${escapeHtml(visitorLocationDetails)}</p>` : ""}<small>${escapeHtml(visitorAddressVerification?.source === "osm" ? ui("form.osmPlace", "Adresa ověřena v OSM — místo musí potvrdit hostitel.", "Address verified with OSM — host approval is required.") : ui("form.manualPlace", "Adresa není ověřena — místo musí potvrdit hostitel.", "Address is unverified — host approval is required."))}</small><button type="button" class="text-button" data-booking-step="2">${escapeHtml(ui("form.changePlace", "Změnit místo", "Change place"))}</button></section>`
      : "";
  const additionalInvitees =
    selectedType?.additionalInviteesMode === "hidden"
      ? ""
      : `<fieldset class="additional-invitees full"><legend>${escapeHtml(t("form.additionalInvitees", "Další účastníci"))}${selectedType?.additionalInviteesMode === "required" ? "" : ` <span>(${escapeHtml(t("form.optional", "volitelné"))})</span>`}</legend><div id="additional-invitee-list"></div><button class="text-button" id="add-invitee" type="button">${escapeHtml(t("form.addInvitee", "+ Přidat dalšího účastníka"))}</button><small>${escapeHtml(t("form.additionalInviteeHelp", "Nejvýše 10 unikátních e-mailových adres. Odkaz ke správě rezervace dostane jen hlavní účastník."))}</small></fieldset>`;
  app.innerHTML = `<section class="contact-step">${meetingSummary()}<section class="card contact-card">${stepHeader(3)}<p class="selected-time">${escapeHtml(formatWhen(selectedSlot))} · ${escapeHtml(zoneLabel(attendeeTimeZone))}${selectedCityDay && attendeeTimeZone !== selectedCityDay.timeZone ? `<br><small>${escapeHtml(t("form.localTime", "Místní čas: {time}", { time: formatWhen(selectedSlot, selectedCityDay.timeZone) }))}</small>` : ""}</p><form id="reservation-form"><label>${escapeHtml(t("form.name", "Jméno a příjmení"))}<input name="name" autocomplete="name" required maxlength="160" /></label><label class="validated-field">${escapeHtml(t("form.email", "E-mail"))}<div><input name="email" type="email" autocomplete="email" required /><small class="field-feedback" aria-live="polite"></small></div></label><label class="validated-field">${escapeHtml(t("form.phone", "Telefon v mezinárodním formátu"))}<div><input name="phone" type="tel" autocomplete="tel" required placeholder="${locale === "cs" ? "+420 123 456789" : "+44 55 6666 7777"}" maxlength="80" /><small class="field-feedback" aria-live="polite"></small></div></label><div class="company-row full"><label class="company-field">${escapeHtml(t("form.organization", "Společnost"))} <span>(${escapeHtml(t("form.optional", "volitelné"))})</span><input name="organization" autocomplete="organization" maxlength="180"/><div class="company-suggestions" role="listbox"></div><small class="company-help"></small></label><label>${escapeHtml(t("form.organizationCountry", "Země společnosti"))} <span>(${escapeHtml(t("form.optional", "volitelné"))})</span><select name="organizationCountry"><option value="">—</option>${companyCountries(
    locale,
  )
    .map(
      ({ code, label }) =>
        `<option value="${code}">${escapeHtml(label)}</option>`,
    )
    .join(
      "",
    )}<option value="XX">${escapeHtml(ui("public.other", "Jiná", "Other"))}</option></select><small class="country-source"></small></label></div>${additionalInvitees}${cityLocationProposal}${preparationSection}<label class="honeypot" aria-hidden="true">Web<input name="website" tabindex="-1" autocomplete="off" /></label><div id="booking-form-protection" class="full"></div><div class="submission-progress full" aria-live="polite"></div><p class="form-error full" role="alert" tabindex="-1"></p><button class="button full" type="submit">${escapeHtml(t("form.submit", "Potvrdit rezervaci"))}</button></form></section></section>`;
  app.querySelector(".contact-card > .selected-time")?.remove();
  const contact = app.querySelector<HTMLFormElement>("#reservation-form")!;
  renderSelectedPlaceSummary();
  for (const [field, mode] of [
    ["phone", selectedType?.phoneMode ?? "required"],
    ["organization", selectedType?.organizationMode ?? "optional"],
    [
      "organizationCountry",
      selectedType?.organizationCountryMode ?? "optional",
    ],
  ] as const) {
    const control = contact.elements.namedItem(field) as
      HTMLInputElement | HTMLSelectElement;
    if (mode === "hidden") {
      control.closest("label")?.remove();
    } else {
      control.required = mode === "required";
      if (mode === "required")
        control.closest("label")?.querySelector(":scope > span")?.remove();
      if (field === "phone" && mode === "optional")
        control
          .closest("label")
          ?.prepend(
            document.createTextNode(`(${t("form.optional", "volitelné")}) `),
          );
    }
  }
  if (!contact.querySelector(".company-row label"))
    contact.querySelector(".company-row")?.remove();
  contact.addEventListener(
    "invalid",
    (event) => {
      const field = event.target;
      if (
        field instanceof HTMLInputElement ||
        field instanceof HTMLSelectElement ||
        field instanceof HTMLTextAreaElement
      ) {
        event.preventDefault();
        fieldFeedback(field, field.validationMessage, "error");
        contact.querySelector<HTMLElement>(":invalid")?.focus();
      }
    },
    true,
  );
  contact.addEventListener("input", (event) => {
    const field = event.target;
    if (
      (field instanceof HTMLInputElement ||
        field instanceof HTMLSelectElement ||
        field instanceof HTMLTextAreaElement) &&
      !field.closest(".validated-field,.company-field,.attachment-drop")
    )
      fieldFeedback(field, "", "neutral");
  });
  contact
    .querySelectorAll<HTMLLabelElement>(".company-row > label")
    .forEach((label) => {
      const caption = document.createElement("span");
      caption.className = "field-caption";
      while (
        label.firstChild &&
        !(label.firstChild instanceof HTMLInputElement) &&
        !(label.firstChild instanceof HTMLSelectElement)
      )
        caption.append(label.firstChild);
      label.prepend(caption);
    });
  for (const [name, value] of Object.entries(draft?.fields ?? {})) {
    if (name === "guestLocationProposal" && selectedCityPlaceId === "other")
      continue;
    const input = contact.elements.namedItem(name);
    if (
      input instanceof HTMLInputElement ||
      input instanceof HTMLTextAreaElement ||
      input instanceof HTMLSelectElement
    )
      input.value = value;
  }
  if (draft?.hadAttachment && !attachment) {
    const notice = document.createElement("p");
    notice.setAttribute("role", "status");
    notice.textContent = ui(
      "public.pleaseSelectYourAttachmentAgainAfterReloading",
      "Po obnovení stránky prosím vyberte přílohu znovu.",
      "Please select your attachment again after reloading.",
    );
    contact.querySelector(".attachment-control")?.prepend(notice);
  }
  initialiseAdditionalInvitees();
  initialiseCompanyLookup();
  void initialiseCountry(contact);
  if (attachmentMode !== "hidden") {
    initialiseAttachment();
    if (attachment) {
      const input = contact.querySelector<HTMLInputElement>(
        '[name="attachment"]',
      );
      if (input) {
        const transfer = new DataTransfer();
        transfer.items.add(attachment);
        input.files = transfer.files;
        input.dispatchEvent(new Event("change"));
      }
    }
  }
  initialiseContactValidation();
  initialiseTurnstile();
  focusStepHeading();
  monitorContactSlot();
  focusView();
}

function monitorContactSlot(): void {
  const form = app.querySelector<HTMLFormElement>("#reservation-form")!;
  const submit = form.querySelector<HTMLButtonElement>(
    'button[type="submit"]',
  )!;
  const notice = document.createElement("div");
  notice.className = "contact-slot-status full";
  notice.setAttribute("role", "status");
  submit.before(notice);
  const key = contactSelectionKey(),
    epoch = contactCheckEpoch;
  contactCheckAbort = new AbortController();
  const signal = contactCheckAbort.signal;
  let running = false,
    failures = 0;
  const valid = () =>
    !signal.aborted &&
    epoch === contactCheckEpoch &&
    form.isConnected &&
    key === contactSelectionKey();
  const display = () => {
    if (!valid()) return;
    updateReservationSubmitReadiness();
    notice.replaceChildren();
    if (contactSlotState === "verified") return;
    notice.append(
      document.createTextNode(
        contactSlotState === "checking"
          ? ui(
              "contact.checkingTime",
              "Ověřujeme vybraný čas. Zatím můžete vyplnit kontaktní údaje.",
              "Checking your time. You can fill in your contact details meanwhile.",
            )
          : contactSlotState === "unavailable"
            ? ui(
                "contact.timeTaken",
                "Vybraný čas už není dostupný. Vaše údaje zůstávají uložené.",
                "This time is no longer available. Your details have been retained.",
              )
            : ui(
                "contact.checkFailed",
                "Čas nyní nelze ověřit. Kontrolu automaticky zopakujeme; vaše údaje zůstávají vyplněné.",
                "We cannot verify this time yet. We will retry automatically; your details are retained.",
              ),
      ),
    );
    if (contactSlotState === "unavailable") {
      const choose = document.createElement("button");
      choose.type = "button";
      choose.className = "text-button";
      choose.dataset.bookingStep = "2";
      choose.textContent = ui(
        "contact.chooseAnotherTime",
        "Vybrat jiný čas",
        "Choose another time",
      );
      notice.append(choose);
    }
  };
  const verify = async () => {
    if (
      !valid() ||
      running ||
      submitting ||
      document.hidden ||
      !navigator.onLine
    )
      return;
    clearTimeout(contactCheckTimer);
    running = true;
    try {
      const available = await revalidateSelectedSlot(true, undefined, signal);
      if (!valid()) return;
      contactSlotState = available ? "verified" : "unavailable";
      contactVerifiedKey = available ? key : "";
      failures = 0;
    } catch (error) {
      if (!valid()) return;
      contactSlotState = "error";
      contactVerifiedKey = "";
      const seconds = Math.max(
        [2, 5, 10, 30, 60][Math.min(failures++, 4)]!,
        availabilityRetrySeconds(),
        Number((error as { retryAfterSeconds?: number })?.retryAfterSeconds) ||
          0,
      );
      contactCheckTimer = window.setTimeout(
        () => void verify(),
        seconds * 1000,
      );
    } finally {
      running = false;
      display();
    }
  };
  contactSlotState = "checking";
  contactVerifiedKey = "";
  display();
  slotRevalidationTimer = window.setInterval(() => void verify(), 60_000);
  slotVisibilityHandler = () => {
    if (!document.hidden) void verify();
  };
  document.addEventListener("visibilitychange", slotVisibilityHandler);
  window.addEventListener("online", () => void verify(), { signal });
  void verify();
}

function updateReservationSubmitReadiness(): void {
  const submit = document.querySelector<HTMLButtonElement>(
    '#reservation-form button[type="submit"]',
  );
  if (submit)
    submit.disabled =
      submitting ||
      contactSlotState !== "verified" ||
      contactVerifiedKey !== contactSelectionKey() ||
      Boolean(turnstileSiteKey && !turnstileToken);
}

function initialiseTurnstile() {
  const node = document.querySelector<HTMLElement>("#booking-form-protection");
  if (!node || !turnstileSiteKey) return;
  startFormProtection(
    node,
    turnstileSiteKey,
    locale === "cs",
    (token) => {
      turnstileToken = token;
      updateReservationSubmitReadiness();
    },
    t,
  );
}

function initialiseAdditionalInvitees(): void {
  const list = document.querySelector<HTMLElement>("#additional-invitee-list");
  const add = document.querySelector<HTMLButtonElement>("#add-invitee");
  if (!list || !add) return;
  const append = (value = "", focus = true) => {
    if (list.querySelectorAll("input").length >= 10) return;
    const row = document.createElement("div");
    row.className = "additional-invitee-row";
    row.innerHTML = `<label class="validated-field">${escapeHtml(t("form.email", "E-mail"))}<input type="email" name="additionalInviteeEmail" autocomplete="email" required /><small class="field-feedback" aria-live="polite"></small></label><button type="button" class="text-button" aria-label="${escapeHtml(t("form.removeInvitee", "Odebrat účastníka"))}">${escapeHtml(t("form.removeInvitee", "Odebrat účastníka"))}</button>`;
    row.querySelector("button")?.addEventListener("click", () => {
      if (
        selectedType?.additionalInviteesMode === "required" &&
        list.querySelectorAll("input").length === 1
      ) {
        row.querySelector("input")!.value = "";
        row.querySelector("input")!.focus();
      } else row.remove();
      add.disabled = false;
      saveJourney();
    });
    row.querySelector("input")!.value = value;
    list.append(row);
    initialiseEmailValidation(row.querySelector("input")!);
    if (focus) row.querySelector("input")?.focus();
    add.disabled = list.querySelectorAll("input").length >= 10;
  };
  for (const value of draft?.invitees ?? []) append(value, false);
  if (
    selectedType?.additionalInviteesMode === "required" &&
    !list.querySelector("input")
  )
    append("", false);
  add.addEventListener("click", () => {
    append();
    saveJourney();
  });
}

async function consumeTurnstileToken(
  node: HTMLElement | null,
): Promise<string> {
  if (!turnstileSiteKey) return "local-bypass";
  const deadline = Date.now() + 15_000;
  while (!turnstileToken && node?.isConnected && Date.now() < deadline)
    await new Promise((resolve) => window.setTimeout(resolve, 100));
  if (!turnstileToken || !node?.isConnected)
    throw new Error(
      ui(
        "form.protectionUnavailable",
        "Bezpečnostní ověření není připravené. Zkuste to prosím znovu.",
        "Security verification is not ready. Please retry.",
      ),
    );
  const value = turnstileToken;
  turnstileToken = "";
  updateReservationSubmitReadiness();
  return value;
}

/** A token belongs to one request. Renew only after that request has settled. */
async function withFormVerification<T>(
  send: (token: string) => Promise<T>,
): Promise<T> {
  const node = document.querySelector<HTMLElement>("#booking-form-protection");
  const token = await consumeTurnstileToken(node);
  try {
    return await send(token);
  } finally {
    // A late response must not reset protection on a newer form.
    if (node?.isConnected) initialiseTurnstile();
  }
}

function fieldFeedback(
  input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
  message: string,
  state: "success" | "error" | "neutral",
) {
  const label = input.closest("label");
  label
    ?.querySelectorAll(".valid-check,.inline-error")
    .forEach((node) => node.remove());
  let feedback = input.nextElementSibling;
  if (!feedback?.classList.contains("field-feedback")) {
    feedback = document.createElement("small");
    feedback.id = `feedback-${crypto.randomUUID()}`;
    input.after(feedback);
    input.setAttribute(
      "aria-describedby",
      [input.getAttribute("aria-describedby"), feedback.id]
        .filter(Boolean)
        .join(" "),
    );
  }
  feedback.id ||= `feedback-${crypto.randomUUID()}`;
  input.setAttribute(
    "aria-describedby",
    [
      ...new Set([
        ...(input.getAttribute("aria-describedby") ?? "")
          .split(" ")
          .filter(Boolean),
        feedback.id,
      ]),
    ].join(" "),
  );
  feedback.classList.remove("success", "error", "neutral");
  feedback.classList.add("field-feedback", state);
  feedback.setAttribute("aria-live", "polite");
  feedback.textContent = message;
  input.setAttribute("aria-invalid", String(state === "error"));
}

function updateContactValidity(
  input: HTMLInputElement,
  valid: boolean,
  invalidText: string,
): void {
  const hasValue = Boolean(input.value.trim());
  fieldFeedback(
    input,
    !hasValue
      ? ""
      : !valid
        ? invalidText
        : input.name === "phone"
          ? ui(
              "form.phoneFormatValid",
              "✓ Platný formát čísla",
              "✓ Valid phone number format",
            )
          : ui("form.emailFormatValid", "✓ Platný formát", "✓ Valid format"),
    !hasValue ? "neutral" : valid ? "success" : "error",
  );
  input.setCustomValidity(hasValue && !valid ? invalidText : "");
}

function initialiseEmailValidation(email: HTMLInputElement): void {
  const validate = () =>
    updateContactValidity(
      email,
      isPublicEmail(email.value),
      ui(
        "public.enterACompleteEmailAddressForExampleNameexamplecom",
        "Zadejte platný e-mail.",
        "Enter a valid email.",
      ),
    );
  email.addEventListener("input", validate);
  validate();
}

function initialiseContactValidation(): void {
  const email = document.querySelector<HTMLInputElement>('input[name="email"]');
  const phone = document.querySelector<HTMLInputElement>('input[name="phone"]');
  if (email) initialiseEmailValidation(email);
  phone?.addEventListener("input", () => {
    const parsed = parsePhoneNumberFromString(phone.value);
    updateContactValidity(
      phone,
      (!phone.required && !phone.value.trim()) ||
        (phone.value.trim().startsWith("+") && Boolean(parsed?.isValid())),
      ui(
        "form.phoneInvalid",
        "Použijte platné mezinárodní číslo začínající +, například +44 20 7946 0958.",
        "Use a valid international phone number starting with +.",
      ),
    );
  });
  if (phone?.value) phone.dispatchEvent(new Event("input"));
}

async function revalidateSelectedSlot(
  showError: boolean,
  cityOverride?: PublicCityDayDetail,
  signal?: AbortSignal,
): Promise<boolean> {
  if (!selectedType || !selectedSlot) return false;
  const chosen = selectedSlot;
  const chosenType = selectedType.slug,
    chosenPlace = selectedCityPlaceId,
    chosenLocation = selectedLocationId;
  const requestSignal = signal
    ? AbortSignal.any([signal, AbortSignal.timeout(10000)])
    : AbortSignal.timeout(10000);
  const current = (data: {
    slots: Slot[];
    availabilityFreshness?: AvailabilityFreshness;
  }) => {
    if (
      data.availabilityFreshness &&
      (data.availabilityFreshness.state !== "current" ||
        data.availabilityFreshness.complete === false)
    )
      throw new Error("Availability is not yet verified");
    return data.slots.some((slot) => slot.startsAt === chosen);
  };
  if (rescheduleActionToken) {
    const { data, error, response } = await api.GET(
      "/booking/public/reservations/{actionToken}/availability",
      {
        cache: "no-store",
        params: {
          path: { actionToken: rescheduleActionToken },
          query: {
            date: dateKey(new Date(chosen)),
            locationId: selectedLocationId || undefined,
            timeZone: attendeeTimeZone,
            ...(selectedRescheduleDayId()
              ? { cityDayId: selectedRescheduleDayId() }
              : {}),
            cityPlaceId: selectedCityPlaceId || undefined,
          },
        },
        signal: requestSignal,
      },
    );
    if (error) {
      if (showError) {
        const raw = response.headers.get("Retry-After");
        const delay = raw
          ? /^\d+$/.test(raw)
            ? Number(raw)
            : Math.max(0, (Date.parse(raw) - Date.now()) / 1000)
          : 0;
        throw Object.assign(
          new Error(
            isMaintenance(error) ? error.detail : problemMessage(error),
          ),
          {
            retryAfterSeconds: Number.isFinite(delay) ? delay : 0,
          },
        );
      }
      return false;
    }
    rescheduleAvailability = data;
    standardPlaceOptions = data.placeOptions;
    standardPlaceTypeId = selectedType.slug;
    if (selectedCityDay)
      selectedCityDay = { ...selectedCityDay, placeOptions: data.placeOptions };
    return current(data);
  }
  const dayContext = cityOverride ?? selectedCityDay;
  if (dayContext) {
    const { data, error, response } = await api.GET(
      "/booking/public/{profileSlug}/city/{cityDaySlug}/availability",
      {
        cache: "no-store",
        headers: routingProofHeaders(dayContext.slug),
        signal: requestSignal,
        params: {
          path: { profileSlug, cityDaySlug: dayContext.slug },
          query: {
            meetingType: selectedType.slug,
            locationId: selectedLocationId || undefined,
            timeZone: attendeeTimeZone,
            ...(dayContext.placeOptions && selectedCityPlaceId
              ? { place: selectedCityPlaceId }
              : {}),
          },
        },
      },
    );
    if (error) {
      if (showError) {
        const raw = response.headers.get("Retry-After");
        const delay = raw
          ? /^\d+$/.test(raw)
            ? Number(raw)
            : Math.max(0, (Date.parse(raw) - Date.now()) / 1000)
          : 0;
        throw Object.assign(
          new Error(
            isMaintenance(error) ? error.detail : problemMessage(error),
          ),
          {
            retryAfterSeconds: Number.isFinite(delay) ? delay : 0,
          },
        );
      }
      return false;
    }
    return current(data);
  }
  const day = dateKey(new Date(selectedSlot));
  const { data, error, response } = await api.GET(
    "/booking/public/{profileSlug}/{meetingTypeSlug}/availability",
    {
      cache: "no-store",
      headers: routingProofHeaders(),
      signal: requestSignal,
      params: {
        path: { profileSlug, meetingTypeSlug: selectedType.slug },
        query: {
          from: day,
          to: day,
          timeZone: attendeeTimeZone,
          locationId: selectedLocationId || undefined,
          cityPlaceId: selectedCityPlaceId || undefined,
        },
      },
    },
  );
  if (error) {
    if (showError) {
      const raw = response.headers.get("Retry-After");
      const delay = raw
        ? /^\d+$/.test(raw)
          ? Number(raw)
          : Math.max(0, (Date.parse(raw) - Date.now()) / 1000)
        : 0;
      throw Object.assign(
        new Error(isMaintenance(error) ? error.detail : problemMessage(error)),
        {
          retryAfterSeconds: Number.isFinite(delay) ? delay : 0,
        },
      );
    }
    return false;
  }
  if (
    !requestSignal.aborted &&
    selectedSlot === chosen &&
    selectedType?.slug === chosenType &&
    selectedCityPlaceId === chosenPlace &&
    selectedLocationId === chosenLocation &&
    data.placeOptions
  ) {
    standardPlaceOptions = data.placeOptions;
    standardPlaceTypeId = chosenType;
    renderSelectedPlaceSummary();
  }
  return current(data);
}

function initialiseCompanyLookup() {
  const country = document.querySelector<HTMLSelectElement>(
    'select[name="organizationCountry"]',
  );
  const input = document.querySelector<HTMLInputElement>(
    'input[name="organization"]',
  );
  selectedCompany = null;
  if (!country) return;
  const countryNames = new Intl.DisplayNames([locale], { type: "region" });
  for (const option of country.options) {
    if (!option.value) continue;
    option.textContent =
      option.value === "XX"
        ? ui("public.other", "Jiná", "Other")
        : (countryNames.of(option.value) ?? option.textContent);
  }
  mountCountryPicker(country, locale);
  if (!input) return;
  const list = document.querySelector<HTMLElement>(".company-suggestions")!;
  const help = document.querySelector<HTMLElement>(".company-help")!;
  help.classList.add("field-feedback");
  help.id = "organization-feedback";
  input.after(help);
  input.setAttribute("aria-describedby", help.id);
  let timer = 0;
  let generation = 0;
  let controller: AbortController | undefined;
  help.setAttribute("role", "status");
  const search = () => {
    const current = ++generation;
    controller?.abort();
    selectedCompany = null;
    list.innerHTML = "";
    window.clearTimeout(timer);
    if (!input.value.trim() || !country.value) {
      help.textContent = "";
      return;
    }
    help.textContent =
      country.value === "CZ"
        ? ui(
            "form.aresHelp",
            "Českou firmu se pokusíme ověřit v ARES.",
            "We will try to verify the Czech company in ARES.",
          )
        : ui(
            "form.foreignCompanyHelp",
            "Zahraniční firmu uložíme ke kontrole.",
            "The company will be saved for review.",
          );
    window.clearTimeout(timer);
    if (country.value !== "CZ" || input.value.trim().length < 4) return;
    help.textContent = ui(
      "public.searchingCompanyRegisters",
      "Hledám v registrech…",
      "Searching company registers…",
    );
    timer = window.setTimeout(async () => {
      controller = new AbortController();
      try {
        const { data, error } = await api.GET(
          "/booking/public/{profileSlug}/company-suggestions",
          {
            signal: controller.signal,
            params: { path: { profileSlug }, query: { q: input.value.trim() } },
          },
        );
        if (current !== generation || !input.isConnected) return;
        if (error || !data) {
          help.textContent = ui(
            "form.aresUnavailable",
            "ARES je právě nedostupný. Pokračujte volným názvem; firmu později zkontrolujeme.",
            "ARES is unavailable. Enter the company name; we will review it later.",
          );
          return;
        }
        help.textContent = data.items.length
          ? ui(
              "public.selectAResultToVerifyTheCompany",
              "Vyberte firmu z výsledků pro ověření.",
              "Select a result to verify the company.",
            )
          : ui(
              "public.noCompanyFoundTryItsNameOrRegistrationNumberFreeTextCanBeSavedForReview",
              "Firma nenalezena. Upravte název nebo zadejte IČO; volný název lze ponechat ke kontrole.",
              "No company found. Try its name or registration number; free text can be saved for review.",
            );
        if (data.meta?.aresAvailable === false && data.items.length) {
          help.textContent += ui(
            "public.aresIsUnavailableResultsAreFromTheJusticeIndex",
            " ARES je nedostupný, výsledky pocházejí z indexu Justice.",
            " ARES is unavailable; results are from the Justice index.",
          );
        }
        if (data.meta?.sources?.length) {
          const sources = data.meta.sources.map((source) =>
            source === "JUSTICE_INDEX"
              ? "Justice (index)"
              : source === "CACHE"
                ? ui("public.cache", "mezipaměť", "cache")
                : "ARES (live)",
          );
          help.textContent += ` ${escapeHtml(ui("public.sources", "Zdroje", "Sources"))}: ${sources.join(", ")}.`;
        }
        if (data.meta?.justiceIndexStale)
          help.textContent += ui(
            "public.theJusticeIndexIsAwaitingRefresh",
            " Index Justice čeká na aktualizaci.",
            "The Justice index is awaiting refresh.",
          );
        list.innerHTML = data.items
          .map(
            (item, index) =>
              `<button type="button" data-company="${index}" role="option"><strong>${escapeHtml(item.officialName)}</strong><small>IČO ${escapeHtml(item.registrationNumber)} · ${escapeHtml(item.registeredAddress)}</small></button>`,
          )
          .join("");
        list.querySelectorAll<HTMLButtonElement>("button").forEach((button) =>
          button.addEventListener("click", () => {
            if (current !== generation) return;
            selectedCompany = data.items[Number(button.dataset.company)];
            input.value = selectedCompany?.officialName ?? input.value;
            saveJourney();
            list.innerHTML = "";
            help.textContent = selectedCompany
              ? `${escapeHtml(ui("public.selectedFromRegisterRegistrationNumber", "Vybráno z registru · IČO", "Selected from register · Registration number"))} ${selectedCompany.registrationNumber}`
              : "";
          }),
        );
      } catch {
        if (current !== generation || !input.isConnected) return;
        help.textContent = ui(
          "public.registersAreUnavailableTheNameCanBeSavedForReview",
          "Registry jsou nedostupné. Název lze ponechat ke kontrole.",
          "Registers are unavailable. The name can be saved for review.",
        );
      }
    }, 350);
  };
  country.addEventListener("change", search);
  input.addEventListener("input", search);
}

function initialiseAttachment() {
  const zone = document.querySelector<HTMLElement>(".attachment-drop")!;
  const input = zone.querySelector<HTMLInputElement>('input[type="file"]')!;
  const status = zone.querySelector<HTMLElement>(".attachment-status")!;
  status.classList.add("field-feedback");
  status.id = "attachment-feedback";
  const filename = zone.querySelector<HTMLElement>(".attachment-filename")!;
  const choose = zone.querySelector<HTMLButtonElement>(".attachment-choose")!;
  choose.onclick = () => input.click();
  input.setAttribute("aria-describedby", status.id);
  const remove =
    document.querySelector<HTMLButtonElement>(".attachment-remove")!;
  const select = (file?: File) => {
    const nextKey = file
      ? `${file.name}:${file.size}:${file.lastModified}`
      : "";
    if (attachmentUpload?.fileKey !== nextKey) attachmentUpload = null;
    attachment = file ?? null;
    status.textContent = file
      ? `${(file.size / 1024 / 1024).toFixed(1)} MB · ${ui("form.fileSelected", "Vybráno, připraveno k nahrání", "Selected, ready to upload")}`
      : "";
    zone.dataset.uploadState = file ? "selected" : "empty";
    if (file) {
      try {
        validateAttachment(file);
      } catch (error) {
        zone.dataset.uploadState = "error";
        status.textContent = (error as Error).message;
      }
      if (attachmentUpload?.acceptedId) {
        zone.dataset.uploadState = "uploaded";
        status.textContent = ui(
          "status.attachmentReady",
          "Příloha je připravena.",
          "Attachment ready.",
        );
      }
    }
    filename.textContent = file?.name ?? "";
    choose.textContent = file
      ? ui("form.changeFile", "Změnit soubor", "Change file")
      : ui("form.chooseFile", "Vybrat soubor", "Choose file");
    choose.setCustomValidity(
      !file && input.dataset.required === "true"
        ? ui("form.fileRequired", "Vyberte přílohu.", "Choose an attachment.")
        : "",
    );
    status.title = file?.name ?? "";
    remove.hidden = !file;
    saveJourney();
  };
  input.addEventListener("change", () => select(input.files?.[0]));
  zone.addEventListener("dragover", (event) => {
    event.preventDefault();
    zone.classList.add("dragging");
  });
  zone.addEventListener("dragleave", () => zone.classList.remove("dragging"));
  zone.addEventListener("drop", (event) => {
    event.preventDefault();
    zone.classList.remove("dragging");
    select(event.dataTransfer?.files[0]);
  });
  remove.addEventListener("click", () => {
    input.value = "";
    select();
    choose.focus();
  });
  select(attachment ?? undefined);
}

function attachmentMimeType(file: File): string {
  if (file.type) return file.type;
  const extension = file.name.toLowerCase().split(".").pop();
  return (
    {
      pdf: "application/pdf",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      txt: "text/plain",
      png: "image/png",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
    }[extension ?? ""] ?? "application/octet-stream"
  );
}

function validateAttachment(file: File): void {
  if (file.size > 10 * 1024 * 1024)
    throw new Error("Příloha může mít nejvýše 10 MB.");
  const allowed = /\.(pdf|docx|xlsx|pptx|txt|png|jpe?g)$/i;
  if (!allowed.test(file.name))
    throw new Error(
      locale === "en"
        ? "Allowed files: PDF, DOCX, XLSX, PPTX, TXT, PNG, JPG; up to 10 MB."
        : "Povolené soubory: PDF, DOCX, XLSX, PPTX, TXT, PNG, JPG; nejvýše 10 MB.",
    );
}

async function uploadAttachment(
  file: File,
  waiting: () => void,
  current: () => boolean,
): Promise<string> {
  validateAttachment(file);
  const bytes = new Uint8Array(await file.arrayBuffer());
  const sha256 = Array.from(
    new Uint8Array(await crypto.subtle.digest("SHA-256", bytes)),
  )
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
  const mimeType = attachmentMimeType(file);
  const fileKey = `${file.name}:${file.size}:${file.lastModified}`;
  if (!current() || attachment !== file)
    throw new Error("attachment-context-changed");
  if (attachmentUpload?.expiresAt && attachmentUpload.expiresAt <= Date.now())
    attachmentUpload = null;
  if (attachmentUpload?.fileKey === fileKey && attachmentUpload.acceptedId)
    return attachmentUpload.acceptedId;
  if (!attachmentUpload || attachmentUpload.fileKey !== fileKey) {
    const { data: session, error } = await withFormVerification((token) =>
      api.POST("/booking/public/{profileSlug}/attachments", {
        params: { path: { profileSlug } },
        body: {
          filename: file.name,
          mimeType: mimeType as never,
          byteSize: file.size,
          sha256,
          turnstileToken: token,
        },
      }),
    );
    if (error) throw error;
    if (!current() || attachment !== file)
      throw new Error("attachment-context-changed");
    attachmentUpload = {
      fileKey,
      id: session.id,
      uploadToken: session.uploadToken,
      expiresAt: Date.now() + session.expiresInSeconds * 1000,
      uploadMode: session.uploadMode,
      ...(session.uploadUrl ? { uploadUrl: session.uploadUrl } : {}),
      uploaded: false,
    };
  }
  const session = attachmentUpload;
  const retryMessage = (seconds: number) =>
    ui(
      "status.attachmentRetry",
      "Bezpečnostní kontrola je vytížená. Zkuste potvrzení znovu za {seconds} s. Údaje zůstávají vyplněné.",
      "The security check is busy. Try confirming again in {seconds} s. Your details are retained.",
    ).replace("{seconds}", String(seconds));
  if (session.retryNotBefore && session.retryNotBefore > Date.now())
    throw new Error(
      retryMessage(Math.ceil((session.retryNotBefore - Date.now()) / 1000)),
    );
  if (
    !session.uploaded &&
    session.uploadMode === "direct" &&
    session.uploadUrl
  ) {
    const response = await fetch(session.uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": mimeType,
        "x-amz-meta-sha256": sha256,
        "x-amz-meta-quarantine": "true",
      },
      body: file,
    });
    if (!response.ok) throw new Error("Nahrání přílohy se nepodařilo.");
    session.uploaded = true;
  }
  const contentBase64 =
    session.uploadMode === "api" && !session.uploaded
      ? await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onerror = () => reject(reader.error);
          reader.onload = () =>
            resolve(String(reader.result).split(",")[1] ?? "");
          reader.readAsDataURL(file);
        })
      : undefined;
  const complete = async (content?: string) => {
    const response = await api.POST(
      "/booking/public/{profileSlug}/attachments/{attachmentId}/complete",
      {
        params: { path: { profileSlug, attachmentId: session.id } },
        body: {
          uploadToken: session.uploadToken,
          ...(content ? { contentBase64: content } : {}),
        },
      },
    );
    if (response.response.status === 429) {
      const retry = response.response.headers.get("Retry-After");
      const seconds =
        retry && /^\d+$/.test(retry) ? Math.max(1, Number(retry)) : 60;
      session.retryNotBefore = Date.now() + seconds * 1000;
      throw new Error(retryMessage(seconds));
    }
    if (response.error) {
      if (
        response.response.status === 503 &&
        response.error.type?.endsWith("attachment-scanner-unavailable")
      )
        session.uploaded = true;
      if (response.response.status === 404 && attachmentUpload === session) {
        attachmentUpload = null;
        throw new Error(
          locale === "cs"
            ? "Platnost nahrávání vypršela. Potvrďte formulář znovu; soubor a údaje zůstaly zachované."
            : "The upload expired. Confirm again; your file and details have been retained.",
        );
      }
      throw response.error;
    }
    return response.data;
  };
  const completed = await complete(contentBase64);
  session.uploaded = true;
  const accepted = await waitForAttachmentScan(completed, {
    current: () =>
      current() && attachmentUpload === session && attachment === file,
    waiting,
    read: () => complete(),
  }).catch((error: unknown) => {
    if (error instanceof AttachmentScanPendingError) {
      error.message = ui(
        "status.attachmentScanPending",
        "Kontrola přílohy ještě není dokončena. Údaje zůstávají vyplněné; zkuste potvrzení později. Rezervace zatím nevznikla.",
        "The file check is not finished yet. Your details are retained; please try confirming later. No booking has been created yet.",
      );
    } else if (
      error instanceof Error &&
      error.message === "attachment-context-changed"
    ) {
      error.message = ui(
        "status.attachmentChanged",
        "Vybraná příloha se změnila. Zkontrolujte formulář a potvrďte jej znovu.",
        "The selected file changed. Review the form and confirm again.",
      );
    }
    throw error;
  });
  session.acceptedId = accepted.id;
  return accepted.id;
}

async function submitReservation(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  if (
    submitting ||
    (turnstileSiteKey && !turnstileToken) ||
    contactSlotState !== "verified" ||
    contactVerifiedKey !== contactSelectionKey()
  )
    return;
  if (!selectedType || !selectedSlot || !selectedLocationId) return;
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (selectedCityPlaceId === "other" && !validAddressConfirmation()) {
    saveJourney();
    setStep(2, true);
    await renderCalendar();
    refreshOwnAddress(true);
    return;
  }
  const submit = form.querySelector<HTMLButtonElement>(
    'button[type="submit"]',
  )!;
  const errorNode = form.querySelector<HTMLElement>(".form-error")!;
  const progressNode = form.querySelector<HTMLElement>(".submission-progress")!;
  let values = new FormData(form);
  const phoneInput = form.querySelector<HTMLInputElement>(
    'input[name="phone"]',
  );
  const phoneValue = phoneInput?.value.trim() ?? "";
  let parsedPhone = parsePhoneNumberFromString(phoneValue);
  if (
    phoneInput &&
    (phoneInput.required || phoneValue) &&
    (!phoneValue.startsWith("+") || !parsedPhone?.isValid())
  ) {
    phoneInput.setCustomValidity(
      ui(
        "form.phoneInvalid",
        "Zadejte platné číslo v mezinárodním formátu začínajícím +.",
        "Enter a valid international phone number starting with +.",
      ),
    );
    phoneInput.reportValidity();
    return;
  }
  phoneInput?.setCustomValidity("");
  if (
    form.querySelector<HTMLInputElement>('input[name="attachment"]')?.dataset
      .required === "true" &&
    !attachment
  ) {
    errorNode.textContent = ui(
      "form.fileRequired",
      "Vyberte přílohu.",
      "Choose an attachment.",
    );
    form.querySelector<HTMLButtonElement>(".attachment-choose")?.focus();
    return;
  }
  if (!form.reportValidity()) return;
  for (const email of form.querySelectorAll<HTMLInputElement>(
    'input[type="email"]',
  )) {
    email.setCustomValidity(
      isPublicEmail(email.value)
        ? ""
        : ui(
            "public.enterACompleteEmailAddress",
            "Zadejte platný e-mail.",
            "Enter a valid email.",
          ),
    );
    email.oninput = () =>
      email.setCustomValidity(
        isPublicEmail(email.value)
          ? ""
          : ui(
              "public.enterACompleteEmailAddress",
              "Zadejte platný e-mail.",
              "Enter a valid email.",
            ),
      );
  }
  if (!form.reportValidity()) return;
  submitting = true;
  submit.disabled = true;
  errorNode.textContent = "";
  let submissionTimer: number | undefined;
  let submissionDetail: HTMLElement | undefined;
  try {
    progressNode.textContent = ui(
      "status.verifyingAvailability",
      "Ověřujeme dostupnost termínu…",
      "Verifying availability…",
    );
    if (!(await revalidateSelectedSlot(true))) {
      rejectedSlot = selectedSlot;
      throw new Error(
        ui(
          "calendar.slotUnavailable",
          "Vybraný termín už není dostupný. Zvolte prosím jiný.",
          "The selected time is no longer available. Please choose another.",
        ),
      );
    }
    progressNode.textContent = ui(
      "status.finishingReservation",
      "Dokončujeme rezervaci…",
      "Completing your booking…",
    );
    const detailNode = document.createElement("p");
    detailNode.className = "submission-operation-detail";
    detailNode.setAttribute("aria-live", "polite");
    progressNode.after(detailNode);
    submissionDetail = detailNode;
    let waitingDetail = ui(
      "status.reservationWaiting",
      "Čekáme na dokončení vytvoření rezervace. Potvrzení se zobrazí po ověření výsledku.",
      "We are waiting for booking creation to finish. Confirmation will appear once the result is verified.",
    );
    submissionTimer = window.setTimeout(() => {
      if (detailNode.isConnected) detailNode.textContent = waitingDetail;
    }, 5000);
    const attachmentStatus =
      form.querySelector<HTMLElement>(".attachment-status");
    const attachmentProgress = form.querySelector<HTMLProgressElement>(
      ".attachment-progress",
    );
    if (attachment) {
      form.querySelector<HTMLElement>(".attachment-drop")!.dataset.uploadState =
        "uploading";
      if (attachmentStatus)
        attachmentStatus.textContent = ui(
          "status.uploadingAttachment",
          "Nahrávám přílohu…",
          "Uploading the file…",
        );
      waitingDetail = ui(
        "status.attachmentWaiting",
        "Připravujeme přílohu a ověřujeme její bezpečnost. Termín zatím není rezervovaný.",
        "We are preparing and checking your attachment. The time is not booked yet.",
      );
      if (attachmentProgress) attachmentProgress.hidden = false;
    }
    const attachmentId =
      attachment && selectedType.attachmentMode !== "hidden"
        ? await uploadAttachment(
            attachment,
            () => {
              const message = ui(
                "status.attachmentScanWaiting",
                "Příloha je nahraná a čeká na bezpečnostní kontrolu. Může to trvat několik minut. Nechte stránku otevřenou; termín zatím není rezervovaný.",
                "Your file is uploaded and awaiting a security check. This may take a few minutes. Keep this page open; the time is not booked yet.",
              );
              if (attachmentStatus) attachmentStatus.textContent = message;
              if (submissionDetail) submissionDetail.textContent = message;
            },
            () => form.isConnected,
          )
        : undefined;
    if (attachmentId && attachmentStatus) {
      form.querySelector<HTMLElement>(".attachment-drop")!.dataset.uploadState =
        "uploaded";
      attachmentStatus.textContent = ui(
        "status.attachmentReady",
        "Příloha je připravena.",
        "Attachment ready.",
      );
    }
    if (attachmentProgress) attachmentProgress.hidden = true;
    waitingDetail = ui(
      "status.reservationWaiting",
      "Čekáme na dokončení vytvoření rezervace. Potvrzení se zobrazí po ověření výsledku.",
      "We are waiting for booking creation to finish. Confirmation will appear once the result is verified.",
    );
    if (detailNode.textContent) detailNode.textContent = waitingDetail;
    // A cold scanner can take minutes. Submit the current form, not the values
    // captured before the wait; the server still checks the time freshly.
    values = new FormData(form);
    const currentPhone = phoneInput?.value.trim() ?? "";
    parsedPhone = parsePhoneNumberFromString(currentPhone);
    if (
      !form.reportValidity() ||
      (phoneInput &&
        (phoneInput.required || currentPhone) &&
        (!currentPhone.startsWith("+") || !parsedPhone?.isValid()))
    )
      throw new Error(
        ui(
          "form.reviewDetails",
          "Zkontrolujte prosím kontaktní údaje a potvrďte rezervaci znovu.",
          "Please review your contact details and confirm again.",
        ),
      );
    const primaryEmail = String(values.get("email")).trim().toLowerCase();
    const additionalInviteeEmails =
      selectedType?.additionalInviteesMode === "hidden"
        ? []
        : Array.from(
            form.querySelectorAll<HTMLInputElement>(
              'input[name="additionalInviteeEmail"]',
            ),
            (input) => input.value.trim().toLowerCase(),
          ).filter(Boolean);
    if (
      (selectedType?.additionalInviteesMode === "required" &&
        additionalInviteeEmails.length === 0) ||
      additionalInviteeEmails.length > 10 ||
      additionalInviteeEmails.some((email) => !isPublicEmail(email))
    ) {
      throw new Error(
        ui(
          "public.enterAtMost10ValidAdditionalAttendeeEmails",
          "Zadejte nejvýše 10 platných e-mailů dalších účastníků.",
          "Enter at most 10 valid additional attendee emails.",
        ),
      );
    }
    if (
      new Set(additionalInviteeEmails).size !==
        additionalInviteeEmails.length ||
      additionalInviteeEmails.includes(primaryEmail)
    ) {
      const message = ui(
        "form.uniqueInvitees",
        "Každý další účastník musí mít unikátní e-mail odlišný od hlavního účastníka.",
        "Each additional attendee must have a unique email different from the primary attendee.",
      );
      const issues = Array.from(
        form.querySelectorAll<HTMLInputElement>(
          'input[name="additionalInviteeEmail"]',
        ),
      ).flatMap((field, index) => {
        const value = field.value.trim().toLowerCase();
        return value &&
          (value === primaryEmail ||
            additionalInviteeEmails.filter((email) => email === value).length >
              1)
          ? [{ pointer: `/additionalInviteeEmails/${index}`, message }]
          : [];
      });
      throw Object.assign(new Error(message), { errors: issues });
    }
    const selection = submissionSelectionKey(
      selectedType.slug,
      selectedCityDay?.slug ?? "",
      selectedSlot,
      selectedLocationId,
      selectedCityPlaceId,
      attendeeTimeZone,
    );
    if (submittedSelection && submittedSelection !== selection)
      clientSubmissionId = crypto.randomUUID();
    submittedSelection = selection;
    saveJourney();
    const body = {
      name: String(values.get("name")),
      email: String(values.get("email")),
      additionalInviteeEmails,
      phone: parsedPhone?.number ?? "",
      organization: String(values.get("organization") ?? ""),
      organizationCountry: String(values.get("organizationCountry") ?? ""),
      organizationRegistrationNumber: selectedCompany?.registrationNumber ?? "",
      companySelectionToken: selectedCompany?.selectionToken ?? "",
      notes: String(values.get("notes") ?? ""),
      startsAt: selectedSlot,
      attendeeTimeZone,
      locationId: selectedLocationId,
      ...(selectedCityPlaceId === "other" && visitorAddressVerification
        ? { placeVerification: visitorAddressVerification }
        : {}),
      guestLocationDetails:
        selectedCityPlaceId === "other" ? visitorLocationDetails : "",
      guestLocationProposal:
        selectedCityPlaceId === "other" ? visitorAddress : "",
      ...(currentPlaceOptions() &&
      selectedCityPlaceId &&
      selectedCityPlaceId !== "other"
        ? { cityPlaceId: selectedCityPlaceId }
        : {}),
      ...(attachmentId ? { attachmentId } : {}),
      locale,
      website: String(values.get("website")),
      startedAt: formStartedAt,
      clientSubmissionId,
    };
    const response = await withFormVerification(async (token) =>
      selectedCityDay
        ? await api.POST(
            "/booking/public/{profileSlug}/city/{cityDaySlug}/reservations",
            {
              params: {
                path: { profileSlug, cityDaySlug: selectedCityDay.slug },
              },
              headers: { "Idempotency-Key": clientSubmissionId },
              body: {
                ...body,
                turnstileToken: token,
                cityMeetingTypeSlug: selectedType!.slug,
              },
            },
          )
        : await api.POST(
            "/booking/public/{profileSlug}/{meetingTypeSlug}/reservations",
            {
              params: {
                path: { profileSlug, meetingTypeSlug: selectedType!.slug },
              },
              headers: { "Idempotency-Key": clientSubmissionId },
              body: { ...body, turnstileToken: token },
            },
          ),
    );
    const { data, error } = response;
    if (error) throw error;
    clearAvailabilityCache();
    let completed = data;
    if (data.status === "processing" && data.actionToken) {
      waitingDetail = ui(
        "status.resultWaiting",
        "Ověřujeme výsledek vytvoření pozvánky v kalendáři. Nechte prosím stránku otevřenou.",
        "We are verifying the calendar invitation result. Please keep this page open.",
      );
      if (detailNode.textContent) detailNode.textContent = waitingDetail;
      for (
        let attempt = 0;
        attempt < 15 && completed.status === "processing";
        attempt += 1
      ) {
        await new Promise((resolve) => window.setTimeout(resolve, 1_000));
        const { data: resumed, error: resumeError } = await api.POST(
          "/booking/public/reservations/{actionToken}/resume",
          {
            params: { path: { actionToken: data.actionToken } },
            body: {},
          },
        );
        if (resumeError) throw resumeError;
        completed = resumed as typeof data;
      }
      if (completed.status === "processing") {
        stopSlotMonitoring();
        app.innerHTML = `<section class="card confirmation compact-confirmation"><p class="eyebrow">${escapeHtml(t("confirmation.processingTitle", "Rezervaci dokončujeme"))}</p><h1>${escapeHtml(t("confirmation.processingBody", "Ověřujeme pozvánku v kalendáři."))}</h1><p>${escapeHtml(t("confirmation.processingHelp", "Termín je pro vás blokovaný. Stav můžete bezpečně obnovit na stránce správy rezervace."))}</p><a class="back-link" href="${bookingBasePath}/manage/${encodeURIComponent(data.actionToken)}">${escapeHtml(t("confirmation.check", "Zkontrolovat stav"))}</a></section>`;
        focusView();
        return;
      }
    }
    if (completed.status === "pending" && data.actionToken) {
      stopSlotMonitoring();
      history.pushState(
        null,
        "",
        `${bookingBasePath}/manage/` + encodeURIComponent(data.actionToken),
      );
      await loadManagement(data.actionToken);
      return;
    }
    if (completed.status !== "confirmed")
      throw new Error(
        ui(
          "status.resultUnconfirmed",
          "Rezervace zatím není potvrzená. Údaje jsme zachovali; můžete bezpečně znovu ověřit stav.",
          "Your booking is not confirmed yet. Your details have been retained; you can safely check the result again.",
        ),
      );
    clearDraft();
    draft = null;
    attachment = null;
    attachmentUpload = null;
    stopSlotMonitoring();
    const chosenLocation = selectedType.locations.find(
      (item) => item.id === selectedLocationId,
    );
    const online = chosenLocation?.kind === "teams";
    const confirmedLocation = body.guestLocationProposal
      ? ui(
          "communication.locationPending",
          "Místo bude potvrzeno",
          "Location to be confirmed",
        )
      : online
        ? "Microsoft Teams"
        : chosenLocation?.address ||
          chosenLocation?.label ||
          ui(
            "communication.locationPending",
            "Místo bude potvrzeno",
            "Location to be confirmed",
          );
    const facts = [
      [
        ui("invitation.time", "Termín", "Time"),
        formatWhen(completed.startsAt) +
          " – " +
          new Intl.DateTimeFormat(locale === "cs" ? "cs-CZ" : "en-GB", {
            timeStyle: "short",
            timeZone: attendeeTimeZone,
          }).format(new Date(completed.endsAt)) +
          " (" +
          attendeeTimeZone +
          ")",
      ],
      [
        ui("confirmation.participants", "Účastníci", "Participants"),
        completed.participants
          .map((participant) => participant.displayName)
          .join(", "),
      ],
      [
        ui("invitation.meetingType", "Typ schůzky", "Meeting type"),
        selectedType.name,
      ],
      [
        ui("invitation.mode", "Způsob setkání", "Meeting mode"),
        online
          ? ui("communication.online", "online", "online")
          : chosenLocation?.kind === "phone"
            ? ui("communication.phone", "telefonicky", "by phone")
            : ui("communication.inPerson", "osobně", "in person"),
      ],
      [ui("invitation.location", "Místo", "Location"), confirmedLocation],
    ];
    app.innerHTML = `<section class="card confirmation compact-confirmation"><p class="confirmation-brand">${escapeHtml(profile.displayName)}</p><div class="confirmation-mark" aria-hidden="true">✓</div><p class="eyebrow">${escapeHtml(t("confirmation.title", "Rezervace potvrzena"))}</p><h1>${escapeHtml(t("confirmation.heading", "Těším se na setkání."))}</h1><p>${escapeHtml(ui("email.confirmation.thanks", "Děkuji za zájem o setkání. Těším se na naši schůzku a produktivní rozhovor.", "Thank you for your interest in meeting. I look forward to our conversation and a productive meeting."))}</p><dl class="confirmation-facts">${facts.map(([label, value]) => `<div><dt>${escapeHtml(label!)}</dt><dd>${escapeHtml(value!)}</dd></div>`).join("")}</dl>${data.actionToken ? `<a class="button" href="${bookingBasePath}/manage/${encodeURIComponent(data.actionToken)}">${escapeHtml(t("confirmation.manage", "Spravovat rezervaci"))}</a>` : ""}${online ? `<p>${escapeHtml(t("confirmation.teamsInfo", "Odkaz pro připojení do Teams najdete v pozvánce a e-mailu."))}</p>` : ""}<p>${escapeHtml(t("confirmation.email", "Pozvánku a odkaz pro změnu nebo zrušení jsme poslali na váš e-mail."))}</p><div class="confirmation-links"><a href="${bookingBasePath}/" class="back-link">${escapeHtml(t("confirmation.another", "Vytvořit další rezervaci"))}</a><a href="${escapeHtml(hostWebsite || `${bookingBasePath}/`)}" class="back-link">${escapeHtml(ui("confirmation.website", "Přejít na osobní web", "Visit website"))}</a></div></section>`;
    focusView();
  } catch (error) {
    const attachmentZone = form.querySelector<HTMLElement>(".attachment-drop");
    if (
      error &&
      typeof error === "object" &&
      "status" in error &&
      error.status === 409
    ) {
      rejectedSlot = selectedSlot;
      clearAvailabilityCache();
      // This explicit rejection proves the current payload was not accepted
      // under that key. Prepare a new key for the visitor's next click only.
      if (
        "detail" in error &&
        error.detail ===
          "The idempotency key is already used for another booking."
      ) {
        clientSubmissionId = crypto.randomUUID();
        saveJourney();
      }
    }
    if (error instanceof AttachmentScanPendingError && attachmentZone) {
      attachmentZone.dataset.uploadState = "pending";
      attachmentZone.querySelector<HTMLElement>(
        ".attachment-status",
      )!.textContent = error.message;
    }
    if (attachmentZone?.dataset.uploadState === "uploading") {
      attachmentZone.dataset.uploadState = "error";
      attachmentZone.querySelector<HTMLElement>(
        ".attachment-status",
      )!.textContent = attachmentUpload?.uploaded
        ? ui(
            "form.fileScanFailed",
            "Příloha je nahraná, ale kontrolu se nepodařilo dokončit. Údaje zůstávají vyplněné; zkuste potvrzení znovu později.",
            "The file is uploaded, but its check could not be completed. Your details are retained; try confirming again later.",
          )
        : ui(
            "form.fileUploadFailed",
            "Nahrání se nepodařilo. Soubor zůstává vybraný; můžete pokus opakovat.",
            "Upload failed. The selected file is retained; you can retry.",
          );
    }
    const attachmentProgress = form.querySelector<HTMLProgressElement>(
      ".attachment-progress",
    );
    if (attachmentProgress) attachmentProgress.hidden = true;
    progressNode.textContent = "";
    const issues =
      error &&
      typeof error === "object" &&
      "errors" in error &&
      Array.isArray(error.errors)
        ? error.errors
        : [];
    if (
      selectedType &&
      issues.some(
        (issue) =>
          typeof issue?.pointer === "string" &&
          issue.pointer.startsWith("/additionalInviteeEmails"),
      )
    ) {
      const refreshed = await api
        .GET("/booking/public/{profileSlug}/{meetingTypeSlug}", {
          params: {
            path: { profileSlug, meetingTypeSlug: selectedType.slug },
            query: { lang: locale },
          },
          cache: "no-store",
        })
        .catch(() => null);
      if (refreshed?.data) {
        selectedType.additionalInviteesMode =
          refreshed.data.additionalInviteesMode;
        if (selectedType?.additionalInviteesMode === "hidden") {
          form.querySelector(".additional-invitees")?.remove();
          if (draft) draft.invitees = [];
        } else if (!form.querySelector(".additional-invitees")) {
          form
            .querySelector('[type="submit"]')
            ?.insertAdjacentHTML(
              "beforebegin",
              `<fieldset class="additional-invitees full"><legend>${escapeHtml(t("form.additionalInvitees", "Další účastníci"))}</legend><div id="additional-invitee-list"></div><button class="text-button" id="add-invitee" type="button">${escapeHtml(t("form.addInvitee", "+ Přidat dalšího účastníka"))}</button></fieldset>`,
            );
          initialiseAdditionalInvitees();
        } else if (
          selectedType?.additionalInviteesMode === "required" &&
          !form.querySelector('[name="additionalInviteeEmail"]')
        ) {
          form.querySelector<HTMLButtonElement>("#add-invitee")?.click();
        }
        if (selectedType?.additionalInviteesMode === "required")
          form.querySelector(".additional-invitees legend span")?.remove();
      }
    }
    let firstField: HTMLElement | undefined;
    for (const issue of issues) {
      if (
        !issue ||
        typeof issue.pointer !== "string" ||
        typeof issue.message !== "string"
      )
        continue;
      const [name, index] = issue.pointer
        .replace(/^\/(?:body\/)?/, "")
        .split("/");
      const field =
        name === "organizationCountry"
          ? form.querySelector<HTMLInputElement>("#company-country-picker")
          : name === "additionalInviteeEmails"
            ? form.querySelectorAll<HTMLInputElement>(
                '[name="additionalInviteeEmail"]',
              )[Number(index ?? 0)]
            : form.elements.namedItem(
                name === "attachmentId" ? "attachment" : (name ?? ""),
              );
      if (
        field instanceof HTMLInputElement ||
        field instanceof HTMLSelectElement ||
        field instanceof HTMLTextAreaElement
      ) {
        fieldFeedback(field, issue.message, "error");
        firstField ??= field;
      }
    }
    errorNode.textContent = firstField
      ? ""
      : isMaintenance(error)
        ? error.detail
        : error instanceof Error
          ? error.message
          : problemMessage(error);
    const requestId =
      error &&
      typeof error === "object" &&
      "requestId" in error &&
      typeof error.requestId === "string" &&
      /^[A-Za-z0-9_-]{1,100}$/.test(error.requestId)
        ? error.requestId
        : null;
    if (requestId)
      errorNode.textContent += ` (${ui("error.requestId", "Požadavek", "Request")}: ${requestId})`;
    (firstField ?? errorNode).focus();
  } finally {
    window.clearTimeout(submissionTimer);
    submissionDetail?.remove();
    progressNode.textContent = "";
    submitting = false;
    updateReservationSubmitReadiness();
  }
}

async function loadManagement(actionToken: string): Promise<void> {
  // A completed calendar action leaves the availability view. Its delayed refresh
  // must not replace management controls while a visitor cancels or reviews it.
  currentStep = 1;
  calendarRevision++;
  calendarAbort.abort();
  clearTimeout(availabilityRetryTimer);
  clearTimeout(visibleCheckTimer);
  clearTimeout(noticeTimer);
  clearTimeout(cacheRefreshTimer);
  clearTimeout(cityOverviewRetryTimer);
  stopSlotMonitoring();
  stopFormProtection();
  try {
    rescheduleActionToken = null;
    const { data, error } = await api.GET(
      "/booking/public/reservations/{actionToken}",
      {
        params: { path: { actionToken } },
      },
    );
    if (error) throw error;
    const processing =
      data.status === "processing" || data.operation?.status === "processing";
    const status =
      data.status === "confirmed"
        ? t("status.confirmedShort", "Potvrzená")
        : data.status === "cancelled"
          ? t("status.cancelledShort", "Zrušená")
          : data.status;
    const failed = !processing && data.operation?.status === "failed";
    const cancelled = !processing && data.status === "cancelled";
    const confirmed = !processing && !failed && data.status === "confirmed";
    if (
      confirmed &&
      new URL(location.href).searchParams.get("reschedule") === "1"
    )
      return await loadRescheduleSlots(actionToken, data);
    // Only the canonical provider result can announce a completed reschedule.
    const rescheduled =
      confirmed &&
      data.operation?.kind === "reschedule" &&
      data.operation.status === "succeeded" &&
      data.placeProposal?.operationId !== data.operation.id;
    const tone = processing
      ? "progress"
      : cancelled
        ? "error"
        : failed
          ? "warning"
          : confirmed
            ? "success"
            : "neutral";
    const heading =
      data.status === "pending" && data.placeProposal
        ? ui(
            "placeProposal.waiting",
            "Čekáme na schválení místa hostitelem.",
            "Waiting for the host to approve the meeting place.",
          )
        : processing
          ? ui(
              "status.processing",
              "Změnu ještě ověřujeme v kalendáři.",
              "We are still verifying the change with the calendar.",
            )
          : cancelled
            ? ui(
                "status.cancelled",
                "Rezervace byla zrušena.",
                "Your booking was cancelled.",
              )
            : failed
              ? ui(
                  "manage.changeFailed",
                  "Změnu se nepodařilo dokončit.",
                  "The change could not be completed.",
                )
              : rescheduled
                ? ui(
                    "manage.rescheduled",
                    "Termín byl úspěšně změněn.",
                    "Your booking was successfully rescheduled.",
                  )
                : confirmed
                  ? ui(
                      "status.confirmed",
                      "Rezervace je potvrzena.",
                      "Your booking is confirmed.",
                    )
                  : status;
    app.innerHTML = `<section class="card confirmation compact-confirmation management-confirmation"><p class="eyebrow">${escapeHtml(t("manage.title", "Správa rezervace"))}</p><div class="management-result" data-tone="${tone}" role="status"><div class="confirmation-mark" aria-hidden="true">${confirmed ? "✓" : cancelled ? "×" : processing ? "…" : "!"}</div><h1>${escapeHtml(heading)}</h1></div><h2 class="management-meeting">${escapeHtml(data.meetingName)}</h2><p class="selected-time">${escapeHtml(formatConfirmationWhen(data.startsAt, data.timezone))}</p><p>${escapeHtml(t("manage.host", "Hostitel: {host}", { host: data.hostName }))}</p>${`<p>${escapeHtml(ui("confirmation.participants", "Účastníci", "Participants"))}: ${escapeHtml(data.participants.map((participant) => participant.displayName).join(", "))}</p>`}${failed ? `<p>${escapeHtml(t("manage.status", "Stav: {status}", { status }))}</p>` : ""}${processing ? `<div class="operation-progress"><progress aria-label="${escapeHtml(heading)}"></progress></div>` : ""}${data.onlineJoinUrl && data.status === "confirmed" && !processing ? `<p>${escapeHtml(t("confirmation.teamsInfo", "Odkaz pro připojení do Teams najdete v pozvánce a e-mailu."))}</p>` : ""}${data.status === "confirmed" && !processing ? `<div class="management-actions"><button class="button secondary" id="reschedule-booking">${escapeHtml(t("manage.reschedule", "Změnit termín"))}</button><button class="button danger" id="cancel-booking">${escapeHtml(t("manage.cancel", "Zrušit rezervaci"))}</button></div>` : ""}<a class="back-link" href="${bookingBasePath}/">${escapeHtml(t("manage.new", "Vytvořit novou rezervaci"))}</a></section><dialog id="cancel-dialog" class="booking-dialog" aria-labelledby="cancel-title" aria-describedby="cancel-description"><form method="dialog"><p class="eyebrow">${escapeHtml(t("manage.cancel", "Zrušení rezervace"))}</p><h2 id="cancel-title">${escapeHtml(t("cancel.title", "Opravdu chcete rezervaci zrušit?"))}</h2><p id="cancel-description">${escapeHtml(t("cancel.description", "Tuto akci nelze vzít zpět."))}</p><label for="cancel-reason">${escapeHtml(t("cancel.reason", "Důvod zrušení (volitelné)"))}</label><textarea id="cancel-reason" rows="2" maxlength="2000"></textarea><div class="management-actions"><button class="button secondary" value="keep" autofocus>${escapeHtml(t("cancel.back", "Ponechat rezervaci"))}</button><button class="button danger" id="confirm-cancel" value="cancel">${escapeHtml(t("cancel.confirm", "Ano, zrušit"))}</button></div><p class="dialog-progress" role="status"></p></form></dialog>`;
    const placePanel = document.createElement("section");
    placePanel.className = "editor-section";
    if (confirmed && data.locationType === "in_person" && data.location) {
      const currentPlace = document.createElement("p");
      currentPlace.textContent =
        ui("placeProposal.current", "Potvrzené místo: ", "Confirmed place: ") +
        data.location;
      placePanel.append(currentPlace);
    }
    if (
      confirmed &&
      data.locationType === "in_person" &&
      data.location &&
      data.locationDetails
    ) {
      const details = document.createElement("p");
      details.textContent = data.locationDetails;
      placePanel.append(details);
    }
    if (data.placeProposal) {
      const note = document.createElement("p");
      note.textContent =
        ui("placeProposal.status", "Návrh místa: ", "Place proposal: ") +
        data.placeProposal.address;
      const status = document.createElement("span");
      status.className = "proposal-status-line";
      status.textContent =
        {
          pending: ui(
            "placeProposal.pending",
            "Čeká na schválení",
            "Awaiting approval",
          ),
          accepted: ui(
            "placeProposal.accepted",
            "Schváleno, čeká na kalendář",
            "Approved, awaiting calendar",
          ),
          succeeded: ui(
            "placeProposal.succeeded",
            "Místo potvrzeno",
            "Place confirmed",
          ),
          rejected: ui("placeProposal.rejected", "Zamítnuto", "Rejected"),
          expired: ui("placeProposal.expired", "Platnost vypršela", "Expired"),
        }[data.placeProposal.status] +
        (data.placeProposal.status === "pending"
          ? " · " +
            ui("placeProposal.expires", "Platnost do ", "Expires ") +
            formatWhen(data.placeProposal.expiresAt, data.timezone)
          : "");
      const detailText =
        data.placeProposal.status === "accepted"
          ? data.placeProposal.approvedLocationDetails
          : data.placeProposal.visitorLocationDetails;
      if (detailText) {
        const detail = document.createElement("span");
        detail.className = "proposal-status-line";
        detail.textContent = detailText;
        note.append(detail);
      }
      note.append(status);
      if (data.placeProposal.status === "succeeded") {
        const history = document.createElement("details");
        const summary = document.createElement("summary");
        summary.textContent = ui(
          "placeProposal.history",
          "Historie návrhu",
          "Proposal history",
        );
        history.append(summary, note);
        placePanel.append(history);
      } else placePanel.append(note);
    }
    app.querySelector(".management-confirmation")?.append(placePanel);
    if (processing) {
      window.setTimeout(async () => {
        const { error: resumeError } = await api.POST(
          "/booking/public/reservations/{actionToken}/resume",
          {
            params: { path: { actionToken } },
            body: {},
          },
        );
        if (resumeError)
          return renderError(
            resumeError,
            () => void loadManagement(actionToken),
          );
        void loadManagement(actionToken);
      }, 1_500);
    }
    document
      .querySelector<HTMLButtonElement>("#reschedule-booking")
      ?.addEventListener(
        "click",
        () =>
          void loadRescheduleSlots(actionToken, data).catch((error) =>
            renderError(error, () => void loadManagement(actionToken)),
          ),
      );
    document
      .querySelector<HTMLButtonElement>("#cancel-booking")
      ?.addEventListener("click", () =>
        document
          .querySelector<HTMLDialogElement>("#cancel-dialog")
          ?.showModal(),
      );
    document
      .querySelector<HTMLButtonElement>("#confirm-cancel")
      ?.addEventListener("click", async (event) => {
        event.preventDefault();
        const button = event.currentTarget as HTMLButtonElement;
        if (button.disabled) return;
        button.disabled = true;
        const progress =
          document.querySelector<HTMLElement>(".dialog-progress");
        if (progress)
          progress.textContent = t(
            "progress.cancel",
            "Ruším rezervaci v Microsoft 365…",
          );
        try {
          const { error: cancelError } = await api.POST(
            "/booking/public/reservations/{actionToken}/cancel",
            {
              params: { path: { actionToken } },
              body: {
                reason:
                  document
                    .querySelector<HTMLTextAreaElement>("#cancel-reason")
                    ?.value.trim() ?? "",
              },
            },
          );
          if (cancelError) throw cancelError;
          await loadManagement(actionToken);
        } catch (error) {
          // The provider may have committed despite a lost response. Read, never re-send.
          renderError(error, () => void loadManagement(actionToken));
        }
      });
    focusView();
  } catch (error) {
    renderError(error, () => void loadManagement(actionToken));
  }
}

function renderRescheduleRestriction(
  actionToken: string,
  reservation: PublicReservation,
  message: string,
): void {
  app.innerHTML = `<section class="card"><h1>${escapeHtml(message)}</h1><p>${escapeHtml(ui("reschedule.originalRetained", "Vaše původní rezervace zůstává zachovaná.", "Your original booking is unchanged."))}</p><p>${escapeHtml(formatConfirmationWhen(reservation.startsAt, reservation.timezone))}</p><button class="button secondary" id="return-booking-management">${escapeHtml(ui("reschedule.returnManagement", "Zpět na správu rezervace", "Back to booking management"))}</button></section>`;
  app.querySelector<HTMLButtonElement>("#return-booking-management")!.onclick =
    () => {
      clearRescheduleView();
      void loadManagement(actionToken);
    };
  focusView();
}

async function loadRescheduleSlots(
  actionToken: string,
  reservation: PublicReservation,
  displayTimeZone = reservation.timezone,
): Promise<void> {
  resetRescheduleTargetChoice();
  if (!hostPublished) {
    renderError(
      {
        detail:
          locale === "cs"
            ? "Hostitel nyní nenabízí nové termíny. Stávající rezervaci můžete ponechat nebo zrušit."
            : "This host is not currently offering new times. You may keep or cancel your existing reservation.",
      },
      () => void loadManagement(actionToken),
    );
    return;
  }
  const address = new URL(location.href);
  if (address.searchParams.get("reschedule") !== "1") {
    address.searchParams.set("reschedule", "1");
    history.pushState({ ...history.state }, "", address);
  }
  selectedCityDay = undefined;
  selectedCityPlaceId = reservation.cityPlaceId ?? "";
  selectedSlot = "";
  placeView = false;
  cityFilter = "";
  dayOffer = null;
  rescheduleContextNotice = "";
  rescheduleDayErrors.clear();
  rescheduleAvailability = null;
  if (reservation.cityDaySlug) {
    const result = await api.GET(
      "/booking/public/{profileSlug}/city/{cityDaySlug}",
      {
        params: {
          path: { profileSlug, cityDaySlug: reservation.cityDaySlug },
          query: { lang: locale },
        },
      },
    );
    if (result.error) {
      if (result.response.status !== 404 && result.response.status !== 410)
        throw result.error;
      rescheduleContextNotice = ui(
        "reschedule.originalDayHidden",
        "Původní CityDay již není zveřejněný. Vaše rezervace zůstává platná; nové termíny ověřujeme podle aktuální nabídky.",
        "The original CityDay is no longer published. Your booking remains valid; new times follow the current availability.",
      );
    } else selectedCityDay = result.data;
  }
  selectedType = (selectedCityDay?.meetingTypes ?? profile.meetingTypes).find(
    (item) => item.slug === reservation.meetingTypeSlug,
  );
  if (!selectedType) {
    return renderRescheduleRestriction(
      actionToken,
      reservation,
      ui(
        "reschedule.typeUnavailable",
        "Tento typ schůzky nyní nenabízí nové termíny.",
        "This meeting type is not currently offering new times.",
      ),
    );
  }
  attendeeTimeZone = displayTimeZone;
  visibleMonth = monthKey(new Date(reservation.startsAt));
  selectedDay = dateKey(new Date(reservation.startsAt));
  const originalLocationId = reservation.locationId;
  const originalKind =
    reservation.locationType === "online"
      ? "teams"
      : reservation.locationType === "phone"
        ? "phone"
        : "in_person";
  const originalLocation = originalLocationId
    ? selectedType.locations.find(
        (location) => location.id === originalLocationId,
      )
    : selectedType.locations.find((location) => location.kind === originalKind);
  if (!originalLocation)
    return renderRescheduleRestriction(
      actionToken,
      reservation,
      ui(
        "reschedule.formatUnavailable",
        "Původní forma setkání už není v nabídce. Pro změnu kontaktujte organizátora.",
        "The original meeting format is no longer offered. Contact the organiser to change it.",
      ),
    );
  selectedLocationId = originalLocation.id;
  rescheduleActionToken = actionToken;
  reschedulingReservation = reservation;
  await renderCalendar();
}

function clearRescheduleView(): void {
  const address = new URL(location.href);
  address.searchParams.delete("reschedule");
  history.replaceState({ ...history.state }, "", address);
}

function installReservationSubmitHandler(): void {
  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== "reservation-form")
      return;
    void submitReservation(event as SubmitEvent);
  });
}

installLocaleSwitcher();
updatePublicShell();
installReservationSubmitHandler();
void loadProfile();

async function initialiseCountry(form: HTMLFormElement): Promise<void> {
  const country = form.elements.namedItem(
    "organizationCountry",
  ) as HTMLSelectElement;
  const source = form.querySelector<HTMLElement>(".country-source")!;
  if (!country || !source) return;
  let applyingEstimate = false;
  let changed =
    draft?.fields.organizationCountryChosen === "true" ||
    Boolean(country.value);
  country.dataset.chosen = draft?.fields.organizationCountryChosen ?? "false";
  country.dataset.source = draft?.fields.organizationCountrySource ?? "";
  if (country.dataset.source === "db-ip" && country.value)
    source.innerHTML =
      '<a href="https://db-ip.com" target="_blank" rel="noopener noreferrer">IP Geolocation by DB-IP</a>';
  country.addEventListener("change", () => {
    if (applyingEstimate) return;
    changed = true;
    country.dataset.chosen = "true";
    country.dataset.source = "";
    source.textContent = "";
    saveJourney();
  });
  if (changed) return;
  const primary = (navigator.languages[0] || navigator.language)
    .split("-")[0]
    ?.toLowerCase();
  let code: string | null =
    primary === "cs"
      ? "CZ"
      : primary === "sk"
        ? "SK"
        : locale === "cs"
          ? "CZ"
          : null;
  let geo = false;
  if (!code) {
    try {
      const { data } = await api.GET("/visitor-context");
      code = data?.countryCode ?? null;
      geo = data?.source === "db-ip";
    } catch {
      /* Missing data must never block booking. */
    }
  }
  if (changed || !country.isConnected || !code) return;
  if (!Array.from(country.options).some((o) => o.value === code)) return;
  country.value = code;
  country.dataset.source = geo ? "db-ip" : "browser";
  if (geo)
    source.innerHTML =
      '<a href="https://db-ip.com" target="_blank" rel="noopener noreferrer">IP Geolocation by DB-IP</a>';
  applyingEstimate = true;
  country.dispatchEvent(new Event("change"));
  applyingEstimate = false;
  saveJourney();
}

async function reviewReschedule(
  actionToken: string,
  candidate: string,
): Promise<void> {
  const reservation = reschedulingReservation;
  if (!reservation) return void loadManagement(actionToken);
  const candidateSlot = slots.find((slot) => slot.startsAt === candidate);
  const zone = reservation.timezone;
  const period = (start: string, end: string) =>
    formatWhen(start, zone) + " – " + formatTime(end, zone) + " (" + zone + ")";
  const place = currentPlaceOptions()?.places.find(
    (item) => item.id === selectedCityPlaceId,
  );
  app.innerHTML = `<section class="card confirmation compact-confirmation"><h1>${escapeHtml(ui("reschedule.review", "Potvrďte nový termín", "Confirm your new time"))}</h1><dl class="confirmation-facts"><div><dt>${escapeHtml(ui("reschedule.previous", "Původní termín", "Current time"))}</dt><dd>${escapeHtml(period(reservation.startsAt, reservation.endsAt))}</dd></div><div><dt>${escapeHtml(ui("reschedule.next", "Nový termín", "New time"))}</dt><dd>${escapeHtml(period(candidate, candidateSlot?.endsAt ?? candidate))}</dd></div>${place ? `<div><dt>${escapeHtml(ui("places.title", "Místo setkání", "Meeting place"))}</dt><dd>${escapeHtml(`${place.fullName || place.name} — ${place.address}`)}</dd></div>` : ""}</dl><div id="address-change-confirmation"></div><p id="reschedule-progress" role="status"><progress></progress> ${escapeHtml(ui("reschedule.checking", "Ověřuji dostupnost termínu…", "Checking availability…"))}</p><div class="management-actions"><button class="button" id="confirm-reschedule" disabled>${escapeHtml(ui("reschedule.confirm", "Potvrdit změnu termínu", "Confirm reschedule"))}</button><button class="button secondary" id="choose-reschedule">${escapeHtml(ui("reschedule.chooseAnother", "Vybrat jiný termín", "Choose another time"))}</button></div></section>`;
  focusView();
  const confirm = app.querySelector<HTMLButtonElement>("#confirm-reschedule")!;
  const reviewVenue = place
    ? app.querySelector<HTMLElement>(".confirmation-facts > div:last-child dd")
    : null;
  const choose = app.querySelector<HTMLButtonElement>("#choose-reschedule")!;
  const progress = app.querySelector<HTMLElement>("#reschedule-progress")!;
  choose.onclick = () => void renderCalendar();
  try {
    const available = await revalidateSelectedSlot(true);
    if (!confirm.isConnected) return;
    const checkedPlace = currentPlaceOptions()?.places.find(
      (item) => item.id === selectedCityPlaceId,
    );
    if (reviewVenue && checkedPlace)
      reviewVenue.textContent = `${checkedPlace.fullName || checkedPlace.name} — ${checkedPlace.address}`;
    progress.textContent = available
      ? ui(
          "reschedule.available",
          "Termín je dostupný. Změnu ještě potvrďte.",
          "This time is available. Please confirm the change.",
        )
      : ui(
          "calendar.slotUnavailable",
          "Termín již není dostupný. Vyberte jiný.",
          "This time is no longer available. Choose another.",
        );
    const addressConfirmation = app.querySelector<HTMLElement>(
      "#address-change-confirmation",
    )!;
    if (available && rescheduleAvailability?.addressChangeRequired) {
      addressConfirmation.innerHTML = `<label class="address-change-confirm"><input type="checkbox" id="accept-address-change" /> <span>${escapeHtml(ui("reschedule.addressChanged", "Adresa místa byla aktualizována. Potvrzuji nové místo uvedené výše.", "The venue address was updated. I accept the new address shown above."))}</span></label>`;
      app.querySelector<HTMLInputElement>("#accept-address-change")!.onchange =
        (event) => {
          confirm.disabled = !(event.currentTarget as HTMLInputElement).checked;
        };
      confirm.disabled = true;
    } else confirm.disabled = !available;
  } catch (error) {
    if (isMaintenance(error))
      return renderError(error, () => void loadProfile());
    if (progress.isConnected)
      progress.textContent = ui(
        "reschedule.checkFailed",
        "Dostupnost se nepodařilo ověřit. Původní rezervace zůstává beze změny.",
        "Availability could not be checked. Your original booking is unchanged.",
      );
  }
  confirm.onclick = async () => {
    if (confirm.disabled || submitting) return;
    confirm.disabled = true;
    choose.disabled = true;
    submitting = true;
    progress.innerHTML = `<progress></progress> ${escapeHtml(ui("reschedule.sending", "Potvrzuji změnu termínu…", "Confirming your new time…"))}`;
    try {
      clearRescheduleView();
      const { error } = await api.POST(
        "/booking/public/reservations/{actionToken}/reschedule",
        {
          params: { path: { actionToken } },
          body: {
            startsAt: candidate,
            ...(selectedRescheduleDayId()
              ? { cityDayId: selectedRescheduleDayId() }
              : {}),
            ...(selectedCityPlaceId
              ? { cityPlaceId: selectedCityPlaceId }
              : {}),
            ...(app.querySelector<HTMLInputElement>("#accept-address-change")
              ?.checked
              ? { confirmAddressChange: true }
              : {}),
          },
        },
      );
      if (error) throw error;
      rescheduleActionToken = null;
      await loadManagement(actionToken);
    } catch (error) {
      // A timeout may have committed. Never re-POST blindly; read the canonical operation.
      if (isMaintenance(error))
        return renderError(error, () => void loadProfile());
      const status =
        error && typeof error === "object" && "status" in error
          ? Number(error.status)
          : 0;
      if (!status || status >= 500) {
        progress.textContent = ui(
          "status.processing",
          "Ověřuji výsledek změny…",
          "Checking the outcome…",
        );
        await loadManagement(actionToken);
      } else renderError(error, () => void loadManagement(actionToken));
    } finally {
      submitting = false;
    }
  };
}

function renderManagementAccess(entryToken: string): void {
  stopFormProtection();
  const label =
    locale === "cs"
      ? "Poslat mi odkaz ke správě"
      : "Email me a management link";
  app.innerHTML = `<section class="card confirmation compact-confirmation"><h1>${escapeHtml(locale === "cs" ? "Správa rezervace" : "Manage booking")}</h1><p>${escapeHtml(locale === "cs" ? "Soukromý odkaz pošleme pouze hlavnímu návštěvníkovi na původní e-mail rezervace." : "We will send a private link only to the original primary visitor’s booking email.")}</p><form id="management-access-form"><div id="access-protection"></div><button class="button" type="submit">${escapeHtml(label)}</button><p role="status" id="access-status"></p></form></section>`;
  focusView();
  const button = app.querySelector<HTMLButtonElement>('button[type="submit"]')!;
  const status = app.querySelector<HTMLElement>("#access-status")!;
  let protection = turnstileSiteKey ? "" : "local-bypass";
  let busy = false;
  const clientRequestId = crypto.randomUUID();
  const protect = () => {
    if (turnstileSiteKey)
      startFormProtection(
        app.querySelector<HTMLElement>("#access-protection")!,
        turnstileSiteKey,
        locale === "cs",
        (value) => {
          protection = value;
          button.disabled = busy || !value;
        },
      );
  };
  protect();
  app.querySelector<HTMLFormElement>("#management-access-form")!.onsubmit =
    async (event) => {
      event.preventDefault();
      if (busy || !protection) return;
      busy = true;
      button.disabled = true;
      status.textContent =
        locale === "cs" ? "Zpracovávám požadavek…" : "Processing request…";
      try {
        const { error } = await api.POST(
          "/booking/public/management-links/{entryToken}",
          {
            params: { path: { entryToken } },
            body: { clientRequestId, turnstileToken: protection },
          },
        );
        if (error) throw error;
        status.textContent =
          locale === "cs"
            ? "Pokud lze rezervaci spravovat, odkaz pošleme hlavnímu návštěvníkovi. Zkontrolujte e-mail."
            : "If the booking can be managed, we will email its primary visitor a link. Please check your email.";
        stopFormProtection();
      } catch (error) {
        if (isMaintenance(error))
          return renderError(error, () => void loadProfile());
        busy = false;
        button.disabled = false;
        status.textContent =
          locale === "cs"
            ? "Požadavek se nepodařilo dokončit. Zkuste to za minutu."
            : "The request could not be completed. Please try again in a minute.";
        protect();
      }
    };
}

async function renderSharedAvailability(): Promise<void> {
  const params = new URLSearchParams(location.search);
  app.innerHTML =
    '<section class="card" aria-busy="true"><h1>' +
    escapeHtml(
      locale === "cs" ? "Dostupné termíny" : "Available appointments",
    ) +
    '</h1><p role="status">' +
    escapeHtml(
      locale === "cs" ? "Ověřuji dostupnost…" : "Checking availability…",
    ) +
    "</p></section>";
  try {
    const { data, error } = await api.GET(
      "/booking/public/{profileSlug}/shared-availability",
      {
        params: {
          path: { profileSlug },
          query: {
            meetingType: params.get("meetingType") ?? "",
            cityId: params.get("cityId") ?? "",
            placeId: params.get("placeId") ?? undefined,
            from: params.get("from") ?? undefined,
            to: params.get("to") ?? undefined,
            lang: locale,
          },
        },
      },
    );
    if (error) throw error;
    const grouped = new Map<string, typeof data.slots>();
    for (const slot of data.slots) {
      const key = new Intl.DateTimeFormat("en-CA", {
        timeZone: data.timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date(slot.startsAt));
      const list = grouped.get(key) ?? [];
      list.push(slot);
      grouped.set(key, list);
    }
    app.innerHTML =
      '<section class="card"><h1>' +
      escapeHtml(data.city) +
      "</h1><p>" +
      escapeHtml(
        data.meetingName + (data.placeName ? " · " + data.placeName : ""),
      ) +
      "</p><p>" +
      escapeHtml(data.from + " – " + data.to + " · " + data.timeZone) +
      "</p>" +
      [...grouped]
        .map(
          ([day, list]) =>
            "<section><h2>" +
            escapeHtml(
              new Intl.DateTimeFormat(locale === "cs" ? "cs-CZ" : "en-GB", {
                dateStyle: "full",
                timeZone: "UTC",
              }).format(new Date(day + "T12:00:00Z")),
            ) +
            '</h2><div class="slot-grid">' +
            list
              .map((slot) => {
                const target = new URL(
                  `${bookingBasePath}/` +
                    (slot.cityDaySlug
                      ? "city/" + slot.cityDaySlug
                      : data.meetingType),
                  location.origin,
                );
                target.searchParams.set("date", day);
                target.searchParams.set("time", slot.startsAt);
                target.searchParams.set("meetingType", data.meetingType);
                target.searchParams.set("locationId", slot.locationId);
                target.searchParams.set("city", data.city);
                target.searchParams.set("lang", locale);
                if (data.placeId)
                  target.searchParams.set("place", data.placeId);
                target.searchParams.set(
                  "shared",
                  location.pathname + location.search,
                );
                return (
                  '<a class="button" href="' +
                  escapeHtml(target.pathname + target.search) +
                  '">' +
                  escapeHtml(
                    new Intl.DateTimeFormat(
                      locale === "cs" ? "cs-CZ" : "en-GB",
                      { timeStyle: "short", timeZone: data.timeZone },
                    ).format(new Date(slot.startsAt)),
                  ) +
                  "</a>"
                );
              })
              .join("") +
            "</div></section>",
        )
        .join("") +
      (data.slots.length
        ? ""
        : "<p>" +
          escapeHtml(
            locale === "cs"
              ? "V tomto období nemáme odpovídající volné termíny."
              : "No matching appointments are available in this period.",
          ) +
          "</p>") +
      "</section>";
  } catch (error) {
    renderError(error, () => void renderSharedAvailability());
  }
}
